import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type JSX,
  type ReactNode,
} from 'react';
import { Navigate, NavLink, useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';
import { z } from 'zod';
import { toast } from 'sonner';
import { Form, FormActions, FormField, FormMessage } from '@/components/form';
import { useFormController } from '@/lib/forms/formController';
import { siteSettingsResource, useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import type { SiteSettings, SiteSettingsUpdateInput } from '@/lib/types/siteSettings';
import type { PublicPropertySummary } from '@/lib/types/publicProperty';
import { useAllPublicPropertyList } from '@/hooks/usePublicProperties';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';
import { useDashboardUser } from './DashboardRouter';
import { ErrorState, Skeleton } from '@/components/feedback';
import {
  SITE_SETTINGS_MEDIA_ALLOWED_TYPES,
  SITE_SETTINGS_MEDIA_MAX_BYTES,
} from '@/lib/constants/siteSettingsMedia';
import {
  isAllowedSiteSettingsMediaType,
  isWithinSiteSettingsMediaSizeLimit,
  uploadSiteSettingsMediaFile,
} from '@/lib/storage/siteSettingsMedia';
import { parsePropertyMediaUrl, removePropertyMediaFiles } from '@/lib/storage/propertyMedia';

const contactHourSchema = z.object({
  label: z.string().trim().min(1, 'Label is required'),
  value: z.string().trim().min(1, 'Value is required'),
});

const leadershipSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  title: z.string().trim().optional(),
});

const hrefSchema = z
  .string()
  .trim()
  .min(1, 'Link is required')
  .refine(
    (value) => value.startsWith('/') || value.startsWith('https://') || value.startsWith('http://'),
    'Enter a path starting with / or a full URL',
  );

const navOptionalHrefSchema = z
  .string()
  .trim()
  .refine(
    (value) =>
      value.length === 0 ||
      value.startsWith('/') ||
      value.startsWith('https://') ||
      value.startsWith('http://') ||
      value.startsWith('tel:') ||
      value.startsWith('mailto:'),
    'Enter a path starting with /, https://, http://, tel:, or mailto:',
  );

const navigationActionSchema = z.enum(['link', 'call', 'auth']);
const navigationStyleSchema = z.enum(['link', 'button', 'outline']);

const optionalHrefSchema = z
  .string()
  .trim()
  .refine(
    (value) =>
      value.length === 0 ||
      value.startsWith('/') ||
      value.startsWith('https://') ||
      value.startsWith('http://'),
    'Enter a path starting with / or a full URL',
  );

const navigationChildHrefSchema = z
  .string()
  .trim()
  .refine(
    (value) =>
      value.length === 0 ||
      value.startsWith('/') ||
      value.startsWith('https://') ||
      value.startsWith('http://') ||
      value.startsWith('tel:') ||
      value.startsWith('mailto:'),
    'Enter a path starting with /, https://, http://, tel:, or mailto:',
  );

const navigationChildSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().trim(),
    href: navigationChildHrefSchema,
    propertyId: z.string().trim(),
    enabled: z.boolean(),
    external: z.boolean(),
  })
  .superRefine((value, ctx) => {
    const hasProperty = value.propertyId.trim().length > 0;
    if (hasProperty) {
      return;
    }
    if (value.label.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Label is required',
        path: ['label'],
      });
    }
    if (value.href.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Add a link or select a property.',
        path: ['href'],
      });
    }
  });

const navigationItemSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().trim().min(1, 'Label is required'),
    href: navOptionalHrefSchema,
    enabled: z.boolean(),
    external: z.boolean(),
    action: navigationActionSchema,
    style: navigationStyleSchema,
    children: z.array(navigationChildSchema),
  })
  .superRefine((value, ctx) => {
    if (value.children.length > 0) {
      return;
    }
    if (value.action === 'link' && value.href.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Add a link or dropdown items for each navigation entry.',
        path: ['href'],
      });
    }
    if (value.action === 'call' && value.href.trim().length > 0 && !value.href.startsWith('tel:')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Call buttons must use a tel: link.',
        path: ['href'],
      });
    }
  });

const titleDescriptionSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  description: z.string().trim().min(1, 'Description is required'),
});

const labelValueSchema = z.object({
  label: z.string().trim().min(1, 'Label is required'),
  value: z.string().trim().min(1, 'Value is required'),
});

const valueLabelSchema = z.object({
  value: z.string().trim().min(1, 'Value is required'),
  label: z.string().trim().min(1, 'Label is required'),
});

const requiredString = (label: string) => z.string().trim().min(1, `${label} is required`);
const stringListSchema = z.array(z.string().trim().min(1, 'Value is required'));
const heroMediaUrlSchema = z
  .string()
  .trim()
  .refine(
    (value) => value.length === 0 || value.startsWith('https://') || value.startsWith('http://'),
    'Enter a full URL starting with https:// or http://',
  );
const heroMediaTypeSchema = z.enum(['video', 'image']);

const formSchema = z.object({
  brandSiteName: requiredString('Site name'),
  brandTagline: requiredString('Tagline'),
  brandLogoUrl: optionalHrefSchema,
  brandFaviconUrl: optionalHrefSchema,
  brandDefaultOgImageUrl: optionalHrefSchema,
  seoDefaultDescription: requiredString('Default description'),
  seoCanonicalOrigin: z.string().trim().url('Canonical origin must be a valid URL'),
  navigationLinks: z.array(navigationItemSchema).min(1, 'Add at least one navigation item'),
  navigationMobileTitle: requiredString('Mobile title'),
  navigationMobileDescription: requiredString('Mobile description'),
  navigationMobileShortcutHeading: requiredString('Shortcut heading'),
  navigationMobileShortcutDescription: requiredString('Shortcut description'),
  navigationMobilePrimaryLabel: requiredString('Primary shortcut label'),
  navigationMobilePrimaryHref: hrefSchema,
  navigationMobileSecondaryLabel: requiredString('Secondary shortcut label'),
  navigationMobileSecondaryHref: hrefSchema,
  footerCompanyName: requiredString('Company name'),
  footerCopyrightText: requiredString('Copyright text'),
  bookingEmail: z.string().trim().email('Provide a valid booking email'),
  contactEmail: z.string().trim().email('Provide a valid contact email'),
  contactPhone: z.string().trim().min(3, 'Contact phone is required'),
  contactAddressLine1: requiredString('Address line 1'),
  contactAddressLine2: z.string().trim().optional(),
  mapEmbedUrl: z.string().trim().url('Map embed URL must be valid'),
  contactHours: z.array(contactHourSchema).min(1, 'Add at least one hours entry'),
  leadershipContacts: z.array(leadershipSchema).min(1, 'Add at least one leadership contact'),
  homeSeoTitle: requiredString('Homepage SEO title'),
  homeSeoDescription: requiredString('Homepage SEO description'),
  homeHeroEyebrow: requiredString('Hero eyebrow'),
  homeHeroTitle: requiredString('Hero title'),
  homeHeroDescription: requiredString('Hero description'),
  homeHeroMediaLabel: requiredString('Hero media label'),
  homeHeroMediaLocation: requiredString('Hero media location'),
  homeHeroMediaAriaLabel: requiredString('Hero media aria label'),
  homeHeroMediaUrl: heroMediaUrlSchema,
  homeHeroMediaType: heroMediaTypeSchema,
  homeAvailabilityEyebrow: requiredString('Availability eyebrow'),
  homeAvailabilityTitle: requiredString('Availability title'),
  homeAvailabilityDescription: requiredString('Availability description'),
  homeAvailabilityCtaLabel: requiredString('CTA label'),
  homeAvailabilityCtaHref: hrefSchema,
  homeAvailabilityEmptyMessage: requiredString('Availability empty message'),
  homeAvailabilityErrorMessage: requiredString('Availability error message'),
  homeContactHeading: requiredString('Contact heading'),
  homeContactDescription: requiredString('Contact description'),
  leadSeoTitle: requiredString('Lead SEO title'),
  leadSeoDescription: requiredString('Lead SEO description'),
  leadCaptureEyebrow: requiredString('Lead eyebrow'),
  leadCaptureTitle: requiredString('Lead title'),
  leadCaptureDescription: requiredString('Lead description'),
  leadCaptureThankYouTitle: requiredString('Thank you title'),
  leadCaptureThankYou: requiredString('Thank you message'),
  leadCaptureBenefitsHeading: requiredString('Benefits heading'),
  leadCaptureBenefits: z.array(titleDescriptionSchema).min(1, 'Add at least one benefit'),
  leadCaptureStats: z.array(valueLabelSchema).min(1, 'Add at least one stat'),
  leadFormFullNameLabel: requiredString('Lead full name label'),
  leadFormEmailLabel: requiredString('Lead email label'),
  leadFormPhoneLabel: requiredString('Lead phone label'),
  leadFormBudgetLabel: requiredString('Lead budget label'),
  leadFormMoveInLabel: requiredString('Lead move-in label'),
  leadFormNotesLabel: requiredString('Lead notes label'),
  leadFormSubmitLabel: requiredString('Lead submit label'),
  leadFormConsentText: requiredString('Lead consent text'),
  leadFormSuccessToast: requiredString('Lead success toast'),
  leadFormErrorToast: requiredString('Lead error toast'),
  aboutSeoTitle: requiredString('About SEO title'),
  aboutSeoDescription: requiredString('About SEO description'),
  aboutHeroTitle: requiredString('About hero title'),
  aboutHeroSubtitle: requiredString('About hero subtitle'),
  aboutTagline: requiredString('About tagline'),
  aboutHeroPrimaryCtaLabel: requiredString('Primary CTA label'),
  aboutHeroPrimaryCtaHref: hrefSchema,
  aboutHeroSecondaryCtaLabel: requiredString('Secondary CTA label'),
  aboutHeroSecondaryCtaHref: hrefSchema,
  aboutHighlightTitle: requiredString('Highlight title'),
  aboutHighlightDescription: requiredString('Highlight description'),
  aboutFeaturedProperties: requiredString('Featured properties'),
  aboutSnapshotEyebrow: requiredString('Snapshot eyebrow'),
  aboutSnapshotStats: z.array(labelValueSchema).min(1, 'Add at least one snapshot stat'),
  aboutBodyHeading: requiredString('About heading'),
  aboutBodyDescription: requiredString('About description'),
  aboutParagraphs: requiredString('About paragraphs'),
  aboutDetailsSummaryLabel: requiredString('Details summary label'),
  aboutPromiseLabel: requiredString('Promise label'),
  aboutValuesTitle: requiredString('Values title'),
  aboutValuesDescription: requiredString('Values description'),
  aboutValuesCtaLabel: requiredString('Values CTA label'),
  aboutValuesCtaHref: hrefSchema,
  aboutValuesPillars: z.array(titleDescriptionSchema).min(1, 'Add at least one value pillar'),
  aboutPortfolioTitle: requiredString('Portfolio title'),
  aboutPortfolioDescription: requiredString('Portfolio description'),
  aboutPortfolioCtaLabel: requiredString('Portfolio CTA label'),
  aboutPortfolioCtaHref: hrefSchema,
  aboutPortfolioFlagshipEyebrow: requiredString('Flagship eyebrow'),
  aboutPortfolioFlagshipTitle: requiredString('Flagship title'),
  aboutPortfolioFlagshipDescription: requiredString('Flagship description'),
  aboutPortfolioFlagshipStats: z.array(labelValueSchema).min(1, 'Add at least one flagship stat'),
  aboutPortfolioFlagshipFeaturesHeading: requiredString('Flagship features heading'),
  aboutPortfolioFlagshipFeaturesToggleLabel: requiredString('Flagship features toggle label'),
  aboutPortfolioFlagshipFeatures: stringListSchema.min(1, 'Add at least one flagship feature'),
  aboutPortfolioDowntownEyebrow: requiredString('Downtown eyebrow'),
  aboutPortfolioDowntownTitle: requiredString('Downtown title'),
  aboutPortfolioDowntownDescription: requiredString('Downtown description'),
  aboutPortfolioDowntownIdealHeading: requiredString('Downtown ideal heading'),
  aboutPortfolioDowntownTenants: stringListSchema.min(1, 'Add at least one ideal tenant'),
  aboutPortfolioDowntownAddressesHeading: requiredString('Downtown addresses heading'),
  aboutPortfolioRetailEyebrow: requiredString('Retail eyebrow'),
  aboutPortfolioRetailTitle: requiredString('Retail title'),
  aboutPortfolioRetailDescription: requiredString('Retail description'),
  aboutPortfolioRetailPlazas: stringListSchema.min(1, 'Add at least one retail plaza'),
  aboutPortfolioRetailFooter: requiredString('Retail footer'),
  aboutPortfolioRetailFooterLinkLabel: requiredString('Retail footer link label'),
  aboutContactHeading: requiredString('About contact heading'),
  aboutContactDescription: requiredString('About contact description'),
  contactPageSeoTitle: requiredString('Contact SEO title'),
  contactPageSeoDescription: requiredString('Contact SEO description'),
  contactPageHeroEyebrow: requiredString('Contact hero eyebrow'),
  contactPageHeroTitle: requiredString('Contact hero title'),
  contactPageHeroDescription: requiredString('Contact hero description'),
  contactPageHeroResponseTime: requiredString('Contact hero response time'),
  contactPageHeroCardEyebrow: requiredString('Hero card eyebrow'),
  contactPageHeroCardTitle: requiredString('Hero card title'),
  contactPageHeroCardDescription: requiredString('Hero card description'),
  contactPageHeroCardCallLabel: requiredString('Hero card call label'),
  contactPageHeroCardEmailLabel: requiredString('Hero card email label'),
  contactPageHeroCardMapLabel: requiredString('Hero card map label'),
  contactPageSupportTitle: requiredString('Support title'),
  contactPageSupportDescription: requiredString('Support description'),
  contactPageSupportHighlights: stringListSchema.min(1, 'Add at least one highlight'),
  contactPageSupportResponseTimeLabel: requiredString('Support response time label'),
  contactPageSupportResponseTimeValue: requiredString('Support response time value'),
  contactPageSupportDirectionsLabel: requiredString('Support directions label'),
  contactPageSupportAvailabilitiesLabel: requiredString('Support availabilities label'),
  contactPageDetailsTitle: requiredString('Details title'),
  contactPageDetailsDescription: requiredString('Details description'),
  contactPageFormTitle: requiredString('Form title'),
  contactPageFormDescription: requiredString('Form description'),
  contactPageFormSuccessMessage: requiredString('Form success message'),
  contactPageFormErrorMessage: requiredString('Form error message'),
  contactPageFormHelperText: requiredString('Form helper text'),
  contactPageFormSubmitLabel: requiredString('Form submit label'),
  contactPageFormFullNameLabel: requiredString('Full name label'),
  contactPageFormFullNamePlaceholder: requiredString('Full name placeholder'),
  contactPageFormPhoneLabel: requiredString('Phone label'),
  contactPageFormPhonePlaceholder: requiredString('Phone placeholder'),
  contactPageFormEmailLabel: requiredString('Email label'),
  contactPageFormEmailPlaceholder: requiredString('Email placeholder'),
  contactPageFormMessageLabel: requiredString('Message label'),
  contactPageFormMessagePlaceholder: requiredString('Message placeholder'),
  contactPageFormSpaceTypeLabel: requiredString('Space type label'),
  contactPageFormSpaceTypePlaceholder: requiredString('Space type placeholder'),
  contactPageFormSpaceTypeOptions: stringListSchema.min(1, 'Add at least one space type'),
  contactPageFormTargetSizeLabel: requiredString('Target size label'),
  contactPageFormTargetSizePlaceholder: requiredString('Target size placeholder'),
  contactPageFormTimelineLabel: requiredString('Timeline label'),
  contactPageFormTimelinePlaceholder: requiredString('Timeline placeholder'),
  contactPageFormTimelineOptions: stringListSchema.min(1, 'Add at least one timeline option'),
  availabilitiesSeoTitle: requiredString('Availabilities SEO title'),
  availabilitiesSeoDescription: requiredString('Availabilities SEO description'),
  availabilitiesHeadingEyebrow: requiredString('Availabilities eyebrow'),
  availabilitiesHeadingTitle: requiredString('Availabilities title'),
  availabilitiesHeadingDescription: requiredString('Availabilities description'),
  availabilitiesFiltersTitle: requiredString('Filters title'),
  availabilitiesFiltersSubtitle: requiredString('Filters subtitle'),
  availabilitiesFiltersEmptyLabel: requiredString('Empty filters label'),
  availabilitiesFiltersActiveTemplate: requiredString('Active filters template'),
  availabilitiesFiltersResetLabel: requiredString('Filters reset label'),
  availabilitiesFiltersOpenLabel: requiredString('Filters open label'),
  availabilitiesFiltersDrawerTitle: requiredString('Drawer title'),
  availabilitiesFiltersDrawerApplyLabel: requiredString('Drawer apply label'),
  availabilitiesFiltersDrawerResetLabel: requiredString('Drawer reset label'),
  availabilitiesFiltersChipPriceLabel: requiredString('Chip price label'),
  availabilitiesFiltersChipSizeLabel: requiredString('Chip size label'),
  availabilitiesFiltersChipUseLabel: requiredString('Chip use label'),
  availabilitiesViewLabel: requiredString('View label'),
  availabilitiesViewCardsLabel: requiredString('Cards label'),
  availabilitiesViewMapLabel: requiredString('Map label'),
  availabilitiesFilterPriceTitle: requiredString('Price filter title'),
  availabilitiesFilterPriceDescription: requiredString('Price filter description'),
  availabilitiesFilterSizeTitle: requiredString('Size filter title'),
  availabilitiesFilterSizeDescription: requiredString('Size filter description'),
  availabilitiesFilterUsesTitle: requiredString('Uses filter title'),
  availabilitiesFilterUsesDescription: requiredString('Uses filter description'),
  availabilitiesFilterMinLabel: requiredString('Min label'),
  availabilitiesFilterMaxLabel: requiredString('Max label'),
  availabilitiesFilterPlaceholder: requiredString('Filter placeholder'),
  availabilitiesResultsNoMatchesMessage: requiredString('No matches message'),
  availabilitiesResultsNoAvailabilityMessage: requiredString('No availability message'),
  availabilitiesResultsClearFiltersLabel: requiredString('Clear filters label'),
  availabilitiesResultsErrorMessage: requiredString('Results error message'),
  availabilitiesListAddressEyebrow: requiredString('List address eyebrow'),
  availabilitiesListUnitCountSingular: requiredString('Unit count singular'),
  availabilitiesListUnitCountPlural: requiredString('Unit count plural'),
  availabilitiesListViewPropertyLabel: requiredString('View property label'),
  availabilitiesMapBackLabel: requiredString('Map back label'),
  availabilitiesMapAddressEyebrow: requiredString('Map address eyebrow'),
  availabilitiesMapAddressesEyebrow: requiredString('Map addresses eyebrow'),
  availabilitiesMapBrowseTitle: requiredString('Map browse title'),
  availabilitiesMapBrowseDescription: requiredString('Map browse description'),
  availabilitiesMapSelectUnitEyebrow: requiredString('Map select unit eyebrow'),
  availabilitiesMapSelectUnitDescription: requiredString('Map select unit description'),
  availabilitiesMapTapPinEyebrow: requiredString('Map tap pin eyebrow'),
  availabilitiesMapTapPinDescription: requiredString('Map tap pin description'),
  availabilitiesMapSuiteCountSingular: requiredString('Map suite count singular'),
  availabilitiesMapSuiteCountPlural: requiredString('Map suite count plural'),
  availabilitiesMapPinFallback: requiredString('Map pin fallback'),
  availabilitiesMapUnitLabelTemplate: requiredString('Map unit label template'),
  availabilitiesMapLevelLabelTemplate: requiredString('Map floor label template'),
  availabilitiesMapDivisibleLabel: requiredString('Map divisible label'),
  availabilitiesMapIncentivesLabel: requiredString('Map incentives label'),
  availabilitiesQuickViewEyebrow: requiredString('Quick view eyebrow'),
  availabilitiesQuickViewTitleTemplate: requiredString('Quick view title template'),
  availabilitiesQuickViewRentLabel: requiredString('Quick view rent label'),
  availabilitiesQuickViewRentSuffix: requiredString('Quick view rent suffix'),
  availabilitiesQuickViewSizeLabel: requiredString('Quick view size label'),
  availabilitiesQuickViewAvailabilityLabel: requiredString('Quick view availability label'),
  availabilitiesQuickViewScheduleLabel: requiredString('Quick view schedule label'),
  availabilitiesQuickViewDetailsLabel: requiredString('Quick view details label'),
  availabilitiesQuickViewDivisibleLabel: requiredString('Quick view divisible label'),
  availabilitiesQuickViewIncentivesLabel: requiredString('Quick view incentives label'),
  propertyDetailMessagesUnpublished: requiredString('Property detail unpublished message'),
  propertyDetailMessagesCtaLabel: requiredString('Property detail CTA label'),
  propertyDetailNavOverview: requiredString('Overview tab label'),
  propertyDetailNavHighlights: requiredString('Highlights tab label'),
  propertyDetailNavLocation: requiredString('Location tab label'),
  propertyDetailNavAvailability: requiredString('Availability tab label'),
  propertyDetailAvailabilityNoUnits: requiredString('Availability no units label'),
  propertyDetailAvailabilityTemplate: requiredString('Availability template'),
  propertyDetailAvailabilityUpdatedLabel: requiredString('Availability updated label'),
  propertyDetailOverviewEyebrow: requiredString('Overview eyebrow'),
  propertyDetailOverviewViewOnMapLabel: requiredString('Overview view on map label'),
  propertyDetailOverviewTitleTemplate: requiredString('Overview title template'),
  propertyDetailUnitsEyebrow: requiredString('Units eyebrow'),
  propertyDetailUnitsTitle: requiredString('Units title'),
  propertyDetailUnitsListedTemplate: requiredString('Units listed template'),
  propertyDetailUnitsEmptyAll: requiredString('Units empty message'),
  propertyDetailUnitsEmptyFiltered: requiredString('Units filtered empty message'),
  propertyDetailUnitsCountSingular: requiredString('Unit count singular label'),
  propertyDetailUnitsCountPlural: requiredString('Unit count plural label'),
  propertyDetailUnitsFloorPlanFallback: requiredString('Floor plan fallback'),
  propertyDetailUnitsMonthlyLeaseLabel: requiredString('Monthly lease label'),
  propertyDetailUnitsContactLabel: requiredString('Contact label'),
  propertyDetailUnitsContactFallback: requiredString('Contact fallback'),
  propertyDetailUnitsBookButtonLabel: requiredString('Book button label'),
  propertyDetailUnitsViewDetailsLabel: requiredString('View details label'),
  propertyDetailUnitsTableSuiteHeader: requiredString('Suite header'),
  propertyDetailUnitsTableFloorHeader: requiredString('Floor header'),
  propertyDetailUnitsTableSizeHeader: requiredString('Size header'),
  propertyDetailUnitsTableMonthlyLeaseHeader: requiredString('Monthly lease header'),
  propertyDetailUnitsTableRateHeader: requiredString('Rate header'),
  propertyDetailUnitsTableActionHeader: requiredString('Action header'),
  propertyDetailUnitsTableNoUnitsMessage: requiredString('Units table empty message'),
  propertyDetailUnitsTableNoMatchesMessage: requiredString('Units table no matches message'),
  propertyDetailUnitFiltersSizeLabel: requiredString('Size filter label'),
  propertyDetailUnitFiltersFloorLabel: requiredString('Floor filter label'),
  propertyDetailUnitFiltersRateLabel: requiredString('Rate filter label'),
  propertyDetailUnitFiltersSizeAllLabel: requiredString('Size all label'),
  propertyDetailUnitFiltersFloorAllLabel: requiredString('Floor all label'),
  propertyDetailUnitFiltersRateAllLabel: requiredString('Rate all label'),
  propertyDetailHighlightsEyebrow: requiredString('Highlights eyebrow'),
  propertyDetailHighlightsTitle: requiredString('Highlights title'),
  propertyDetailHighlightsDescription: requiredString('Highlights description'),
  propertyDetailHighlightsDivisibleLabel: requiredString('Divisible label'),
  propertyDetailHighlightsIncentivesLabel: requiredString('Incentives label'),
  propertyDetailHighlightsFloorPlanLabel: requiredString('Floor plan label'),
  propertyDetailHighlightsEmptyMessage: requiredString('Highlights empty message'),
  propertyDetailLocationTitle: requiredString('Location title'),
  propertyDetailLocationDirectionsLabel: requiredString('Directions label'),
  propertyDetailKeyFiguresTitle: requiredString('Key figures title'),
  propertyDetailKeyFiguresSubtitle: requiredString('Key figures subtitle'),
  propertyDetailKeyFiguresTotalRentableLabel: requiredString('Total rentable label'),
  propertyDetailKeyFiguresUnitsLiveLabel: requiredString('Units live label'),
  propertyDetailKeyFiguresIncentivesLabel: requiredString('Key figures incentives label'),
  propertyDetailKeyFiguresDivisibleLabel: requiredString('Key figures divisible label'),
  propertyDetailLabelsRentFallback: requiredString('Rent fallback label'),
  propertyDetailLabelsAvailabilityRangeFallback: requiredString('Availability range fallback'),
  propertyDetailLabelsAvailabilitySingleFallback: requiredString('Availability single fallback'),
  propertyDetailLabelsUnitImmediateLabel: requiredString('Unit immediate label'),
  propertyDetailLabelsUnitNegotiableLabel: requiredString('Unit negotiable label'),
  unitDetailNotFoundMessage: requiredString('Unit not found message'),
  unitDetailUnavailableMessage: requiredString('Unit unavailable message'),
  unitDetailViewPropertyLabel: requiredString('View property label'),
  unitDetailBrowseAvailabilitiesLabel: requiredString('Browse availabilities label'),
  unitDetailViewOtherUnitsLabel: requiredString('View other units label'),
  unitDetailStickyEyebrow: requiredString('Sticky eyebrow'),
  unitDetailStickyButtonLabel: requiredString('Sticky button label'),
  unitDetailAtAGlanceTitle: requiredString('At a glance title'),
  unitDetailPropertyDetailsTitle: requiredString('Property details title'),
  unitDetailLeaseDetailsTitle: requiredString('Lease details title'),
  unitDetailPropertyDescriptionEyebrow: requiredString('Property description eyebrow'),
  unitDetailPropertyDescriptionTitle: requiredString('Property description title'),
  unitDetailScheduleEyebrow: requiredString('Schedule eyebrow'),
  unitDetailScheduleTitle: requiredString('Schedule title'),
  unitDetailLeasingDetailsEyebrow: requiredString('Leasing details eyebrow'),
  unitDetailLeasingDetailsTitle: requiredString('Leasing details title'),
  unitDetailPhotoGalleryTitle: requiredString('Photo gallery title'),
  unitDetailSizeLabel: requiredString('Size label'),
  unitDetailFloorLabel: requiredString('Floor label'),
  unitDetailDivisibleLabel: requiredString('Divisible label'),
  unitDetailStatusLabel: requiredString('Status label'),
  unitDetailAvailabilityLabel: requiredString('Availability label'),
  unitDetailPossessionLabel: requiredString('Possession label'),
  unitDetailUpdatedLabel: requiredString('Updated label'),
  unitDetailImmediateLabel: requiredString('Immediate label'),
  unitDetailNegotiableLabel: requiredString('Negotiable label'),
  unitDetailImmediatePossessionLabel: requiredString('Immediate possession label'),
  unitDetailNegotiablePossessionLabel: requiredString('Negotiable possession label'),
  unitDetailUpdatedRecentlyLabel: requiredString('Updated recently label'),
  unitDetailLeasingPromptIndustrial: requiredString('Industrial leasing prompt'),
  unitDetailLeasingPromptFlex: requiredString('Flex leasing prompt'),
  unitDetailLeasingPromptRetail: requiredString('Retail leasing prompt'),
  unitDetailLeasingPromptOffice: requiredString('Office leasing prompt'),
  unitDetailLeasingPromptFallback: requiredString('Fallback leasing prompt'),
  unitDetailPhotoGalleryEmptyMessage: requiredString('Photo gallery empty message'),
  showingDialogTitle: requiredString('Showing dialog title'),
  showingDialogDescription: requiredString('Showing dialog description'),
  showingDialogStepIndicatorTemplate: requiredString('Step indicator template'),
  showingDialogSelectedUnitLabel: requiredString('Selected unit label'),
  showingDialogSelectedUnitDescription: requiredString('Selected unit description'),
  showingDialogStepContactLabel: requiredString('Contact step label'),
  showingDialogStepScheduleLabel: requiredString('Schedule step label'),
  showingDialogStepReviewLabel: requiredString('Review step label'),
  showingDialogFieldFirstNameLabel: requiredString('First name label'),
  showingDialogFieldLastNameLabel: requiredString('Last name label'),
  showingDialogFieldEmailLabel: requiredString('Email label'),
  showingDialogFieldPhoneLabel: requiredString('Phone label'),
  showingDialogFieldDateLabel: requiredString('Date label'),
  showingDialogFieldTimeLabel: requiredString('Time label'),
  showingDialogFieldMessageLabel: requiredString('Message label'),
  showingDialogFieldDateHint: requiredString('Date hint'),
  showingDialogFieldTimeHint: requiredString('Time hint'),
  showingDialogMessagePlaceholder: requiredString('Message placeholder'),
  showingDialogReviewTitle: requiredString('Review title'),
  showingDialogReviewContactLabel: requiredString('Review contact label'),
  showingDialogReviewSlotLabel: requiredString('Review slot label'),
  showingDialogReviewUnitLabel: requiredString('Review unit label'),
  showingDialogReviewMessageLabel: requiredString('Review message label'),
  showingDialogReviewConfirmationNote: requiredString('Review confirmation note'),
  showingDialogReviewGuidanceNext: requiredString('Review guidance next'),
  showingDialogReviewGuidanceReview: requiredString('Review guidance review'),
  showingDialogButtonCancelLabel: requiredString('Cancel button label'),
  showingDialogButtonBackLabel: requiredString('Back button label'),
  showingDialogButtonNextLabel: requiredString('Next button label'),
  showingDialogButtonConfirmLabel: requiredString('Confirm button label'),
  showingDialogButtonDoneLabel: requiredString('Done button label'),
  showingDialogErrorContactMissing: requiredString('Contact error message'),
  showingDialogErrorScheduleMissing: requiredString('Schedule error message'),
  showingDialogErrorSubmit: requiredString('Submit error message'),
  showingDialogSuccessTitle: requiredString('Success title'),
  showingDialogSuccessDescription: requiredString('Success description'),
  showingDialogSuccessTimeLabel: requiredString('Success time label'),
  showingDialogSuccessEmailLabel: requiredString('Success email label'),
  showingDialogSuccessNameLabel: requiredString('Success name label'),
  showingDialogSuccessMessageLabel: requiredString('Success message label'),
  showingDialogSuccessCalendarLabel: requiredString('Calendar label'),
  showingDialogSuccessPortalQueued: requiredString('Portal queued message'),
  showingDialogSuccessPortalSent: requiredString('Portal sent message'),
  showingDialogUnavailableTooltip: requiredString('Unavailable tooltip'),
  sharedContactAddressLabel: requiredString('Contact address label'),
  sharedContactPhoneLabel: requiredString('Contact phone label'),
  sharedContactEmailLabel: requiredString('Contact email label'),
  sharedContactHoursLabel: requiredString('Contact hours label'),
  sharedContactLeadershipLabel: requiredString('Contact leadership label'),
  sharedContactMapTitle: requiredString('Contact map title'),
  sharedContactMapPreviewTitle: requiredString('Map preview title'),
  sharedContactMapPreviewDescription: requiredString('Map preview description'),
  sharedContactMapFooter: requiredString('Map footer'),
  sharedContactMapLinkLabel: requiredString('Map link label'),
  sharedAvailabilityAlertsEyebrow: requiredString('Alerts eyebrow'),
  sharedAvailabilityAlertsTitle: requiredString('Alerts title'),
  sharedAvailabilityAlertsDescription: requiredString('Alerts description'),
  sharedAvailabilityAlertsEmailLabel: requiredString('Alerts email label'),
  sharedAvailabilityAlertsEmailPlaceholder: requiredString('Alerts email placeholder'),
  sharedAvailabilityAlertsSubmitLabel: requiredString('Alerts submit label'),
  sharedAvailabilityAlertsSuccessMessage: requiredString('Alerts success message'),
  sharedAvailabilityAlertsErrorMessage: requiredString('Alerts error message'),
  sharedAvailabilityAlertsNote: requiredString('Alerts note'),
  sharedPropertyCardPhotoFallback: requiredString('Property card photo fallback'),
  sharedPropertyCardRentFallback: requiredString('Property card rent fallback'),
  sharedPropertyCardAvailabilityFallback: requiredString('Property card availability fallback'),
  sharedPropertyCardDivisibleLabel: requiredString('Property card divisible label'),
  sharedPropertyCardVacancyLabel: requiredString('Property card vacancy label'),
  sharedPropertyCardVacancyAvailableLabel: requiredString('Property card vacancy available'),
  sharedPropertyCardVacancyLeasedLabel: requiredString('Property card vacancy leased'),
  sharedPropertyCardSizeLabel: requiredString('Property card size label'),
  sharedPropertyCardUnitCountZeroLabel: requiredString('Property card unit count zero'),
  sharedPropertyCardUnitCountSingularLabel: requiredString('Property card unit count singular'),
  sharedPropertyCardUnitCountPluralLabel: requiredString('Property card unit count plural'),
  sharedUnitCardPhotoFallback: requiredString('Unit card photo fallback'),
  sharedUnitCardAvailabilityLabel: requiredString('Unit card availability label'),
  sharedUnitCardAvailabilityFallback: requiredString('Unit card availability fallback'),
  sharedUnitCardFloorLabel: requiredString('Unit card floor label'),
  sharedUnitCardViewDetailsLabel: requiredString('Unit card view details label'),
  sharedUnitCardDivisibleLabel: requiredString('Unit card divisible label'),
  sharedEmptyStatePropertyGrid: requiredString('Property grid empty state'),
  sharedEmptyStateUnitGroup: requiredString('Unit group empty state'),
});

type SiteSettingsFormValues = z.infer<typeof formSchema>;

type NavigationChildFormValues = z.infer<typeof navigationChildSchema>;
type NavigationItemFormValues = z.infer<typeof navigationItemSchema>;
type NavigationLinksFormValues = NavigationItemFormValues[];

type NavigationDisplayValue = 'link' | 'cta' | 'call' | 'login';

const NAVIGATION_DISPLAY_OPTIONS: Array<{
  value: NavigationDisplayValue;
  label: string;
  action: NavigationItemFormValues['action'];
  style: NavigationItemFormValues['style'];
}> = [
  { value: 'link', label: 'Text link', action: 'link', style: 'link' },
  { value: 'cta', label: 'CTA button', action: 'link', style: 'button' },
  { value: 'call', label: 'Call button', action: 'call', style: 'button' },
  { value: 'login', label: 'Login button', action: 'auth', style: 'outline' },
];

const resolveNavigationAction = (
  value: NavigationItemFormValues['action'] | undefined,
): NavigationItemFormValues['action'] => {
  if (value === 'call' || value === 'auth') {
    return value;
  }
  return 'link';
};

const resolveNavigationStyle = (
  value: NavigationItemFormValues['style'] | undefined,
): NavigationItemFormValues['style'] => {
  if (value === 'button' || value === 'outline') {
    return value;
  }
  return 'link';
};

const resolveNavigationDisplay = (item: NavigationItemFormValues): NavigationDisplayValue => {
  const action = resolveNavigationAction(item.action);
  const style = resolveNavigationStyle(item.style);

  if (action === 'auth') {
    return 'login';
  }
  if (action === 'call') {
    return 'call';
  }
  if (style === 'button') {
    return 'cta';
  }
  return 'link';
};

const createNavigationId = (prefix: string): string => {
  if (typeof globalThis.crypto.randomUUID === 'function') {
    return `${prefix}-${globalThis.crypto.randomUUID()}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
};

const createNavigationChild = (
  overrides: Partial<NavigationChildFormValues> = {},
): NavigationChildFormValues => ({
  id: overrides.id ?? createNavigationId('nav-child'),
  label: overrides.label ?? '',
  href: overrides.href ?? '',
  propertyId: overrides.propertyId ?? '',
  enabled: overrides.enabled ?? true,
  external: overrides.external ?? false,
});

const createNavigationItem = (
  overrides: Partial<NavigationItemFormValues> = {},
): NavigationItemFormValues => ({
  id: overrides.id ?? createNavigationId('nav-item'),
  label: overrides.label ?? '',
  href: overrides.href ?? '',
  enabled: overrides.enabled ?? true,
  external: overrides.external ?? false,
  action: resolveNavigationAction(overrides.action),
  style: resolveNavigationStyle(overrides.style),
  children: (overrides.children ?? []).map((child) => createNavigationChild(child)),
});

type NavigationPropertyOption = {
  id: string;
  label: string;
};

const EMPTY_FORM: SiteSettingsFormValues = {
  brandSiteName: 'Strashin Developments',
  brandTagline: 'Leasing & Workspaces',
  brandLogoUrl: '',
  brandFaviconUrl: '/logo.jpg',
  brandDefaultOgImageUrl: '/logo.jpg',
  seoDefaultDescription:
    'Strashin Developments delivers sustainable commercial real estate with smart building systems and tenant-first amenities.',
  seoCanonicalOrigin: 'https://strashindevelopments.com',
  navigationLinks: [
    createNavigationItem({ label: 'Availabilities', href: '/availabilities' }),
    createNavigationItem({ label: 'About Us', href: '/about' }),
    createNavigationItem({ label: 'Contact', href: '/contact' }),
  ],
  navigationMobileTitle: 'Browse Strashin',
  navigationMobileDescription: 'Jump to sections or manage your account.',
  navigationMobileShortcutHeading: 'Need a shortcut?',
  navigationMobileShortcutDescription: 'Browse availability or contact the team.',
  navigationMobilePrimaryLabel: 'View availability',
  navigationMobilePrimaryHref: '/availabilities',
  navigationMobileSecondaryLabel: 'Contact',
  navigationMobileSecondaryHref: '/contact',
  footerCompanyName: 'Strashin Developments',
  footerCopyrightText: '© {year} Strashin Developments. All rights reserved.',
  bookingEmail: '',
  contactEmail: '',
  contactPhone: '',
  contactAddressLine1: '',
  contactAddressLine2: '',
  mapEmbedUrl: '',
  contactHours: [
    { label: 'Monday–Friday', value: '' },
    { label: 'Saturday & Sunday', value: '' },
  ],
  leadershipContacts: [{ name: '', title: '' }],
  homeSeoTitle: 'Sustainable Commercial Real Estate',
  homeSeoDescription:
    'Explore Strashin Developments’ 501 Alliance campus — a next-generation, low-carbon workspace designed for high-performing teams.',
  homeHeroEyebrow: 'Flagship Property',
  homeHeroTitle: '501 Alliance — Toronto’s next-generation green office campus',
  homeHeroDescription:
    'Powered by rooftop solar and geothermal systems, 501 Alliance delivers low-carbon operations, optimized air quality, and smart building efficiency—helping teams work better and tread lighter.',
  homeHeroMediaLabel: '501 Alliance',
  homeHeroMediaLocation: 'Junction Triangle, Toronto',
  homeHeroMediaAriaLabel: 'Interview video at 501 Alliance',
  homeHeroMediaUrl: '',
  homeHeroMediaType: 'video',
  homeAvailabilityEyebrow: 'Featured Availability',
  homeAvailabilityTitle: 'Units Available Now',
  homeAvailabilityDescription:
    'Browse every published available unit in our portfolio, grouped by property. Jump straight into the details to see rent, size, and timing.',
  homeAvailabilityCtaLabel: 'View property portfolio',
  homeAvailabilityCtaHref: '/availabilities',
  homeAvailabilityEmptyMessage:
    'No units are published right now. Check back soon for new availability.',
  homeAvailabilityErrorMessage: 'We’re unable to load availability right now.',
  homeContactHeading: 'Contact us',
  homeContactDescription: 'Reach out for leasing, tenant services, and partnership inquiries.',
  leadSeoTitle: 'Schedule a Tour with Strashin Developments',
  leadSeoDescription:
    'Tell us about your space requirements and our leasing team will connect with tailored options across the Strashin Developments portfolio.',
  leadCaptureEyebrow: 'Leasing Concierge',
  leadCaptureTitle: 'Unlock your next workspace',
  leadCaptureDescription:
    'Tell us about your space requirements and our leasing team will connect with tailored options across the Strashin Developments portfolio.',
  leadCaptureThankYouTitle: 'Thank you',
  leadCaptureThankYou:
    'Our leasing team has received your request and will reach out within 48 hours with tailored options. If you need to update any details, reply to the confirmation email.',
  leadCaptureBenefitsHeading: 'Why partner with Strashin',
  leadCaptureBenefits: [
    {
      title: 'Operational transparency',
      description:
        'We surface availability, pricing, and sustainability data up front so you can evaluate options quickly.',
    },
    {
      title: 'Curated tours',
      description:
        'Our team schedules on-site tours and virtual walk-throughs tailored to your campaign goals.',
    },
    {
      title: 'End-to-end support',
      description:
        'From first contact to lease negotiation and tenant onboarding, we stay aligned with your milestones.',
    },
  ],
  leadCaptureStats: [
    { value: '48 hrs', label: 'Average response time on new leasing requests.' },
    { value: '6M+ sqft', label: 'Managed across Strashin’s sustainable portfolio.' },
    { value: '98%', label: 'Tenant retention rate thanks to proactive service.' },
  ],
  leadFormFullNameLabel: 'Full name',
  leadFormEmailLabel: 'Work email',
  leadFormPhoneLabel: 'Phone number (optional)',
  leadFormBudgetLabel: 'Monthly budget (CAD)',
  leadFormMoveInLabel: 'Target move-in date',
  leadFormNotesLabel: 'Tell us more about your needs',
  leadFormSubmitLabel: 'Submit request',
  leadFormConsentText:
    'By submitting this form you consent to Strashin Developments contacting you about leasing opportunities. You can opt out at any time.',
  leadFormSuccessToast: 'Lead submitted. Our team will be in touch shortly.',
  leadFormErrorToast: 'We could not submit your request. Please try again.',
  aboutSeoTitle: 'About Us',
  aboutSeoDescription:
    'Strashin Developments Ltd. is a Toronto-based property management and redevelopment firm focused on adaptive reuse, efficient systems, and trouble-free workspaces.',
  aboutHeroTitle: 'Toronto-based property management and redevelopment focused on adaptive reuse.',
  aboutHeroSubtitle:
    'We bring multi-disciplinary real estate experience across office, multi-family residential, retail, and industrial properties with a commitment to sustainable, trouble-free workspaces.',
  aboutTagline: 'About Us',
  aboutHeroPrimaryCtaLabel: 'View availabilities',
  aboutHeroPrimaryCtaHref: '/availabilities',
  aboutHeroSecondaryCtaLabel: 'Contact us',
  aboutHeroSecondaryCtaHref: '/contact',
  aboutHighlightTitle: 'Toronto Downtown West portfolio',
  aboutHighlightDescription:
    "Over 130,000 sq ft across four character post-and-beam buildings in Toronto's downtown west.",
  aboutFeaturedProperties:
    '460 Richmond Street West\n20 Maud Street\n20 Camden Street\n487 Adelaide Street West',
  aboutSnapshotEyebrow: 'At a glance',
  aboutSnapshotStats: [
    { label: 'CleanTech campus', value: '315,000 sq ft' },
    { label: 'Downtown West portfolio', value: '130,000+ sq ft' },
    { label: 'Retail legacy', value: 'Since 1958' },
  ],
  aboutBodyHeading: 'Strashin Developments Ltd.',
  aboutBodyDescription:
    'A Toronto team managing and redeveloping properties for modern tenants across the GTA.',
  aboutParagraphs:
    "Strashin Developments Ltd. is a Toronto-based property management and redevelopment firm. We manage and redevelop properties across downtown Toronto and the Greater Toronto Area, with multi-disciplinary experience across office, multi-family residential, retail, and industrial real estate investment, management, and development.\n\nWe believe the most sustainable building is the one already standing. By adaptively reusing and repurposing structures, we can often achieve greater carbon savings than new construction. Our projects incorporate high-efficiency upgrades such as reflective roofing, rooftop solar, geothermal HVAC, efficient plumbing fixtures, LED lighting and controls, and storm-water harvesting.\n\nA flagship example is 501 Alliance Avenue, a major green renovation of the former Cooper Canada sporting goods factory near Weston Road and Black Creek. Operated through 501 Alliance Investments Inc., the campus spans 315,000 sq ft and offers spaces from 2,000 to 67,000 sq ft. It is zoned Prestige Employment and accommodates industrial, office, and retail uses, with surface and underground parking.\n\nIn Toronto's downtown west, we manage a portfolio of four character post-and-beam buildings totaling over 130,000 sq ft. Units range from 600 to 7,800 sq ft with surface parking, supporting startups, tech teams, creative agencies, fitness studios, legal firms, retailers, restaurants, consultants, and more.\n\nThrough our parent company S Strashin and Sons Ltd., we have offered retail space since 1958. Today we operate the Bradstock and Weston properties near Weston Road and Bradstock Avenue between Sheppard Avenue and Finch Avenue, as well as the Victoria Park Plaza at Victoria Park Avenue and St Clair Avenue. Availability changes over time; contact us for current openings.\n\nOur promise is simple: deliver trouble-free, sustainable workspaces that help tenants stay productive. A hassle-free workspace is a direct contributor to business success, and we treat it that way every day.",
  aboutDetailsSummaryLabel: 'More about our portfolio and history',
  aboutPromiseLabel: 'Our promise',
  aboutValuesTitle: 'Sustainability, service, and long-term value',
  aboutValuesDescription:
    'From adaptive reuse to tenant-first operations, we build and manage spaces designed for modern teams.',
  aboutValuesCtaLabel: 'View availabilities',
  aboutValuesCtaHref: '/availabilities',
  aboutValuesPillars: [
    {
      title: 'Adaptive reuse',
      description:
        'We focus on reusing and upgrading existing buildings to cut embodied carbon while preserving character.',
    },
    {
      title: 'Efficient systems',
      description:
        'Reflective roofing, rooftop solar, geothermal HVAC, LED lighting, efficient plumbing, and storm-water harvesting deliver lasting performance.',
    },
    {
      title: 'Trouble-free tenancy',
      description:
        'We prioritize smooth operations so tenants can stay productive with fewer distractions and surprises.',
    },
  ],
  aboutPortfolioTitle: 'Portfolio highlights',
  aboutPortfolioDescription:
    'A snapshot of our flagship redevelopment and the portfolios we operate across Toronto.',
  aboutPortfolioCtaLabel: 'View availabilities',
  aboutPortfolioCtaHref: '/availabilities',
  aboutPortfolioFlagshipEyebrow: 'CleanTech and IT centre',
  aboutPortfolioFlagshipTitle: '501 Alliance Avenue',
  aboutPortfolioFlagshipDescription:
    'A major green renovation of the former Cooper Canada sporting goods factory, near Weston Road and Black Creek, operated through 501 Alliance Investments Inc.',
  aboutPortfolioFlagshipStats: [
    { label: 'Total campus', value: '315,000 sq ft' },
    { label: 'Space ranges', value: '2,000 to 67,000 sq ft' },
    { label: 'Zoning', value: 'Prestige Employment' },
    { label: 'Uses', value: 'Industrial, office, and retail' },
  ],
  aboutPortfolioFlagshipFeaturesHeading: 'Sustainability features',
  aboutPortfolioFlagshipFeaturesToggleLabel: 'See all sustainability features',
  aboutPortfolioFlagshipFeatures: [
    'High-SRI reflective roof',
    '400+ kW rooftop solar farm',
    'Insulated roof and walls',
    'Storm-water harvesting',
    'Geothermal HVAC',
    'Efficient plumbing fixtures',
    'High-efficiency LED lighting and controls',
    'Geothermal heated parking lots',
    'Bicycle parking and EV-ready spaces',
    'Fibre-optic connectivity',
  ],
  aboutPortfolioDowntownEyebrow: 'Downtown West portfolio',
  aboutPortfolioDowntownTitle: 'Character post-and-beam buildings',
  aboutPortfolioDowntownDescription:
    "Over 130,000 sq ft across four buildings in Toronto's downtown west, with unit sizes from 600 to 7,800 sq ft and surface parking available.",
  aboutPortfolioDowntownIdealHeading: 'Ideal for',
  aboutPortfolioDowntownTenants: [
    'Startups and tech companies',
    'Creative agencies and studios',
    'Fitness and wellness operators',
    'Legal and professional services',
    'Retail and hospitality concepts',
    'Consulting and client-facing teams',
  ],
  aboutPortfolioDowntownAddressesHeading: 'Addresses',
  aboutPortfolioRetailEyebrow: 'Retail plazas',
  aboutPortfolioRetailTitle: 'Retail space since 1958',
  aboutPortfolioRetailDescription:
    'Through our parent company S Strashin and Sons Ltd., we have offered retail space across Toronto for decades. Availability changes over time.',
  aboutPortfolioRetailPlazas: [
    'Bradstock and Weston properties (Weston Road at Bradstock Ave, between Sheppard Ave and Finch Ave)',
    'Victoria Park Plaza (Victoria Park Ave at St Clair Ave)',
  ],
  aboutPortfolioRetailFooter:
    'For current retail openings, reach out via the contact details below or visit the',
  aboutPortfolioRetailFooterLinkLabel: 'Contact page',
  aboutContactHeading: 'Contact Strashin Developments',
  aboutContactDescription:
    'Connect with our team for leasing inquiries, tenant services, and redevelopment opportunities.',
  contactPageSeoTitle: 'Contact Us',
  contactPageSeoDescription:
    'Contact Strashin Developments for trouble-free commercial workspaces tailored to your business.',
  contactPageHeroEyebrow: 'Contact Us',
  contactPageHeroTitle: 'Contact Us For Trouble-Free Commercial Workspaces',
  contactPageHeroDescription:
    'We prioritize smooth, reliable tenant experiences so you can focus on your business. Let us help you find the right workspace.',
  contactPageHeroResponseTime: 'Response time: within 1 business day.',
  contactPageHeroCardEyebrow: 'Quick contact',
  contactPageHeroCardTitle: 'Talk to our leasing team',
  contactPageHeroCardDescription:
    'Share your timeline and space needs. We respond within 1 business day.',
  contactPageHeroCardCallLabel: 'Call now',
  contactPageHeroCardEmailLabel: 'Email us',
  contactPageHeroCardMapLabel: 'Open in maps',
  contactPageSupportTitle: 'What happens next',
  contactPageSupportDescription:
    'Share a few details and we will confirm availability, match you with best-fit spaces, and coordinate next steps.',
  contactPageSupportHighlights: [
    'Shortlist of suites matched to size, use, and timing.',
    'Tour coordination and tenant support.',
    'Clear next steps on pricing, incentives, and fit-up options.',
  ],
  contactPageSupportResponseTimeLabel: 'Response time',
  contactPageSupportResponseTimeValue: 'Within 1 business day',
  contactPageSupportDirectionsLabel: 'Get directions',
  contactPageSupportAvailabilitiesLabel: 'View availabilities',
  contactPageDetailsTitle: 'Contact details',
  contactPageDetailsDescription: 'Reach our team directly for leasing inquiries or tenant support.',
  contactPageFormTitle: 'Send us a message',
  contactPageFormDescription:
    'Share your contact details and workspace needs. We respond within 1 business day.',
  contactPageFormSuccessMessage:
    'Thank you, {name}. We received your message and will be in touch shortly.',
  contactPageFormErrorMessage: 'Unable to submit your message.',
  contactPageFormHelperText: 'Prefer to talk? Call us at {phone}.',
  contactPageFormSubmitLabel: 'Send message',
  contactPageFormFullNameLabel: 'Full name',
  contactPageFormFullNamePlaceholder: 'Jane Doe',
  contactPageFormPhoneLabel: 'Phone number',
  contactPageFormPhonePlaceholder: '{phone}',
  contactPageFormEmailLabel: 'Email address',
  contactPageFormEmailPlaceholder: 'you@example.com',
  contactPageFormMessageLabel: 'Message',
  contactPageFormMessagePlaceholder: 'Tell us about your ideal workspace and timing.',
  contactPageFormSpaceTypeLabel: 'Space type (optional)',
  contactPageFormSpaceTypePlaceholder: 'Select a space type',
  contactPageFormSpaceTypeOptions: ['Office', 'Retail', 'Industrial', 'Flex', 'Other'],
  contactPageFormTargetSizeLabel: 'Target size in sq ft (optional)',
  contactPageFormTargetSizePlaceholder: '2,500',
  contactPageFormTimelineLabel: 'Move-in timeline (optional)',
  contactPageFormTimelinePlaceholder: 'Select a timeline',
  contactPageFormTimelineOptions: ['Immediately', '1-3 months', '3-6 months', '6+ months'],
  availabilitiesSeoTitle: 'Availabilities',
  availabilitiesSeoDescription:
    'Browse every available unit across Strashin Developments, grouped by address with 501 Alliance highlighted first.',
  availabilitiesHeadingEyebrow: 'Availabilities',
  availabilitiesHeadingTitle: 'Current units available',
  availabilitiesHeadingDescription:
    "Explore all available suites across our portfolio. Listings are grouped by address so you can quickly scan what's open at each property.",
  availabilitiesFiltersTitle: 'Filters',
  availabilitiesFiltersSubtitle: 'Filter by price, size, and use.',
  availabilitiesFiltersEmptyLabel: 'No active filters',
  availabilitiesFiltersActiveTemplate: '{count} active',
  availabilitiesFiltersResetLabel: 'Reset',
  availabilitiesFiltersOpenLabel: 'Open filters',
  availabilitiesFiltersDrawerTitle: 'Filter availabilities',
  availabilitiesFiltersDrawerApplyLabel: 'Apply',
  availabilitiesFiltersDrawerResetLabel: 'Reset',
  availabilitiesFiltersChipPriceLabel: 'Price',
  availabilitiesFiltersChipSizeLabel: 'Size',
  availabilitiesFiltersChipUseLabel: 'Use',
  availabilitiesViewLabel: 'View',
  availabilitiesViewCardsLabel: 'Cards',
  availabilitiesViewMapLabel: 'Map + list',
  availabilitiesFilterPriceTitle: 'Price per square foot',
  availabilitiesFilterPriceDescription: 'Net rent per SF (exclude TMI).',
  availabilitiesFilterSizeTitle: 'Suite size (sq ft)',
  availabilitiesFilterSizeDescription: 'Filter by total rentable area.',
  availabilitiesFilterUsesTitle: 'Permitted uses',
  availabilitiesFilterUsesDescription: 'Choose one or more building types.',
  availabilitiesFilterMinLabel: 'Min',
  availabilitiesFilterMaxLabel: 'Max',
  availabilitiesFilterPlaceholder: 'Any',
  availabilitiesResultsNoMatchesMessage: 'No available units match these filters.',
  availabilitiesResultsNoAvailabilityMessage:
    'No units are available at the moment. Check back soon for new availability.',
  availabilitiesResultsClearFiltersLabel: 'Clear filters',
  availabilitiesResultsErrorMessage: "We're unable to load availabilities right now.",
  availabilitiesListAddressEyebrow: 'Address',
  availabilitiesListUnitCountSingular: '1 unit available',
  availabilitiesListUnitCountPlural: '{count} units available',
  availabilitiesListViewPropertyLabel: 'View property overview',
  availabilitiesMapBackLabel: 'All addresses',
  availabilitiesMapAddressEyebrow: 'Address',
  availabilitiesMapAddressesEyebrow: 'Addresses with availability',
  availabilitiesMapBrowseTitle: 'Browse pins',
  availabilitiesMapBrowseDescription:
    'Tap a pin on the map, or pick an address to preview available suites.',
  availabilitiesMapSelectUnitEyebrow: 'Select a unit',
  availabilitiesMapSelectUnitDescription:
    'Tap a suite above to preview rent, size, and availability before opening the full details page.',
  availabilitiesMapTapPinEyebrow: 'Tap a pin',
  availabilitiesMapTapPinDescription:
    'Select an address to see every available suite at that location.',
  availabilitiesMapSuiteCountSingular: '1 suite',
  availabilitiesMapSuiteCountPlural: '{count} suites',
  availabilitiesMapPinFallback: 'Map pin coming soon',
  availabilitiesMapUnitLabelTemplate: 'Unit {unitLabel}',
  availabilitiesMapLevelLabelTemplate: '{floor}',
  availabilitiesMapDivisibleLabel: 'Divisible',
  availabilitiesMapIncentivesLabel: 'Incentives',
  availabilitiesQuickViewEyebrow: 'Preview',
  availabilitiesQuickViewTitleTemplate: 'Unit {unitLabel} · Floor {floor}',
  availabilitiesQuickViewRentLabel: 'Rent (est.)',
  availabilitiesQuickViewRentSuffix: '/ mo',
  availabilitiesQuickViewSizeLabel: 'Size',
  availabilitiesQuickViewAvailabilityLabel: 'Availability',
  availabilitiesQuickViewScheduleLabel: 'Schedule showing',
  availabilitiesQuickViewDetailsLabel: 'See unit details',
  availabilitiesQuickViewDivisibleLabel: 'Divisible',
  availabilitiesQuickViewIncentivesLabel: 'Incentives',
  propertyDetailMessagesUnpublished: 'This listing may have been unpublished.',
  propertyDetailMessagesCtaLabel: 'Explore availabilities',
  propertyDetailNavOverview: 'Overview',
  propertyDetailNavHighlights: 'Highlights',
  propertyDetailNavLocation: 'Location',
  propertyDetailNavAvailability: 'Availability',
  propertyDetailAvailabilityNoUnits: 'No units currently available',
  propertyDetailAvailabilityTemplate: '{available} of {total} units available',
  propertyDetailAvailabilityUpdatedLabel: 'Availability updated regularly.',
  propertyDetailOverviewEyebrow: 'Property',
  propertyDetailOverviewViewOnMapLabel: 'View on map',
  propertyDetailOverviewTitleTemplate: '{assetType} in {submarket}',
  propertyDetailUnitsEyebrow: 'Available units',
  propertyDetailUnitsTitle: 'Availability',
  propertyDetailUnitsListedTemplate: '{filtered} of {total} listed',
  propertyDetailUnitsEmptyAll: 'No units are currently listed for lease.',
  propertyDetailUnitsEmptyFiltered:
    'No units match the current filters. Clear filters to see all available suites.',
  propertyDetailUnitsCountSingular: 'unit',
  propertyDetailUnitsCountPlural: 'units',
  propertyDetailUnitsFloorPlanFallback: 'Floor plan coming soon',
  propertyDetailUnitsMonthlyLeaseLabel: 'Est. monthly lease',
  propertyDetailUnitsContactLabel: 'Contact',
  propertyDetailUnitsContactFallback: 'Book a showing to connect',
  propertyDetailUnitsBookButtonLabel: 'Book a Showing',
  propertyDetailUnitsViewDetailsLabel: 'View full unit details',
  propertyDetailUnitsTableSuiteHeader: 'Suite',
  propertyDetailUnitsTableFloorHeader: 'Floor',
  propertyDetailUnitsTableSizeHeader: 'Size (SF)',
  propertyDetailUnitsTableMonthlyLeaseHeader: 'Est. monthly lease',
  propertyDetailUnitsTableRateHeader: 'Rate',
  propertyDetailUnitsTableActionHeader: 'Action',
  propertyDetailUnitsTableNoUnitsMessage: 'No units are currently listed for lease.',
  propertyDetailUnitsTableNoMatchesMessage:
    'No units match the current filters. Clear filters to see all available suites.',
  propertyDetailUnitFiltersSizeLabel: 'Size',
  propertyDetailUnitFiltersFloorLabel: 'Floor',
  propertyDetailUnitFiltersRateLabel: 'Rate',
  propertyDetailUnitFiltersSizeAllLabel: 'All sizes ({count})',
  propertyDetailUnitFiltersFloorAllLabel: 'All floors',
  propertyDetailUnitFiltersRateAllLabel: 'All rates',
  propertyDetailHighlightsEyebrow: 'Highlights',
  propertyDetailHighlightsTitle: 'Highlights',
  propertyDetailHighlightsDescription:
    'A quick snapshot based on the current listing. Confirm details during your showing.',
  propertyDetailHighlightsDivisibleLabel: 'Potentially divisible suites',
  propertyDetailHighlightsIncentivesLabel: 'Incentives may be available',
  propertyDetailHighlightsFloorPlanLabel: 'Floor plan preview available',
  propertyDetailHighlightsEmptyMessage:
    'Book a showing to review building details, fit-up options, and current availability.',
  propertyDetailLocationTitle: 'Location',
  propertyDetailLocationDirectionsLabel: 'Directions',
  propertyDetailKeyFiguresTitle: 'Key figures',
  propertyDetailKeyFiguresSubtitle: 'Snapshot',
  propertyDetailKeyFiguresTotalRentableLabel: 'Total Rentable SF',
  propertyDetailKeyFiguresUnitsLiveLabel: 'Units Live',
  propertyDetailKeyFiguresIncentivesLabel: 'Incentives',
  propertyDetailKeyFiguresDivisibleLabel: 'Divisible',
  propertyDetailLabelsRentFallback: 'Contact for rates',
  propertyDetailLabelsAvailabilityRangeFallback: 'Flexible availability',
  propertyDetailLabelsAvailabilitySingleFallback: 'Available',
  propertyDetailLabelsUnitImmediateLabel: 'Immediate',
  propertyDetailLabelsUnitNegotiableLabel: 'Negotiable',
  unitDetailNotFoundMessage: 'Unit not found or no longer published.',
  unitDetailUnavailableMessage: 'This unit is no longer available.',
  unitDetailViewPropertyLabel: 'View property listings',
  unitDetailBrowseAvailabilitiesLabel: 'Browse availabilities',
  unitDetailViewOtherUnitsLabel: 'View other units',
  unitDetailStickyEyebrow: 'Schedule a showing',
  unitDetailStickyButtonLabel: 'Book now',
  unitDetailAtAGlanceTitle: 'At a glance',
  unitDetailPropertyDetailsTitle: 'Property Details',
  unitDetailLeaseDetailsTitle: 'Lease Details',
  unitDetailPropertyDescriptionEyebrow: 'Property description',
  unitDetailPropertyDescriptionTitle: 'About this property',
  unitDetailScheduleEyebrow: 'Schedule a showing',
  unitDetailScheduleTitle: '{unitLabel}',
  unitDetailLeasingDetailsEyebrow: 'Leasing details',
  unitDetailLeasingDetailsTitle: 'Confirm the details that matter',
  unitDetailPhotoGalleryTitle: 'Photo Gallery',
  unitDetailSizeLabel: 'Size',
  unitDetailFloorLabel: 'Floor',
  unitDetailDivisibleLabel: 'Divisible',
  unitDetailStatusLabel: 'Status',
  unitDetailAvailabilityLabel: 'Availability',
  unitDetailPossessionLabel: 'Possession',
  unitDetailUpdatedLabel: 'Updated',
  unitDetailImmediateLabel: 'Immediate',
  unitDetailNegotiableLabel: 'Negotiable',
  unitDetailImmediatePossessionLabel: 'Immediate possession',
  unitDetailNegotiablePossessionLabel: 'Negotiable possession',
  unitDetailUpdatedRecentlyLabel: 'Updated recently',
  unitDetailLeasingPromptIndustrial:
    "Ask about loading access, clear height, power, parking, and any available incentives. We'll confirm what applies to this suite.",
  unitDetailLeasingPromptFlex:
    "Ask about loading access, clear height, power, parking, and any available incentives. We'll confirm what applies to this suite.",
  unitDetailLeasingPromptRetail:
    "Ask about storefront exposure, signage options, utilities, delivery access, and any available incentives. We'll confirm what applies to this suite.",
  unitDetailLeasingPromptOffice:
    "Ask about build-out options, parking, transit access, building services, and any available incentives. We'll confirm what applies to this suite.",
  unitDetailLeasingPromptFallback:
    "Ask about fit-up options, building services, parking, and any available incentives. We'll confirm what applies to this suite.",
  unitDetailPhotoGalleryEmptyMessage: 'Photos coming soon',
  showingDialogTitle: 'Schedule a showing',
  showingDialogDescription:
    'No account needed—share your contact details and pick an available weekday time. We confirm instantly and tailor the visit to your notes.',
  showingDialogStepIndicatorTemplate: 'Step {step} of {total}',
  showingDialogSelectedUnitLabel: 'Selected unit',
  showingDialogSelectedUnitDescription: 'We’ll use this context to tailor your tour.',
  showingDialogStepContactLabel: 'Contact',
  showingDialogStepScheduleLabel: 'Schedule',
  showingDialogStepReviewLabel: 'Review',
  showingDialogFieldFirstNameLabel: 'First name',
  showingDialogFieldLastNameLabel: 'Last name',
  showingDialogFieldEmailLabel: 'Work email',
  showingDialogFieldPhoneLabel: 'Phone',
  showingDialogFieldDateLabel: 'Date',
  showingDialogFieldTimeLabel: 'Time',
  showingDialogFieldMessageLabel: 'Message',
  showingDialogFieldDateHint: 'Weekdays only',
  showingDialogFieldTimeHint: 'Only available slots are shown. Weekday visits preferred.',
  showingDialogMessagePlaceholder: 'Enter questions or tour preferences (optional)',
  showingDialogReviewTitle: 'Review and confirm',
  showingDialogReviewContactLabel: 'Contact',
  showingDialogReviewSlotLabel: 'Slot',
  showingDialogReviewUnitLabel: 'Unit',
  showingDialogReviewMessageLabel: 'Message',
  showingDialogReviewConfirmationNote:
    'We’ll email a confirmation and contact you if anything changes.',
  showingDialogReviewGuidanceNext: 'We confirm your tour instantly after you submit.',
  showingDialogReviewGuidanceReview: 'Review details before finishing.',
  showingDialogButtonCancelLabel: 'Cancel',
  showingDialogButtonBackLabel: 'Back',
  showingDialogButtonNextLabel: 'Next',
  showingDialogButtonConfirmLabel: 'Confirm booking',
  showingDialogButtonDoneLabel: 'Done',
  showingDialogErrorContactMissing: 'Add your name, email, and phone to continue.',
  showingDialogErrorScheduleMissing: 'Pick an available weekday time.',
  showingDialogErrorSubmit: 'We could not submit your request. Please try again.',
  showingDialogSuccessTitle: 'Tour confirmed',
  showingDialogSuccessDescription: 'We’ve reserved your slot and generated a calendar invite.',
  showingDialogSuccessTimeLabel: 'Time',
  showingDialogSuccessEmailLabel: 'Email',
  showingDialogSuccessNameLabel: 'Name',
  showingDialogSuccessMessageLabel: 'Message',
  showingDialogSuccessCalendarLabel: 'Add to calendar (.ics)',
  showingDialogSuccessPortalQueued:
    'Portal invite will be emailed to {email}. The link expires in 14 days, and we only send one invite per email.',
  showingDialogSuccessPortalSent:
    'A portal invite has already been sent for this email. Reuse your existing link or request a new one if it expired.',
  showingDialogUnavailableTooltip:
    'This unit is not currently available. Check back soon or favorite it for updates.',
  sharedContactAddressLabel: 'Address',
  sharedContactPhoneLabel: 'Phone',
  sharedContactEmailLabel: 'Email',
  sharedContactHoursLabel: 'Hours',
  sharedContactLeadershipLabel: 'Leadership Contacts',
  sharedContactMapTitle: 'Map location',
  sharedContactMapPreviewTitle: 'Map preview',
  sharedContactMapPreviewDescription: 'Use the link below for turn-by-turn directions.',
  sharedContactMapFooter: 'View this location on the map for turn-by-turn directions.',
  sharedContactMapLinkLabel: 'Open in maps',
  sharedAvailabilityAlertsEyebrow: 'Availability Alerts',
  sharedAvailabilityAlertsTitle: 'Get updates when units change status',
  sharedAvailabilityAlertsDescription:
    'Receive an email when a unit becomes available or unavailable.',
  sharedAvailabilityAlertsEmailLabel: 'Email address',
  sharedAvailabilityAlertsEmailPlaceholder: 'you@example.com',
  sharedAvailabilityAlertsSubmitLabel: 'Subscribe',
  sharedAvailabilityAlertsSuccessMessage: "You're subscribed to availability alerts.",
  sharedAvailabilityAlertsErrorMessage: 'Unable to subscribe right now.',
  sharedAvailabilityAlertsNote:
    'Note: Email delivery is queued but not yet enabled. This will start sending once the email provider integration is configured.',
  sharedPropertyCardPhotoFallback: 'Photo coming soon',
  sharedPropertyCardRentFallback: 'Contact for rent',
  sharedPropertyCardAvailabilityFallback: 'Flexible availability',
  sharedPropertyCardDivisibleLabel: 'Divisible',
  sharedPropertyCardVacancyLabel: 'Vacancy',
  sharedPropertyCardVacancyAvailableLabel: 'Available',
  sharedPropertyCardVacancyLeasedLabel: 'Leased',
  sharedPropertyCardSizeLabel: 'Size',
  sharedPropertyCardUnitCountZeroLabel: 'No published units',
  sharedPropertyCardUnitCountSingularLabel: '1 unit live',
  sharedPropertyCardUnitCountPluralLabel: '{count} units live',
  sharedUnitCardPhotoFallback: 'Photo coming soon',
  sharedUnitCardAvailabilityLabel: 'Availability',
  sharedUnitCardAvailabilityFallback: 'Available now',
  sharedUnitCardFloorLabel: 'Floor',
  sharedUnitCardViewDetailsLabel: 'View details',
  sharedUnitCardDivisibleLabel: 'Divisible',
  sharedEmptyStatePropertyGrid:
    'New listings are on their way. Check back soon for available properties.',
  sharedEmptyStateUnitGroup:
    'No published units are currently available. Check back soon for new listings.',
};

const CORE_SETTINGS_NAV_GROUPS: Array<{
  label: string;
  items: Array<{ id: string; label: string }>;
}> = [
  {
    label: 'Core',
    items: [
      { id: 'settings-brand', label: 'Brand' },
      { id: 'settings-seo', label: 'SEO defaults' },
      { id: 'settings-navigation', label: 'Navigation' },
      { id: 'settings-footer', label: 'Footer' },
      { id: 'settings-contact', label: 'Booking & Contact' },
    ],
  },
];

const PAGE_SETTINGS_ITEMS = [
  {
    id: 'home',
    label: 'Home page',
    sectionId: 'settings-home',
    description: 'Homepage hero, availability callouts, and contact section copy.',
    previewPath: '/',
    previewHint: 'Open the public homepage.',
    keywords: ['homepage', 'hero', 'availability', 'contact'],
  },
  {
    id: 'lead',
    label: 'Lead capture',
    sectionId: 'settings-lead',
    description: 'Campaign landing copy, lead form prompts, and thank-you messaging.',
    previewPath: '/campaigns/lead',
    previewHint: 'Open the lead capture landing page.',
    keywords: ['campaign', 'lead', 'thank you'],
  },
  {
    id: 'about',
    label: 'About page',
    sectionId: 'settings-about',
    description: 'Company story, values, and portfolio highlights.',
    previewPath: '/about',
    previewHint: 'Open the public About page.',
    keywords: ['about', 'values', 'portfolio'],
  },
  {
    id: 'contact',
    label: 'Contact page',
    sectionId: 'settings-contact-page',
    description: 'Contact hero, support details, and form messaging.',
    previewPath: '/contact',
    previewHint: 'Open the public Contact page.',
    keywords: ['contact', 'support', 'form'],
  },
  {
    id: 'availabilities',
    label: 'Availabilities',
    sectionId: 'settings-availabilities',
    description: 'Listings page headings, filters, and empty state copy.',
    previewPath: '/availabilities',
    previewHint: 'Open the public Availabilities page.',
    keywords: ['listings', 'filters', 'availability'],
  },
  {
    id: 'property-detail',
    label: 'Property detail',
    sectionId: 'settings-property-detail',
    description: 'Property detail headings, labels, and highlight copy.',
    previewPath: '/availabilities',
    previewHint: 'Open a property from Availabilities to see this copy.',
    keywords: ['property', 'detail', 'highlights'],
  },
  {
    id: 'unit-detail',
    label: 'Unit detail',
    sectionId: 'settings-unit-detail',
    description: 'Unit detail headings, labels, and leasing prompts.',
    previewPath: '/availabilities',
    previewHint: 'Open a unit from a property detail page to see this copy.',
    keywords: ['unit', 'leasing', 'details'],
  },
  {
    id: 'showing',
    label: 'Showing dialog',
    sectionId: 'settings-showing',
    description: 'Showing request dialog steps, field labels, and confirmations.',
    previewPath: '/availabilities',
    previewHint: 'Start a showing request from a unit to preview the dialog.',
    keywords: ['showing', 'dialog', 'request'],
  },
  {
    id: 'shared',
    label: 'Shared labels',
    sectionId: 'settings-shared',
    description: 'Reusable labels and empty states across the public site.',
    previewPath: '/availabilities',
    previewHint: 'Review cards and empty states on Availabilities.',
    keywords: ['labels', 'empty states', 'shared'],
  },
] as const;

type PageSettingsId = (typeof PAGE_SETTINGS_ITEMS)[number]['id'];
type PageSettingsItem = (typeof PAGE_SETTINGS_ITEMS)[number];

const PAGE_SETTINGS_NAV_GROUPS: Array<{ label: string; itemIds: PageSettingsId[] }> = [
  { label: 'Marketing pages', itemIds: ['home', 'lead', 'about', 'contact'] },
  { label: 'Listings', itemIds: ['availabilities', 'property-detail', 'unit-detail'] },
  { label: 'Showing', itemIds: ['showing'] },
  { label: 'Shared', itemIds: ['shared'] },
];
const FALLBACK_PAGE = PAGE_SETTINGS_ITEMS[0];

const resolvePageSettingsId = (value: string | undefined): PageSettingsId | null => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }
  const match = PAGE_SETTINGS_ITEMS.find((item) => item.id === value);
  return match ? match.id : null;
};

function SettingsNav(): JSX.Element {
  return (
    <nav
      aria-label="Settings navigation"
      className="rounded-2xl border border-surface-subtle bg-white p-5 shadow-card"
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {CORE_SETTINGS_NAV_GROUPS.map((group) => (
          <div key={group.label} className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
              {group.label}
            </p>
            <div className="flex flex-wrap gap-2">
              {group.items.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="rounded-full border border-surface-subtle bg-surface-light px-3 py-1.5 text-xs font-semibold text-brand-primary transition hover:border-brand-primary/40 hover:bg-brand-primary/5"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </nav>
  );
}

function PageSettingsNav(): JSX.Element {
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();
  const filteredItems = useMemo<PageSettingsItem[]>(() => {
    if (normalizedQuery.length === 0) {
      return PAGE_SETTINGS_ITEMS;
    }
    return PAGE_SETTINGS_ITEMS.filter((item) => {
      const keywords = item.keywords.join(' ');
      const haystack = `${item.label} ${item.description} ${keywords}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [normalizedQuery]);
  const filteredIds = useMemo(() => new Set(filteredItems.map((item) => item.id)), [filteredItems]);
  const groups = useMemo(
    () =>
      PAGE_SETTINGS_NAV_GROUPS.map((group) => ({
        label: group.label,
        itemIds: group.itemIds.filter((itemId) => filteredIds.has(itemId)),
      })).filter((group) => group.itemIds.length > 0),
    [filteredIds],
  );

  return (
    <nav
      aria-label="Page settings navigation"
      className="rounded-2xl border border-surface-subtle bg-white p-5 shadow-card"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            Page Settings
          </p>
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              placeholder="Search pages or sections"
              aria-label="Search page settings"
              className="w-full rounded-xl border border-surface-subtle px-3 py-2.5 text-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:max-w-sm md:py-2"
            />
            {query.trim().length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                }}
                className="inline-flex items-center justify-center rounded-full border border-surface-subtle bg-surface-light px-3 py-1.5 text-xs font-semibold text-text-primary/70 transition hover:border-brand-primary/40 hover:bg-brand-primary/5"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        {groups.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {groups.map((group) => (
              <div key={group.label} className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  {group.label}
                </p>
                <div className="grid gap-2">
                  {group.itemIds.map((itemId) => {
                    const item = PAGE_SETTINGS_ITEMS.find((entry) => entry.id === itemId);
                    if (!item) {
                      return null;
                    }
                    return (
                      <NavLink
                        key={item.id}
                        to={`/dashboard/page-settings/${item.id}`}
                        className={({ isActive }) =>
                          clsx(
                            'flex flex-col gap-1 rounded-xl border px-3 py-2 text-left transition',
                            isActive
                              ? 'border-brand-primary bg-brand-primary/10 text-brand-primary shadow-sm'
                              : 'border-surface-subtle bg-surface-light text-text-primary hover:border-brand-primary/40 hover:bg-brand-primary/5',
                          )
                        }
                      >
                        <span className="text-sm font-semibold">{item.label}</span>
                        <span className="text-xs text-text-primary/70">{item.description}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-primary/60">
            No matches. Try a different keyword or clear the search.
          </p>
        )}
      </div>
    </nav>
  );
}

type SettingsViewMode = 'core' | 'page';

function SiteSettingsView({
  mode,
  activePageId,
}: {
  mode: SettingsViewMode;
  activePageId?: PageSettingsId;
}): JSX.Element {
  const user = useDashboardUser();
  const isPageSettings = mode === 'page';
  const activePage = useMemo(() => {
    if (!isPageSettings) {
      return null;
    }
    return PAGE_SETTINGS_ITEMS.find((item) => item.id === activePageId) ?? FALLBACK_PAGE;
  }, [activePageId, isPageSettings]);
  const pageSettingsLink = `/dashboard/page-settings/${(activePage ?? FALLBACK_PAGE).id}`;
  const showCoreSettings = mode === 'core';
  const showPage = (id: PageSettingsId) => isPageSettings && activePage?.id === id;
  const headingKicker = isPageSettings ? 'Page Settings' : 'Site Settings';
  const headingTitle = isPageSettings
    ? `${activePage?.label ?? 'Page'} settings`
    : 'CMS & Booking controls';
  const headingDescription = isPageSettings
    ? (activePage?.description ??
      'Update page-specific copy, labels, and UI content for the selected page.')
    : 'Update booking contact details and public-facing copy. Changes apply to the marketing site immediately.';
  const previewPath = activePage?.previewPath ?? null;
  const previewHint = activePage?.previewHint ?? null;
  const settingsQuery = useSiteSettingsDetail();
  const siteSettings = settingsQuery.data ?? null;
  const propertiesQuery = useAllPublicPropertyList({ pageSize: 60, vacancyOnly: false });
  const portfolioProperties = useMemo(() => {
    const items = propertiesQuery.data?.items ?? [];
    return [...items].sort((a, b) => {
      const aLabel = formatPropertyAddress(a.address);
      const bLabel = formatPropertyAddress(b.address);
      return aLabel.localeCompare(bLabel, 'en-CA', { sensitivity: 'base' });
    });
  }, [propertiesQuery.data?.items]);
  const propertiesAreLoading =
    portfolioProperties.length === 0 && (propertiesQuery.isLoading || propertiesQuery.isFetching);
  const propertiesFetchFailed = Boolean(propertiesQuery.error);
  const propertyOptions = useMemo<NavigationPropertyOption[]>(
    () =>
      portfolioProperties.map((property) => {
        const addressLabel = formatPropertyAddress(property.address);
        const submarket = property.submarket.trim();
        const label = submarket.length > 0 ? `${addressLabel} (${submarket})` : addressLabel;
        return { id: property.id, label };
      }),
    [portfolioProperties],
  );
  const hasSettings = siteSettings !== null;
  const siteSettingsVersion =
    siteSettings === null ? null : `${siteSettings.id}:${siteSettings.updatedAt ?? ''}`;
  const lastAppliedSettingsVersionRef = useRef<string | null>(null);
  const baselineRef = useRef<SiteSettingsFormValues>(EMPTY_FORM);
  const hasSeededPropertiesRef = useRef(false);
  const [heroMediaUploadError, setHeroMediaUploadError] = useState<string | null>(null);
  const [isHeroMediaUploading, setIsHeroMediaUploading] = useState(false);
  const [heroMediaUploadProgress, setHeroMediaUploadProgress] = useState<number | null>(null);
  const heroMediaMaxSizeGb = useMemo(
    () => Math.round(SITE_SETTINGS_MEDIA_MAX_BYTES / (1024 * 1024 * 1024)),
    [],
  );
  const heroMediaAcceptTypes = useMemo(() => SITE_SETTINGS_MEDIA_ALLOWED_TYPES.join(','), []);
  const updateMutation = siteSettingsResource.useMutation('update', {
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Unable to update settings.';
      toast.error(message);
    },
  });

  const form = useFormController<SiteSettingsFormValues>({
    initialValues: EMPTY_FORM,
    schema: formSchema,
    onSubmit: async (values) => {
      const payload = mapFormToUpdateInput(values);
      const updated = await updateMutation.mutateAsync({ user, values: payload });
      lastAppliedSettingsVersionRef.current = `${updated.id}:${updated.updatedAt ?? ''}`;
      const nextValues = mapSettingsToForm(updated);
      baselineRef.current = nextValues;
      form.reset(nextValues);
      toast.success('Site settings updated.');
    },
  });
  const { isDirty, isFieldDirty, values, submitSuccess, submitError, isSubmitting } = form;
  const heroMediaUrl = form.values.homeHeroMediaUrl.trim();
  const heroMediaType = form.values.homeHeroMediaType;
  const resolveHeroMediaTypeFromFile = (file: File): SiteSettingsFormValues['homeHeroMediaType'] =>
    file.type.startsWith('video/') ? 'video' : 'image';
  const resolveHeroMediaTypeFromUrl = (
    url: string,
  ): SiteSettingsFormValues['homeHeroMediaType'] | null => {
    if (url.length === 0) {
      return null;
    }
    const clean = url.split('?')[0].split('#')[0].toLowerCase();
    if (clean.endsWith('.mp4') || clean.endsWith('.webm') || clean.endsWith('.mov')) {
      return 'video';
    }
    if (
      clean.endsWith('.jpg') ||
      clean.endsWith('.jpeg') ||
      clean.endsWith('.png') ||
      clean.endsWith('.webp') ||
      clean.endsWith('.heic') ||
      clean.endsWith('.heif')
    ) {
      return 'image';
    }
    return null;
  };
  const heroMediaPreviewType = resolveHeroMediaTypeFromUrl(heroMediaUrl) ?? heroMediaType;
  const heroMediaPreviewLabel = heroMediaPreviewType === 'video' ? 'Video' : 'Photo';
  const heroMediaProgressValue =
    heroMediaUploadProgress === null ? 0 : Math.min(100, Math.max(0, heroMediaUploadProgress));
  const shouldShowHeroMediaProgress = heroMediaUploadProgress !== null;
  const canInteractWithHeroMedia = !isHeroMediaUploading;

  const handleHeroMediaFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files ?? []).slice(0, 1);
    if (files.length === 0) {
      return;
    }

    setHeroMediaUploadError(null);
    setHeroMediaUploadProgress(null);

    const [file] = files;
    if (!isAllowedSiteSettingsMediaType(file)) {
      setHeroMediaUploadError(`Unsupported file type: ${file.type || file.name}.`);
      event.target.value = '';
      return;
    }

    if (!isWithinSiteSettingsMediaSizeLimit(file)) {
      setHeroMediaUploadError(
        `Files must be under ${heroMediaMaxSizeGb.toString()}GB (Supabase limit).`,
      );
      event.target.value = '';
      return;
    }

    setIsHeroMediaUploading(true);

    try {
      if (heroMediaUrl.length > 0) {
        const parsed = parsePropertyMediaUrl(heroMediaUrl);
        if (parsed) {
          await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
        }
      }

      setHeroMediaUploadProgress(0);
      const result = await uploadSiteSettingsMediaFile({
        file,
        onProgress: setHeroMediaUploadProgress,
      });
      if (typeof result.url === 'string' && result.url.length > 0) {
        const nextType = resolveHeroMediaTypeFromFile(file);
        form.setValue('homeHeroMediaUrl', result.url);
        form.setValue('homeHeroMediaType', nextType);
        toast.success(`Uploaded hero ${nextType === 'video' ? 'video' : 'photo'}.`);
      }
    } catch (uploadError) {
      const message =
        uploadError instanceof Error ? uploadError.message : 'Upload failed. Please try again.';
      setHeroMediaUploadError(message);
      toast.error(message);
    } finally {
      setIsHeroMediaUploading(false);
      setHeroMediaUploadProgress(null);
      event.target.value = '';
    }
  };

  const handleHeroMediaRemove = async (): Promise<void> => {
    if (heroMediaUrl.length === 0) {
      return;
    }

    setHeroMediaUploadError(null);
    setHeroMediaUploadProgress(null);
    setIsHeroMediaUploading(true);
    let removed = false;

    try {
      const parsed = parsePropertyMediaUrl(heroMediaUrl);
      if (parsed) {
        await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
      }
      removed = true;
    } catch (removeError) {
      const message =
        removeError instanceof Error ? removeError.message : 'Unable to remove media from storage.';
      setHeroMediaUploadError(message);
      toast.error(message);
    } finally {
      if (removed) {
        form.setValue('homeHeroMediaUrl', '');
        form.setValue('homeHeroMediaType', 'video');
        toast.success('Removed hero media.');
      }
      setIsHeroMediaUploading(false);
    }
  };
  const resetForm = form.reset;
  const navigationLinks = siteSettings?.navigation.links ?? [];
  const hasPropertiesNavItem = navigationLinks.some((entry) =>
    entry.label.trim().toLowerCase().includes('properties'),
  );
  const hasPropertyChildren = navigationLinks.some((entry) =>
    (entry.children ?? []).some((child) => (child.propertyId ?? '').trim().length > 0),
  );
  const shouldSeedProperties =
    !hasSeededPropertiesRef.current &&
    portfolioProperties.length > 0 &&
    !hasPropertyChildren &&
    !hasPropertiesNavItem;

  useEffect(() => {
    if (siteSettings === null || siteSettingsVersion === null) {
      return;
    }

    if (lastAppliedSettingsVersionRef.current === siteSettingsVersion && !shouldSeedProperties) {
      return;
    }

    if (isDirty) {
      return;
    }

    lastAppliedSettingsVersionRef.current = siteSettingsVersion;
    if (shouldSeedProperties) {
      hasSeededPropertiesRef.current = true;
    }
    const nextValues = mapSettingsToForm(siteSettings, {
      seededProperties: shouldSeedProperties ? portfolioProperties : [],
    });
    baselineRef.current = nextValues;
    resetForm(nextValues);
  }, [
    isDirty,
    portfolioProperties,
    resetForm,
    siteSettingsVersion,
    shouldSeedProperties,
    siteSettings,
  ]);

  const showLoading = settingsQuery.isLoading && !hasSettings;
  const error = settingsQuery.error ?? null;
  const updatedAtValue = siteSettings?.updatedAt;
  const hasSubmitError = typeof submitError === 'string' && submitError.length > 0;
  const lastUpdated = useMemo(() => {
    if (updatedAtValue === null || updatedAtValue === undefined) {
      return null;
    }
    try {
      return new Date(updatedAtValue).toLocaleString();
    } catch {
      return updatedAtValue;
    }
  }, [updatedAtValue]);
  const dirtyFieldCount = useMemo(() => {
    if (!isDirty) {
      return 0;
    }
    return (Object.keys(values) as Array<keyof SiteSettingsFormValues>).reduce(
      (total, key) => total + (isFieldDirty(key) ? 1 : 0),
      0,
    );
  }, [isDirty, isFieldDirty, values]);
  const showActionBar = isDirty || submitSuccess || hasSubmitError;
  const canSubmit = isDirty && !isSubmitting && !updateMutation.isPending && !isHeroMediaUploading;
  const actionMessage = isDirty
    ? `${String(dirtyFieldCount)} field${dirtyFieldCount === 1 ? '' : 's'} updated`
    : submitSuccess
      ? 'All changes saved'
      : hasSubmitError
        ? 'Fix the highlighted fields to save'
        : '';
  const handleDiscard = () => {
    form.reset(baselineRef.current);
  };

  if (showLoading) {
    return (
      <section className="grid gap-lg py-10">
        <Skeleton className="h-10 w-64 rounded-xl" />
        <Skeleton className="h-[420px] rounded-2xl" />
      </section>
    );
  }

  if (error) {
    return (
      <ErrorState
        title="Unable to load site settings"
        description={error instanceof Error ? error.message : 'An unexpected error occurred.'}
      />
    );
  }

  return (
    <section className="flex flex-col gap-lg py-10">
      <header className="flex flex-col gap-xs">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
          {headingKicker}
        </p>
        <h1 className="text-3xl font-semibold text-brand-primary">{headingTitle}</h1>
        <p className="text-sm text-text-primary/70">{headingDescription}</p>
        <div className="flex flex-wrap items-center gap-2">
          <NavLink
            to="/dashboard/site-settings"
            className={({ isActive }) =>
              clsx(
                'rounded-full border px-3 py-1.5 text-xs font-semibold transition',
                isActive
                  ? 'border-brand-primary bg-brand-primary text-white shadow-sm'
                  : 'border-surface-subtle bg-surface-light text-brand-primary hover:border-brand-primary/40 hover:bg-brand-primary/5',
              )
            }
          >
            Sitewide settings
          </NavLink>
          <NavLink
            to={pageSettingsLink}
            className={({ isActive }) =>
              clsx(
                'rounded-full border px-3 py-1.5 text-xs font-semibold transition',
                isActive
                  ? 'border-brand-primary bg-brand-primary text-white shadow-sm'
                  : 'border-surface-subtle bg-surface-light text-brand-primary hover:border-brand-primary/40 hover:bg-brand-primary/5',
              )
            }
          >
            Page copy
          </NavLink>
        </div>
        {isPageSettings && previewPath ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-text-primary/60">
            <a
              href={previewPath}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-brand-primary transition hover:underline"
            >
              Open page preview
            </a>
            {previewHint ? <span>{previewHint}</span> : null}
          </div>
        ) : null}
        {lastUpdated !== null ? (
          <p className="text-xs text-text-primary/60">Last updated {lastUpdated}</p>
        ) : null}
      </header>

      <Form
        className="flex flex-col gap-lg"
        onSubmit={(event) => {
          void form.handleSubmit(event);
        }}
      >
        {isPageSettings && activePage ? <PageSettingsNav /> : <SettingsNav />}

        {showCoreSettings ? (
          <SettingsCard
            title="Brand"
            id="settings-brand"
            description="Site name, tagline, and default imagery used across the public site."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="brand-site-name"
                label="Site name"
                required
                error={form.errors.brandSiteName}
              >
                <input
                  type="text"
                  value={form.values.brandSiteName}
                  onChange={(event) => {
                    form.setValue('brandSiteName', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('brandSiteName').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <FormField
                id="brand-tagline"
                label="Tagline"
                required
                error={form.errors.brandTagline}
              >
                <input
                  type="text"
                  value={form.values.brandTagline}
                  onChange={(event) => {
                    form.setValue('brandTagline', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('brandTagline').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="brand-logo-url"
                label="Logo URL"
                error={form.errors.brandLogoUrl}
                description="Optional. Use a full URL or a path starting with /."
              >
                <input
                  type="text"
                  value={form.values.brandLogoUrl}
                  onChange={(event) => {
                    form.setValue('brandLogoUrl', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('brandLogoUrl').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <FormField
                id="brand-favicon-url"
                label="Favicon URL"
                error={form.errors.brandFaviconUrl}
                description="Optional. Used to update the browser tab icon."
              >
                <input
                  type="text"
                  value={form.values.brandFaviconUrl}
                  onChange={(event) => {
                    form.setValue('brandFaviconUrl', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('brandFaviconUrl').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>

            <FormField
              id="brand-default-og-image"
              label="Default social image"
              error={form.errors.brandDefaultOgImageUrl}
              description="Optional. Used as the default OpenGraph/Twitter share image."
            >
              <input
                type="text"
                value={form.values.brandDefaultOgImageUrl}
                onChange={(event) => {
                  form.setValue('brandDefaultOgImageUrl', event.target.value);
                }}
                onBlur={() => {
                  form.field('brandDefaultOgImageUrl').onBlur();
                }}
                className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
              />
            </FormField>
          </SettingsCard>
        ) : null}

        {showPage('contact') ? (
          <SettingsCard
            title="Contact page"
            id="settings-contact-page"
            description="Contact hero, support details, and form messaging."
          >
            <SettingsGroup title="SEO">
              <FormField
                id="contact-page-seo-title"
                label="SEO title"
                required
                error={form.errors.contactPageSeoTitle}
              >
                <input
                  type="text"
                  value={form.values.contactPageSeoTitle}
                  onChange={(event) => {
                    form.setValue('contactPageSeoTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageSeoTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-seo-description"
                label="SEO description"
                required
                error={form.errors.contactPageSeoDescription}
              >
                <textarea
                  value={form.values.contactPageSeoDescription}
                  onChange={(event) => {
                    form.setValue('contactPageSeoDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageSeoDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Hero">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="contact-page-hero-eyebrow"
                  label="Hero eyebrow"
                  required
                  error={form.errors.contactPageHeroEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.contactPageHeroEyebrow}
                    onChange={(event) => {
                      form.setValue('contactPageHeroEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageHeroEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-hero-title"
                  label="Hero title"
                  required
                  error={form.errors.contactPageHeroTitle}
                >
                  <input
                    type="text"
                    value={form.values.contactPageHeroTitle}
                    onChange={(event) => {
                      form.setValue('contactPageHeroTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageHeroTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="contact-page-hero-description"
                label="Hero description"
                required
                error={form.errors.contactPageHeroDescription}
              >
                <textarea
                  value={form.values.contactPageHeroDescription}
                  onChange={(event) => {
                    form.setValue('contactPageHeroDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageHeroDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-hero-response-time"
                label="Hero response time"
                required
                error={form.errors.contactPageHeroResponseTime}
              >
                <input
                  type="text"
                  value={form.values.contactPageHeroResponseTime}
                  onChange={(event) => {
                    form.setValue('contactPageHeroResponseTime', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageHeroResponseTime').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Hero card">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="contact-page-hero-card-eyebrow"
                  label="Hero card eyebrow"
                  required
                  error={form.errors.contactPageHeroCardEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.contactPageHeroCardEyebrow}
                    onChange={(event) => {
                      form.setValue('contactPageHeroCardEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageHeroCardEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-hero-card-title"
                  label="Hero card title"
                  required
                  error={form.errors.contactPageHeroCardTitle}
                >
                  <input
                    type="text"
                    value={form.values.contactPageHeroCardTitle}
                    onChange={(event) => {
                      form.setValue('contactPageHeroCardTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageHeroCardTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="contact-page-hero-card-description"
                label="Hero card description"
                required
                error={form.errors.contactPageHeroCardDescription}
              >
                <textarea
                  value={form.values.contactPageHeroCardDescription}
                  onChange={(event) => {
                    form.setValue('contactPageHeroCardDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageHeroCardDescription').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  id="contact-page-hero-card-call-label"
                  label="Call label"
                  required
                  error={form.errors.contactPageHeroCardCallLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageHeroCardCallLabel}
                    onChange={(event) => {
                      form.setValue('contactPageHeroCardCallLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageHeroCardCallLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-hero-card-email-label"
                  label="Email label"
                  required
                  error={form.errors.contactPageHeroCardEmailLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageHeroCardEmailLabel}
                    onChange={(event) => {
                      form.setValue('contactPageHeroCardEmailLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageHeroCardEmailLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-hero-card-map-label"
                  label="Map label"
                  required
                  error={form.errors.contactPageHeroCardMapLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageHeroCardMapLabel}
                    onChange={(event) => {
                      form.setValue('contactPageHeroCardMapLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageHeroCardMapLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Support section">
              <FormField
                id="contact-page-support-title"
                label="Support title"
                required
                error={form.errors.contactPageSupportTitle}
              >
                <input
                  type="text"
                  value={form.values.contactPageSupportTitle}
                  onChange={(event) => {
                    form.setValue('contactPageSupportTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageSupportTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-support-description"
                label="Support description"
                required
                error={form.errors.contactPageSupportDescription}
              >
                <textarea
                  value={form.values.contactPageSupportDescription}
                  onChange={(event) => {
                    form.setValue('contactPageSupportDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageSupportDescription').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <StringListFieldGroup
                title="Support highlights"
                description="Short bullet points shown next to the form."
                items={form.values.contactPageSupportHighlights}
                error={form.errors.contactPageSupportHighlights}
                onChange={(next) => {
                  form.setValue('contactPageSupportHighlights', next);
                }}
                addLabel="Add highlight"
              />
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="contact-page-support-response-time-label"
                  label="Response time label"
                  required
                  error={form.errors.contactPageSupportResponseTimeLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageSupportResponseTimeLabel}
                    onChange={(event) => {
                      form.setValue('contactPageSupportResponseTimeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageSupportResponseTimeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-support-response-time-value"
                  label="Response time value"
                  required
                  error={form.errors.contactPageSupportResponseTimeValue}
                >
                  <input
                    type="text"
                    value={form.values.contactPageSupportResponseTimeValue}
                    onChange={(event) => {
                      form.setValue('contactPageSupportResponseTimeValue', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageSupportResponseTimeValue').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-support-directions-label"
                  label="Directions label"
                  required
                  error={form.errors.contactPageSupportDirectionsLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageSupportDirectionsLabel}
                    onChange={(event) => {
                      form.setValue('contactPageSupportDirectionsLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageSupportDirectionsLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-support-availabilities-label"
                  label="Availabilities label"
                  required
                  error={form.errors.contactPageSupportAvailabilitiesLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageSupportAvailabilitiesLabel}
                    onChange={(event) => {
                      form.setValue('contactPageSupportAvailabilitiesLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageSupportAvailabilitiesLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Contact details card">
              <FormField
                id="contact-page-details-title"
                label="Details title"
                required
                error={form.errors.contactPageDetailsTitle}
              >
                <input
                  type="text"
                  value={form.values.contactPageDetailsTitle}
                  onChange={(event) => {
                    form.setValue('contactPageDetailsTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageDetailsTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-details-description"
                label="Details description"
                required
                error={form.errors.contactPageDetailsDescription}
              >
                <textarea
                  value={form.values.contactPageDetailsDescription}
                  onChange={(event) => {
                    form.setValue('contactPageDetailsDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageDetailsDescription').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Form">
              <FormField
                id="contact-page-form-title"
                label="Form title"
                required
                error={form.errors.contactPageFormTitle}
              >
                <input
                  type="text"
                  value={form.values.contactPageFormTitle}
                  onChange={(event) => {
                    form.setValue('contactPageFormTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageFormTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-form-description"
                label="Form description"
                required
                error={form.errors.contactPageFormDescription}
              >
                <textarea
                  value={form.values.contactPageFormDescription}
                  onChange={(event) => {
                    form.setValue('contactPageFormDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageFormDescription').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-form-success-message"
                label="Success message"
                required
                error={form.errors.contactPageFormSuccessMessage}
                description="Use {name} for the contact name."
              >
                <textarea
                  value={form.values.contactPageFormSuccessMessage}
                  onChange={(event) => {
                    form.setValue('contactPageFormSuccessMessage', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageFormSuccessMessage').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-form-error-message"
                label="Error message"
                required
                error={form.errors.contactPageFormErrorMessage}
              >
                <input
                  type="text"
                  value={form.values.contactPageFormErrorMessage}
                  onChange={(event) => {
                    form.setValue('contactPageFormErrorMessage', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageFormErrorMessage').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-form-helper-text"
                label="Helper text"
                required
                error={form.errors.contactPageFormHelperText}
                description="Use {phone} for the contact phone number."
              >
                <input
                  type="text"
                  value={form.values.contactPageFormHelperText}
                  onChange={(event) => {
                    form.setValue('contactPageFormHelperText', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageFormHelperText').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-page-form-submit-label"
                label="Submit label"
                required
                error={form.errors.contactPageFormSubmitLabel}
              >
                <input
                  type="text"
                  value={form.values.contactPageFormSubmitLabel}
                  onChange={(event) => {
                    form.setValue('contactPageFormSubmitLabel', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPageFormSubmitLabel').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Form fields">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="contact-page-form-full-name-label"
                  label="Full name label"
                  required
                  error={form.errors.contactPageFormFullNameLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormFullNameLabel}
                    onChange={(event) => {
                      form.setValue('contactPageFormFullNameLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormFullNameLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-full-name-placeholder"
                  label="Full name placeholder"
                  required
                  error={form.errors.contactPageFormFullNamePlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormFullNamePlaceholder}
                    onChange={(event) => {
                      form.setValue('contactPageFormFullNamePlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormFullNamePlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-phone-label"
                  label="Phone label"
                  required
                  error={form.errors.contactPageFormPhoneLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormPhoneLabel}
                    onChange={(event) => {
                      form.setValue('contactPageFormPhoneLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormPhoneLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-phone-placeholder"
                  label="Phone placeholder"
                  required
                  error={form.errors.contactPageFormPhonePlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormPhonePlaceholder}
                    onChange={(event) => {
                      form.setValue('contactPageFormPhonePlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormPhonePlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-email-label"
                  label="Email label"
                  required
                  error={form.errors.contactPageFormEmailLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormEmailLabel}
                    onChange={(event) => {
                      form.setValue('contactPageFormEmailLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormEmailLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-email-placeholder"
                  label="Email placeholder"
                  required
                  error={form.errors.contactPageFormEmailPlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormEmailPlaceholder}
                    onChange={(event) => {
                      form.setValue('contactPageFormEmailPlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormEmailPlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-message-label"
                  label="Message label"
                  required
                  error={form.errors.contactPageFormMessageLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormMessageLabel}
                    onChange={(event) => {
                      form.setValue('contactPageFormMessageLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormMessageLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-message-placeholder"
                  label="Message placeholder"
                  required
                  error={form.errors.contactPageFormMessagePlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormMessagePlaceholder}
                    onChange={(event) => {
                      form.setValue('contactPageFormMessagePlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormMessagePlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-space-type-label"
                  label="Space type label"
                  required
                  error={form.errors.contactPageFormSpaceTypeLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormSpaceTypeLabel}
                    onChange={(event) => {
                      form.setValue('contactPageFormSpaceTypeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormSpaceTypeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-space-type-placeholder"
                  label="Space type placeholder"
                  required
                  error={form.errors.contactPageFormSpaceTypePlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormSpaceTypePlaceholder}
                    onChange={(event) => {
                      form.setValue('contactPageFormSpaceTypePlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormSpaceTypePlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <StringListFieldGroup
                title="Space type options"
                description="Select options displayed in the space type dropdown."
                items={form.values.contactPageFormSpaceTypeOptions}
                error={form.errors.contactPageFormSpaceTypeOptions}
                onChange={(next) => {
                  form.setValue('contactPageFormSpaceTypeOptions', next);
                }}
                addLabel="Add option"
              />
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="contact-page-form-target-size-label"
                  label="Target size label"
                  required
                  error={form.errors.contactPageFormTargetSizeLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormTargetSizeLabel}
                    onChange={(event) => {
                      form.setValue('contactPageFormTargetSizeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormTargetSizeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-target-size-placeholder"
                  label="Target size placeholder"
                  required
                  error={form.errors.contactPageFormTargetSizePlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormTargetSizePlaceholder}
                    onChange={(event) => {
                      form.setValue('contactPageFormTargetSizePlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormTargetSizePlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-timeline-label"
                  label="Timeline label"
                  required
                  error={form.errors.contactPageFormTimelineLabel}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormTimelineLabel}
                    onChange={(event) => {
                      form.setValue('contactPageFormTimelineLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormTimelineLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="contact-page-form-timeline-placeholder"
                  label="Timeline placeholder"
                  required
                  error={form.errors.contactPageFormTimelinePlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.contactPageFormTimelinePlaceholder}
                    onChange={(event) => {
                      form.setValue('contactPageFormTimelinePlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('contactPageFormTimelinePlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <StringListFieldGroup
                title="Timeline options"
                description="Select options displayed in the timeline dropdown."
                items={form.values.contactPageFormTimelineOptions}
                error={form.errors.contactPageFormTimelineOptions}
                onChange={(next) => {
                  form.setValue('contactPageFormTimelineOptions', next);
                }}
                addLabel="Add option"
              />
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {showPage('availabilities') ? (
          <SettingsCard
            title="Availabilities"
            id="settings-availabilities"
            description="Listings page headings, filters, and empty state copy."
          >
            <SettingsGroup title="SEO">
              <FormField
                id="availabilities-seo-title"
                label="SEO title"
                required
                error={form.errors.availabilitiesSeoTitle}
              >
                <input
                  type="text"
                  value={form.values.availabilitiesSeoTitle}
                  onChange={(event) => {
                    form.setValue('availabilitiesSeoTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('availabilitiesSeoTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="availabilities-seo-description"
                label="SEO description"
                required
                error={form.errors.availabilitiesSeoDescription}
              >
                <textarea
                  value={form.values.availabilitiesSeoDescription}
                  onChange={(event) => {
                    form.setValue('availabilitiesSeoDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('availabilitiesSeoDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Heading">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="availabilities-heading-eyebrow"
                  label="Heading eyebrow"
                  required
                  error={form.errors.availabilitiesHeadingEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesHeadingEyebrow}
                    onChange={(event) => {
                      form.setValue('availabilitiesHeadingEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesHeadingEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-heading-title"
                  label="Heading title"
                  required
                  error={form.errors.availabilitiesHeadingTitle}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesHeadingTitle}
                    onChange={(event) => {
                      form.setValue('availabilitiesHeadingTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesHeadingTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="availabilities-heading-description"
                label="Heading description"
                required
                error={form.errors.availabilitiesHeadingDescription}
              >
                <textarea
                  value={form.values.availabilitiesHeadingDescription}
                  onChange={(event) => {
                    form.setValue('availabilitiesHeadingDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('availabilitiesHeadingDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Filters panel">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="availabilities-filters-title"
                  label="Filters title"
                  required
                  error={form.errors.availabilitiesFiltersTitle}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersTitle}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-subtitle"
                  label="Filters subtitle"
                  required
                  error={form.errors.availabilitiesFiltersSubtitle}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersSubtitle}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersSubtitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersSubtitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-empty-label"
                  label="Empty filters label"
                  required
                  error={form.errors.availabilitiesFiltersEmptyLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersEmptyLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersEmptyLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersEmptyLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-active-template"
                  label="Active filters template"
                  required
                  error={form.errors.availabilitiesFiltersActiveTemplate}
                  description="Use {count} for the active filter count."
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersActiveTemplate}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersActiveTemplate', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersActiveTemplate').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-reset-label"
                  label="Filters reset label"
                  required
                  error={form.errors.availabilitiesFiltersResetLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersResetLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersResetLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersResetLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-open-label"
                  label="Filters open label"
                  required
                  error={form.errors.availabilitiesFiltersOpenLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersOpenLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersOpenLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersOpenLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-drawer-title"
                  label="Drawer title"
                  required
                  error={form.errors.availabilitiesFiltersDrawerTitle}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersDrawerTitle}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersDrawerTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersDrawerTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-drawer-apply"
                  label="Drawer apply label"
                  required
                  error={form.errors.availabilitiesFiltersDrawerApplyLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersDrawerApplyLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersDrawerApplyLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersDrawerApplyLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-drawer-reset"
                  label="Drawer reset label"
                  required
                  error={form.errors.availabilitiesFiltersDrawerResetLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersDrawerResetLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersDrawerResetLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersDrawerResetLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-chip-price-label"
                  label="Chip price label"
                  required
                  error={form.errors.availabilitiesFiltersChipPriceLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersChipPriceLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersChipPriceLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersChipPriceLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-chip-size-label"
                  label="Chip size label"
                  required
                  error={form.errors.availabilitiesFiltersChipSizeLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersChipSizeLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersChipSizeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersChipSizeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filters-chip-use-label"
                  label="Chip use label"
                  required
                  error={form.errors.availabilitiesFiltersChipUseLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFiltersChipUseLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFiltersChipUseLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFiltersChipUseLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="View toggle">
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  id="availabilities-view-label"
                  label="View label"
                  required
                  error={form.errors.availabilitiesViewLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesViewLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesViewLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesViewLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-view-cards-label"
                  label="Cards label"
                  required
                  error={form.errors.availabilitiesViewCardsLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesViewCardsLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesViewCardsLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesViewCardsLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-view-map-label"
                  label="Map label"
                  required
                  error={form.errors.availabilitiesViewMapLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesViewMapLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesViewMapLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesViewMapLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Filter controls">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="availabilities-filter-price-title"
                  label="Price filter title"
                  required
                  error={form.errors.availabilitiesFilterPriceTitle}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterPriceTitle}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterPriceTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterPriceTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filter-price-description"
                  label="Price filter description"
                  required
                  error={form.errors.availabilitiesFilterPriceDescription}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterPriceDescription}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterPriceDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterPriceDescription').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filter-size-title"
                  label="Size filter title"
                  required
                  error={form.errors.availabilitiesFilterSizeTitle}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterSizeTitle}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterSizeTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterSizeTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filter-size-description"
                  label="Size filter description"
                  required
                  error={form.errors.availabilitiesFilterSizeDescription}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterSizeDescription}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterSizeDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterSizeDescription').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filter-uses-title"
                  label="Uses filter title"
                  required
                  error={form.errors.availabilitiesFilterUsesTitle}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterUsesTitle}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterUsesTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterUsesTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filter-uses-description"
                  label="Uses filter description"
                  required
                  error={form.errors.availabilitiesFilterUsesDescription}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterUsesDescription}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterUsesDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterUsesDescription').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filter-min-label"
                  label="Min label"
                  required
                  error={form.errors.availabilitiesFilterMinLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterMinLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterMinLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterMinLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filter-max-label"
                  label="Max label"
                  required
                  error={form.errors.availabilitiesFilterMaxLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterMaxLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterMaxLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterMaxLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-filter-placeholder"
                  label="Filter placeholder"
                  required
                  error={form.errors.availabilitiesFilterPlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesFilterPlaceholder}
                    onChange={(event) => {
                      form.setValue('availabilitiesFilterPlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesFilterPlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Results messaging">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="availabilities-results-no-matches"
                  label="No matches message"
                  required
                  error={form.errors.availabilitiesResultsNoMatchesMessage}
                >
                  <textarea
                    value={form.values.availabilitiesResultsNoMatchesMessage}
                    onChange={(event) => {
                      form.setValue('availabilitiesResultsNoMatchesMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesResultsNoMatchesMessage').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-results-no-availability"
                  label="No availability message"
                  required
                  error={form.errors.availabilitiesResultsNoAvailabilityMessage}
                >
                  <textarea
                    value={form.values.availabilitiesResultsNoAvailabilityMessage}
                    onChange={(event) => {
                      form.setValue(
                        'availabilitiesResultsNoAvailabilityMessage',
                        event.target.value,
                      );
                    }}
                    onBlur={() => {
                      form.field('availabilitiesResultsNoAvailabilityMessage').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-results-clear-filters"
                  label="Clear filters label"
                  required
                  error={form.errors.availabilitiesResultsClearFiltersLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesResultsClearFiltersLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesResultsClearFiltersLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesResultsClearFiltersLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-results-error-message"
                  label="Results error message"
                  required
                  error={form.errors.availabilitiesResultsErrorMessage}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesResultsErrorMessage}
                    onChange={(event) => {
                      form.setValue('availabilitiesResultsErrorMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesResultsErrorMessage').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="List cards">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="availabilities-list-address-eyebrow"
                  label="Address eyebrow"
                  required
                  error={form.errors.availabilitiesListAddressEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesListAddressEyebrow}
                    onChange={(event) => {
                      form.setValue('availabilitiesListAddressEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesListAddressEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-list-unit-count-singular"
                  label="Unit count singular"
                  required
                  error={form.errors.availabilitiesListUnitCountSingular}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesListUnitCountSingular}
                    onChange={(event) => {
                      form.setValue('availabilitiesListUnitCountSingular', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesListUnitCountSingular').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-list-unit-count-plural"
                  label="Unit count plural"
                  required
                  error={form.errors.availabilitiesListUnitCountPlural}
                  description="Use {count} for the unit count."
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesListUnitCountPlural}
                    onChange={(event) => {
                      form.setValue('availabilitiesListUnitCountPlural', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesListUnitCountPlural').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-list-view-property-label"
                  label="View property label"
                  required
                  error={form.errors.availabilitiesListViewPropertyLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesListViewPropertyLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesListViewPropertyLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesListViewPropertyLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Map panel">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="availabilities-map-back-label"
                  label="Map back label"
                  required
                  error={form.errors.availabilitiesMapBackLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapBackLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapBackLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapBackLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-address-eyebrow"
                  label="Map address eyebrow"
                  required
                  error={form.errors.availabilitiesMapAddressEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapAddressEyebrow}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapAddressEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapAddressEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-addresses-eyebrow"
                  label="Map addresses eyebrow"
                  required
                  error={form.errors.availabilitiesMapAddressesEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapAddressesEyebrow}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapAddressesEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapAddressesEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-browse-title"
                  label="Map browse title"
                  required
                  error={form.errors.availabilitiesMapBrowseTitle}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapBrowseTitle}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapBrowseTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapBrowseTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-browse-description"
                  label="Map browse description"
                  required
                  error={form.errors.availabilitiesMapBrowseDescription}
                >
                  <textarea
                    value={form.values.availabilitiesMapBrowseDescription}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapBrowseDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapBrowseDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-select-unit-eyebrow"
                  label="Select unit eyebrow"
                  required
                  error={form.errors.availabilitiesMapSelectUnitEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapSelectUnitEyebrow}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapSelectUnitEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapSelectUnitEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-select-unit-description"
                  label="Select unit description"
                  required
                  error={form.errors.availabilitiesMapSelectUnitDescription}
                >
                  <textarea
                    value={form.values.availabilitiesMapSelectUnitDescription}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapSelectUnitDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapSelectUnitDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-tap-pin-eyebrow"
                  label="Tap pin eyebrow"
                  required
                  error={form.errors.availabilitiesMapTapPinEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapTapPinEyebrow}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapTapPinEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapTapPinEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-tap-pin-description"
                  label="Tap pin description"
                  required
                  error={form.errors.availabilitiesMapTapPinDescription}
                >
                  <textarea
                    value={form.values.availabilitiesMapTapPinDescription}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapTapPinDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapTapPinDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-suite-count-singular"
                  label="Suite count singular"
                  required
                  error={form.errors.availabilitiesMapSuiteCountSingular}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapSuiteCountSingular}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapSuiteCountSingular', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapSuiteCountSingular').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-suite-count-plural"
                  label="Suite count plural"
                  required
                  error={form.errors.availabilitiesMapSuiteCountPlural}
                  description="Use {count} for the suite count."
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapSuiteCountPlural}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapSuiteCountPlural', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapSuiteCountPlural').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-pin-fallback"
                  label="Map pin fallback"
                  required
                  error={form.errors.availabilitiesMapPinFallback}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapPinFallback}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapPinFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapPinFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Map unit row">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="availabilities-map-unit-label-template"
                  label="Unit label template"
                  required
                  error={form.errors.availabilitiesMapUnitLabelTemplate}
                  description="Use {unitLabel} for the unit label (legacy {unitCode} still works)."
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapUnitLabelTemplate}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapUnitLabelTemplate', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapUnitLabelTemplate').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-level-label-template"
                  label="Floor label template"
                  required
                  error={form.errors.availabilitiesMapLevelLabelTemplate}
                  description="Use {floor} for the floor."
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapLevelLabelTemplate}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapLevelLabelTemplate', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapLevelLabelTemplate').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-divisible-label"
                  label="Divisible label"
                  required
                  error={form.errors.availabilitiesMapDivisibleLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapDivisibleLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapDivisibleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapDivisibleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-map-incentives-label"
                  label="Incentives label"
                  required
                  error={form.errors.availabilitiesMapIncentivesLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesMapIncentivesLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesMapIncentivesLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesMapIncentivesLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Quick view">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="availabilities-quick-view-eyebrow"
                  label="Quick view eyebrow"
                  required
                  error={form.errors.availabilitiesQuickViewEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewEyebrow}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-title-template"
                  label="Quick view title template"
                  required
                  error={form.errors.availabilitiesQuickViewTitleTemplate}
                  description="Use {unitLabel} and {floor} (legacy {unitCode} still works)."
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewTitleTemplate}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewTitleTemplate', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewTitleTemplate').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-rent-label"
                  label="Rent label"
                  required
                  error={form.errors.availabilitiesQuickViewRentLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewRentLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewRentLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewRentLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-rent-suffix"
                  label="Rent suffix"
                  required
                  error={form.errors.availabilitiesQuickViewRentSuffix}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewRentSuffix}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewRentSuffix', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewRentSuffix').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-size-label"
                  label="Size label"
                  required
                  error={form.errors.availabilitiesQuickViewSizeLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewSizeLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewSizeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewSizeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-availability-label"
                  label="Availability label"
                  required
                  error={form.errors.availabilitiesQuickViewAvailabilityLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewAvailabilityLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewAvailabilityLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewAvailabilityLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-schedule-label"
                  label="Schedule label"
                  required
                  error={form.errors.availabilitiesQuickViewScheduleLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewScheduleLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewScheduleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewScheduleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-details-label"
                  label="Details label"
                  required
                  error={form.errors.availabilitiesQuickViewDetailsLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewDetailsLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewDetailsLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewDetailsLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-divisible-label"
                  label="Divisible label"
                  required
                  error={form.errors.availabilitiesQuickViewDivisibleLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewDivisibleLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewDivisibleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewDivisibleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="availabilities-quick-view-incentives-label"
                  label="Incentives label"
                  required
                  error={form.errors.availabilitiesQuickViewIncentivesLabel}
                >
                  <input
                    type="text"
                    value={form.values.availabilitiesQuickViewIncentivesLabel}
                    onChange={(event) => {
                      form.setValue('availabilitiesQuickViewIncentivesLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('availabilitiesQuickViewIncentivesLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {showPage('property-detail') ? (
          <SettingsCard
            title="Property detail"
            id="settings-property-detail"
            description="Property detail headings, labels, and highlight copy."
          >
            <SettingsGroup title="Status messages">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-unpublished-message"
                  label="Unpublished message"
                  required
                  error={form.errors.propertyDetailMessagesUnpublished}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailMessagesUnpublished}
                    onChange={(event) => {
                      form.setValue('propertyDetailMessagesUnpublished', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailMessagesUnpublished').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-cta-label"
                  label="CTA label"
                  required
                  error={form.errors.propertyDetailMessagesCtaLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailMessagesCtaLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailMessagesCtaLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailMessagesCtaLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Section navigation">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-nav-overview"
                  label="Overview tab label"
                  required
                  error={form.errors.propertyDetailNavOverview}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailNavOverview}
                    onChange={(event) => {
                      form.setValue('propertyDetailNavOverview', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailNavOverview').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-nav-highlights"
                  label="Highlights tab label"
                  required
                  error={form.errors.propertyDetailNavHighlights}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailNavHighlights}
                    onChange={(event) => {
                      form.setValue('propertyDetailNavHighlights', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailNavHighlights').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-nav-location"
                  label="Location tab label"
                  required
                  error={form.errors.propertyDetailNavLocation}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailNavLocation}
                    onChange={(event) => {
                      form.setValue('propertyDetailNavLocation', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailNavLocation').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-nav-availability"
                  label="Availability tab label"
                  required
                  error={form.errors.propertyDetailNavAvailability}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailNavAvailability}
                    onChange={(event) => {
                      form.setValue('propertyDetailNavAvailability', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailNavAvailability').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Availability strip">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-availability-no-units"
                  label="No units label"
                  required
                  error={form.errors.propertyDetailAvailabilityNoUnits}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailAvailabilityNoUnits}
                    onChange={(event) => {
                      form.setValue('propertyDetailAvailabilityNoUnits', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailAvailabilityNoUnits').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-availability-template"
                  label="Availability template"
                  required
                  error={form.errors.propertyDetailAvailabilityTemplate}
                  description="Use {available} and {total}."
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailAvailabilityTemplate}
                    onChange={(event) => {
                      form.setValue('propertyDetailAvailabilityTemplate', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailAvailabilityTemplate').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-availability-updated-label"
                  label="Updated label"
                  required
                  error={form.errors.propertyDetailAvailabilityUpdatedLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailAvailabilityUpdatedLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailAvailabilityUpdatedLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailAvailabilityUpdatedLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Overview">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-overview-eyebrow"
                  label="Overview eyebrow"
                  required
                  error={form.errors.propertyDetailOverviewEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailOverviewEyebrow}
                    onChange={(event) => {
                      form.setValue('propertyDetailOverviewEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailOverviewEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-overview-view-on-map-label"
                  label="View on map label"
                  required
                  error={form.errors.propertyDetailOverviewViewOnMapLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailOverviewViewOnMapLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailOverviewViewOnMapLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailOverviewViewOnMapLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-overview-title-template"
                  label="Overview title template"
                  required
                  error={form.errors.propertyDetailOverviewTitleTemplate}
                  description="Use {assetType} and {submarket}."
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailOverviewTitleTemplate}
                    onChange={(event) => {
                      form.setValue('propertyDetailOverviewTitleTemplate', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailOverviewTitleTemplate').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Units section">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-units-eyebrow"
                  label="Units eyebrow"
                  required
                  error={form.errors.propertyDetailUnitsEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsEyebrow}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-title"
                  label="Units title"
                  required
                  error={form.errors.propertyDetailUnitsTitle}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsTitle}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-listed-template"
                  label="Units listed template"
                  required
                  error={form.errors.propertyDetailUnitsListedTemplate}
                  description="Use {filtered} and {total}."
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsListedTemplate}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsListedTemplate', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsListedTemplate').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-empty-all"
                  label="Units empty message"
                  required
                  error={form.errors.propertyDetailUnitsEmptyAll}
                >
                  <textarea
                    value={form.values.propertyDetailUnitsEmptyAll}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsEmptyAll', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsEmptyAll').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-empty-filtered"
                  label="Units filtered message"
                  required
                  error={form.errors.propertyDetailUnitsEmptyFiltered}
                >
                  <textarea
                    value={form.values.propertyDetailUnitsEmptyFiltered}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsEmptyFiltered', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsEmptyFiltered').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-count-singular"
                  label="Unit count singular"
                  required
                  error={form.errors.propertyDetailUnitsCountSingular}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsCountSingular}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsCountSingular', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsCountSingular').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-count-plural"
                  label="Unit count plural"
                  required
                  error={form.errors.propertyDetailUnitsCountPlural}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsCountPlural}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsCountPlural', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsCountPlural').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-floor-plan-fallback"
                  label="Floor plan fallback"
                  required
                  error={form.errors.propertyDetailUnitsFloorPlanFallback}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsFloorPlanFallback}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsFloorPlanFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsFloorPlanFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-monthly-lease-label"
                  label="Monthly lease label"
                  required
                  error={form.errors.propertyDetailUnitsMonthlyLeaseLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsMonthlyLeaseLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsMonthlyLeaseLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsMonthlyLeaseLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-contact-label"
                  label="Contact label"
                  required
                  error={form.errors.propertyDetailUnitsContactLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsContactLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsContactLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsContactLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-contact-fallback"
                  label="Contact fallback"
                  required
                  error={form.errors.propertyDetailUnitsContactFallback}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsContactFallback}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsContactFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsContactFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-book-button-label"
                  label="Book button label"
                  required
                  error={form.errors.propertyDetailUnitsBookButtonLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsBookButtonLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsBookButtonLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsBookButtonLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-view-details-label"
                  label="View details label"
                  required
                  error={form.errors.propertyDetailUnitsViewDetailsLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsViewDetailsLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsViewDetailsLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsViewDetailsLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Units table">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-units-table-suite-header"
                  label="Suite header"
                  required
                  error={form.errors.propertyDetailUnitsTableSuiteHeader}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsTableSuiteHeader}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsTableSuiteHeader', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTableSuiteHeader').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-table-floor-header"
                  label="Floor header"
                  required
                  error={form.errors.propertyDetailUnitsTableFloorHeader}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsTableFloorHeader}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsTableFloorHeader', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTableFloorHeader').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-table-size-header"
                  label="Size header"
                  required
                  error={form.errors.propertyDetailUnitsTableSizeHeader}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsTableSizeHeader}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsTableSizeHeader', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTableSizeHeader').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-table-monthly-lease-header"
                  label="Monthly lease header"
                  required
                  error={form.errors.propertyDetailUnitsTableMonthlyLeaseHeader}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsTableMonthlyLeaseHeader}
                    onChange={(event) => {
                      form.setValue(
                        'propertyDetailUnitsTableMonthlyLeaseHeader',
                        event.target.value,
                      );
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTableMonthlyLeaseHeader').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-table-rate-header"
                  label="Rate header"
                  required
                  error={form.errors.propertyDetailUnitsTableRateHeader}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsTableRateHeader}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsTableRateHeader', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTableRateHeader').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-table-action-header"
                  label="Action header"
                  required
                  error={form.errors.propertyDetailUnitsTableActionHeader}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitsTableActionHeader}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsTableActionHeader', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTableActionHeader').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-table-no-units-message"
                  label="No units message"
                  required
                  error={form.errors.propertyDetailUnitsTableNoUnitsMessage}
                >
                  <textarea
                    value={form.values.propertyDetailUnitsTableNoUnitsMessage}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsTableNoUnitsMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTableNoUnitsMessage').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-units-table-no-matches-message"
                  label="No matches message"
                  required
                  error={form.errors.propertyDetailUnitsTableNoMatchesMessage}
                >
                  <textarea
                    value={form.values.propertyDetailUnitsTableNoMatchesMessage}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitsTableNoMatchesMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitsTableNoMatchesMessage').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Unit filters">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-unit-filters-size-label"
                  label="Size filter label"
                  required
                  error={form.errors.propertyDetailUnitFiltersSizeLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitFiltersSizeLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitFiltersSizeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitFiltersSizeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-unit-filters-floor-label"
                  label="Floor filter label"
                  required
                  error={form.errors.propertyDetailUnitFiltersFloorLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitFiltersFloorLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitFiltersFloorLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitFiltersFloorLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-unit-filters-rate-label"
                  label="Rate filter label"
                  required
                  error={form.errors.propertyDetailUnitFiltersRateLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitFiltersRateLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitFiltersRateLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitFiltersRateLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-unit-filters-size-all-label"
                  label="Size all label"
                  required
                  error={form.errors.propertyDetailUnitFiltersSizeAllLabel}
                  description="Use {count} for the size count."
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitFiltersSizeAllLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitFiltersSizeAllLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitFiltersSizeAllLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-unit-filters-floor-all-label"
                  label="Floor all label"
                  required
                  error={form.errors.propertyDetailUnitFiltersFloorAllLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitFiltersFloorAllLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitFiltersFloorAllLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitFiltersFloorAllLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-unit-filters-rate-all-label"
                  label="Rate all label"
                  required
                  error={form.errors.propertyDetailUnitFiltersRateAllLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailUnitFiltersRateAllLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailUnitFiltersRateAllLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailUnitFiltersRateAllLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Highlights">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-highlights-eyebrow"
                  label="Highlights eyebrow"
                  required
                  error={form.errors.propertyDetailHighlightsEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailHighlightsEyebrow}
                    onChange={(event) => {
                      form.setValue('propertyDetailHighlightsEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailHighlightsEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-highlights-title"
                  label="Highlights title"
                  required
                  error={form.errors.propertyDetailHighlightsTitle}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailHighlightsTitle}
                    onChange={(event) => {
                      form.setValue('propertyDetailHighlightsTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailHighlightsTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="property-detail-highlights-description"
                label="Highlights description"
                required
                error={form.errors.propertyDetailHighlightsDescription}
              >
                <textarea
                  value={form.values.propertyDetailHighlightsDescription}
                  onChange={(event) => {
                    form.setValue('propertyDetailHighlightsDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('propertyDetailHighlightsDescription').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-highlights-divisible-label"
                  label="Divisible label"
                  required
                  error={form.errors.propertyDetailHighlightsDivisibleLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailHighlightsDivisibleLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailHighlightsDivisibleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailHighlightsDivisibleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-highlights-incentives-label"
                  label="Incentives label"
                  required
                  error={form.errors.propertyDetailHighlightsIncentivesLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailHighlightsIncentivesLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailHighlightsIncentivesLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailHighlightsIncentivesLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-highlights-floor-plan-label"
                  label="Floor plan label"
                  required
                  error={form.errors.propertyDetailHighlightsFloorPlanLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailHighlightsFloorPlanLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailHighlightsFloorPlanLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailHighlightsFloorPlanLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="property-detail-highlights-empty-message"
                label="Highlights empty message"
                required
                error={form.errors.propertyDetailHighlightsEmptyMessage}
              >
                <textarea
                  value={form.values.propertyDetailHighlightsEmptyMessage}
                  onChange={(event) => {
                    form.setValue('propertyDetailHighlightsEmptyMessage', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('propertyDetailHighlightsEmptyMessage').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Location card">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-location-title"
                  label="Location title"
                  required
                  error={form.errors.propertyDetailLocationTitle}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailLocationTitle}
                    onChange={(event) => {
                      form.setValue('propertyDetailLocationTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailLocationTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-location-directions-label"
                  label="Directions label"
                  required
                  error={form.errors.propertyDetailLocationDirectionsLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailLocationDirectionsLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailLocationDirectionsLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailLocationDirectionsLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Key figures">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-key-figures-title"
                  label="Key figures title"
                  required
                  error={form.errors.propertyDetailKeyFiguresTitle}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailKeyFiguresTitle}
                    onChange={(event) => {
                      form.setValue('propertyDetailKeyFiguresTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailKeyFiguresTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-key-figures-subtitle"
                  label="Key figures subtitle"
                  required
                  error={form.errors.propertyDetailKeyFiguresSubtitle}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailKeyFiguresSubtitle}
                    onChange={(event) => {
                      form.setValue('propertyDetailKeyFiguresSubtitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailKeyFiguresSubtitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-key-figures-total-rentable-label"
                  label="Total rentable label"
                  required
                  error={form.errors.propertyDetailKeyFiguresTotalRentableLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailKeyFiguresTotalRentableLabel}
                    onChange={(event) => {
                      form.setValue(
                        'propertyDetailKeyFiguresTotalRentableLabel',
                        event.target.value,
                      );
                    }}
                    onBlur={() => {
                      form.field('propertyDetailKeyFiguresTotalRentableLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-key-figures-units-live-label"
                  label="Units live label"
                  required
                  error={form.errors.propertyDetailKeyFiguresUnitsLiveLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailKeyFiguresUnitsLiveLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailKeyFiguresUnitsLiveLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailKeyFiguresUnitsLiveLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-key-figures-incentives-label"
                  label="Incentives label"
                  required
                  error={form.errors.propertyDetailKeyFiguresIncentivesLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailKeyFiguresIncentivesLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailKeyFiguresIncentivesLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailKeyFiguresIncentivesLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-key-figures-divisible-label"
                  label="Divisible label"
                  required
                  error={form.errors.propertyDetailKeyFiguresDivisibleLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailKeyFiguresDivisibleLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailKeyFiguresDivisibleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailKeyFiguresDivisibleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Fallback labels">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="property-detail-labels-rent-fallback"
                  label="Rent fallback"
                  required
                  error={form.errors.propertyDetailLabelsRentFallback}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailLabelsRentFallback}
                    onChange={(event) => {
                      form.setValue('propertyDetailLabelsRentFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailLabelsRentFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-labels-availability-range-fallback"
                  label="Availability range fallback"
                  required
                  error={form.errors.propertyDetailLabelsAvailabilityRangeFallback}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailLabelsAvailabilityRangeFallback}
                    onChange={(event) => {
                      form.setValue(
                        'propertyDetailLabelsAvailabilityRangeFallback',
                        event.target.value,
                      );
                    }}
                    onBlur={() => {
                      form.field('propertyDetailLabelsAvailabilityRangeFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-labels-availability-single-fallback"
                  label="Availability single fallback"
                  required
                  error={form.errors.propertyDetailLabelsAvailabilitySingleFallback}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailLabelsAvailabilitySingleFallback}
                    onChange={(event) => {
                      form.setValue(
                        'propertyDetailLabelsAvailabilitySingleFallback',
                        event.target.value,
                      );
                    }}
                    onBlur={() => {
                      form.field('propertyDetailLabelsAvailabilitySingleFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-labels-unit-immediate"
                  label="Unit immediate label"
                  required
                  error={form.errors.propertyDetailLabelsUnitImmediateLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailLabelsUnitImmediateLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailLabelsUnitImmediateLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailLabelsUnitImmediateLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="property-detail-labels-unit-negotiable"
                  label="Unit negotiable label"
                  required
                  error={form.errors.propertyDetailLabelsUnitNegotiableLabel}
                >
                  <input
                    type="text"
                    value={form.values.propertyDetailLabelsUnitNegotiableLabel}
                    onChange={(event) => {
                      form.setValue('propertyDetailLabelsUnitNegotiableLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('propertyDetailLabelsUnitNegotiableLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {showPage('unit-detail') ? (
          <SettingsCard
            title="Unit detail"
            id="settings-unit-detail"
            description="Unit detail headings, labels, and leasing prompts."
          >
            <SettingsGroup title="Status messages">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="unit-detail-not-found-message"
                  label="Not found message"
                  required
                  error={form.errors.unitDetailNotFoundMessage}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailNotFoundMessage}
                    onChange={(event) => {
                      form.setValue('unitDetailNotFoundMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailNotFoundMessage').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-unavailable-message"
                  label="Unavailable message"
                  required
                  error={form.errors.unitDetailUnavailableMessage}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailUnavailableMessage}
                    onChange={(event) => {
                      form.setValue('unitDetailUnavailableMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailUnavailableMessage').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Actions">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="unit-detail-view-property-label"
                  label="View property label"
                  required
                  error={form.errors.unitDetailViewPropertyLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailViewPropertyLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailViewPropertyLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailViewPropertyLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-browse-availabilities-label"
                  label="Browse availabilities label"
                  required
                  error={form.errors.unitDetailBrowseAvailabilitiesLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailBrowseAvailabilitiesLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailBrowseAvailabilitiesLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailBrowseAvailabilitiesLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-view-other-units-label"
                  label="View other units label"
                  required
                  error={form.errors.unitDetailViewOtherUnitsLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailViewOtherUnitsLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailViewOtherUnitsLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailViewOtherUnitsLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-sticky-eyebrow"
                  label="Sticky eyebrow"
                  required
                  error={form.errors.unitDetailStickyEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailStickyEyebrow}
                    onChange={(event) => {
                      form.setValue('unitDetailStickyEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailStickyEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-sticky-button-label"
                  label="Sticky button label"
                  required
                  error={form.errors.unitDetailStickyButtonLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailStickyButtonLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailStickyButtonLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailStickyButtonLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Headings">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="unit-detail-at-a-glance-title"
                  label="At a glance title"
                  required
                  error={form.errors.unitDetailAtAGlanceTitle}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailAtAGlanceTitle}
                    onChange={(event) => {
                      form.setValue('unitDetailAtAGlanceTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailAtAGlanceTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-property-details-title"
                  label="Property details title"
                  required
                  error={form.errors.unitDetailPropertyDetailsTitle}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailPropertyDetailsTitle}
                    onChange={(event) => {
                      form.setValue('unitDetailPropertyDetailsTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailPropertyDetailsTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-lease-details-title"
                  label="Lease details title"
                  required
                  error={form.errors.unitDetailLeaseDetailsTitle}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailLeaseDetailsTitle}
                    onChange={(event) => {
                      form.setValue('unitDetailLeaseDetailsTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailLeaseDetailsTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-property-description-eyebrow"
                  label="Property description eyebrow"
                  required
                  error={form.errors.unitDetailPropertyDescriptionEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailPropertyDescriptionEyebrow}
                    onChange={(event) => {
                      form.setValue('unitDetailPropertyDescriptionEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailPropertyDescriptionEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-property-description-title"
                  label="Property description title"
                  required
                  error={form.errors.unitDetailPropertyDescriptionTitle}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailPropertyDescriptionTitle}
                    onChange={(event) => {
                      form.setValue('unitDetailPropertyDescriptionTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailPropertyDescriptionTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-schedule-eyebrow"
                  label="Schedule eyebrow"
                  required
                  error={form.errors.unitDetailScheduleEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailScheduleEyebrow}
                    onChange={(event) => {
                      form.setValue('unitDetailScheduleEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailScheduleEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-schedule-title"
                  label="Schedule title"
                  required
                  error={form.errors.unitDetailScheduleTitle}
                  description="Use {unitLabel} for the unit label."
                >
                  <input
                    type="text"
                    value={form.values.unitDetailScheduleTitle}
                    onChange={(event) => {
                      form.setValue('unitDetailScheduleTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailScheduleTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-leasing-details-eyebrow"
                  label="Leasing details eyebrow"
                  required
                  error={form.errors.unitDetailLeasingDetailsEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailLeasingDetailsEyebrow}
                    onChange={(event) => {
                      form.setValue('unitDetailLeasingDetailsEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailLeasingDetailsEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-leasing-details-title"
                  label="Leasing details title"
                  required
                  error={form.errors.unitDetailLeasingDetailsTitle}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailLeasingDetailsTitle}
                    onChange={(event) => {
                      form.setValue('unitDetailLeasingDetailsTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailLeasingDetailsTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-photo-gallery-title"
                  label="Photo gallery title"
                  required
                  error={form.errors.unitDetailPhotoGalleryTitle}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailPhotoGalleryTitle}
                    onChange={(event) => {
                      form.setValue('unitDetailPhotoGalleryTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailPhotoGalleryTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Labels">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="unit-detail-size-label"
                  label="Size label"
                  required
                  error={form.errors.unitDetailSizeLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailSizeLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailSizeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailSizeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-floor-label"
                  label="Floor label"
                  required
                  error={form.errors.unitDetailFloorLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailFloorLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailFloorLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailFloorLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-divisible-label"
                  label="Divisible label"
                  required
                  error={form.errors.unitDetailDivisibleLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailDivisibleLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailDivisibleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailDivisibleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-status-label"
                  label="Status label"
                  required
                  error={form.errors.unitDetailStatusLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailStatusLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailStatusLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailStatusLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-availability-label"
                  label="Availability label"
                  required
                  error={form.errors.unitDetailAvailabilityLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailAvailabilityLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailAvailabilityLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailAvailabilityLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-updated-label"
                  label="Updated label"
                  required
                  error={form.errors.unitDetailUpdatedLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailUpdatedLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailUpdatedLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailUpdatedLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-immediate-label"
                  label="Immediate label"
                  required
                  error={form.errors.unitDetailImmediateLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailImmediateLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailImmediateLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailImmediateLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-negotiable-label"
                  label="Negotiable label"
                  required
                  error={form.errors.unitDetailNegotiableLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailNegotiableLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailNegotiableLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailNegotiableLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="unit-detail-updated-recently-label"
                  label="Updated recently label"
                  required
                  error={form.errors.unitDetailUpdatedRecentlyLabel}
                >
                  <input
                    type="text"
                    value={form.values.unitDetailUpdatedRecentlyLabel}
                    onChange={(event) => {
                      form.setValue('unitDetailUpdatedRecentlyLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('unitDetailUpdatedRecentlyLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Leasing prompts">
              <FormField
                id="unit-detail-leasing-prompt-industrial"
                label="Industrial prompt"
                required
                error={form.errors.unitDetailLeasingPromptIndustrial}
              >
                <textarea
                  value={form.values.unitDetailLeasingPromptIndustrial}
                  onChange={(event) => {
                    form.setValue('unitDetailLeasingPromptIndustrial', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('unitDetailLeasingPromptIndustrial').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="unit-detail-leasing-prompt-flex"
                label="Flex prompt"
                required
                error={form.errors.unitDetailLeasingPromptFlex}
              >
                <textarea
                  value={form.values.unitDetailLeasingPromptFlex}
                  onChange={(event) => {
                    form.setValue('unitDetailLeasingPromptFlex', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('unitDetailLeasingPromptFlex').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="unit-detail-leasing-prompt-retail"
                label="Retail prompt"
                required
                error={form.errors.unitDetailLeasingPromptRetail}
              >
                <textarea
                  value={form.values.unitDetailLeasingPromptRetail}
                  onChange={(event) => {
                    form.setValue('unitDetailLeasingPromptRetail', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('unitDetailLeasingPromptRetail').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="unit-detail-leasing-prompt-office"
                label="Office prompt"
                required
                error={form.errors.unitDetailLeasingPromptOffice}
              >
                <textarea
                  value={form.values.unitDetailLeasingPromptOffice}
                  onChange={(event) => {
                    form.setValue('unitDetailLeasingPromptOffice', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('unitDetailLeasingPromptOffice').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="unit-detail-leasing-prompt-fallback"
                label="Fallback prompt"
                required
                error={form.errors.unitDetailLeasingPromptFallback}
              >
                <textarea
                  value={form.values.unitDetailLeasingPromptFallback}
                  onChange={(event) => {
                    form.setValue('unitDetailLeasingPromptFallback', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('unitDetailLeasingPromptFallback').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Photo gallery">
              <FormField
                id="unit-detail-photo-gallery-empty-message"
                label="Photo gallery empty message"
                required
                error={form.errors.unitDetailPhotoGalleryEmptyMessage}
              >
                <input
                  type="text"
                  value={form.values.unitDetailPhotoGalleryEmptyMessage}
                  onChange={(event) => {
                    form.setValue('unitDetailPhotoGalleryEmptyMessage', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('unitDetailPhotoGalleryEmptyMessage').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {showPage('showing') ? (
          <SettingsCard
            title="Showing dialog"
            id="settings-showing"
            description="Showing request dialog steps, field labels, and confirmations."
          >
            <SettingsGroup title="Overview">
              <FormField
                id="showing-dialog-title"
                label="Dialog title"
                required
                error={form.errors.showingDialogTitle}
              >
                <input
                  type="text"
                  value={form.values.showingDialogTitle}
                  onChange={(event) => {
                    form.setValue('showingDialogTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('showingDialogTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="showing-dialog-description"
                label="Dialog description"
                required
                error={form.errors.showingDialogDescription}
              >
                <textarea
                  value={form.values.showingDialogDescription}
                  onChange={(event) => {
                    form.setValue('showingDialogDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('showingDialogDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="showing-dialog-step-indicator"
                label="Step indicator template"
                required
                error={form.errors.showingDialogStepIndicatorTemplate}
                description="Use {step} and {total}."
              >
                <input
                  type="text"
                  value={form.values.showingDialogStepIndicatorTemplate}
                  onChange={(event) => {
                    form.setValue('showingDialogStepIndicatorTemplate', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('showingDialogStepIndicatorTemplate').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="showing-dialog-selected-unit-label"
                  label="Selected unit label"
                  required
                  error={form.errors.showingDialogSelectedUnitLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogSelectedUnitLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogSelectedUnitLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSelectedUnitLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-selected-unit-description"
                  label="Selected unit description"
                  required
                  error={form.errors.showingDialogSelectedUnitDescription}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogSelectedUnitDescription}
                    onChange={(event) => {
                      form.setValue('showingDialogSelectedUnitDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSelectedUnitDescription').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Step labels">
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  id="showing-dialog-step-contact-label"
                  label="Contact step label"
                  required
                  error={form.errors.showingDialogStepContactLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogStepContactLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogStepContactLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogStepContactLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-step-schedule-label"
                  label="Schedule step label"
                  required
                  error={form.errors.showingDialogStepScheduleLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogStepScheduleLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogStepScheduleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogStepScheduleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-step-review-label"
                  label="Review step label"
                  required
                  error={form.errors.showingDialogStepReviewLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogStepReviewLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogStepReviewLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogStepReviewLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Field labels">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="showing-dialog-field-first-name"
                  label="First name label"
                  required
                  error={form.errors.showingDialogFieldFirstNameLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldFirstNameLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldFirstNameLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldFirstNameLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-field-last-name"
                  label="Last name label"
                  required
                  error={form.errors.showingDialogFieldLastNameLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldLastNameLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldLastNameLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldLastNameLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-field-email"
                  label="Email label"
                  required
                  error={form.errors.showingDialogFieldEmailLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldEmailLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldEmailLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldEmailLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-field-phone"
                  label="Phone label"
                  required
                  error={form.errors.showingDialogFieldPhoneLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldPhoneLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldPhoneLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldPhoneLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-field-date"
                  label="Date label"
                  required
                  error={form.errors.showingDialogFieldDateLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldDateLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldDateLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldDateLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-field-time"
                  label="Time label"
                  required
                  error={form.errors.showingDialogFieldTimeLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldTimeLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldTimeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldTimeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-field-message"
                  label="Message label"
                  required
                  error={form.errors.showingDialogFieldMessageLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldMessageLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldMessageLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldMessageLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Field hints">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="showing-dialog-field-date-hint"
                  label="Date hint"
                  required
                  error={form.errors.showingDialogFieldDateHint}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldDateHint}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldDateHint', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldDateHint').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-field-time-hint"
                  label="Time hint"
                  required
                  error={form.errors.showingDialogFieldTimeHint}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogFieldTimeHint}
                    onChange={(event) => {
                      form.setValue('showingDialogFieldTimeHint', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogFieldTimeHint').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-message-placeholder"
                  label="Message placeholder"
                  required
                  error={form.errors.showingDialogMessagePlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogMessagePlaceholder}
                    onChange={(event) => {
                      form.setValue('showingDialogMessagePlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogMessagePlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Review screen">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="showing-dialog-review-title"
                  label="Review title"
                  required
                  error={form.errors.showingDialogReviewTitle}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogReviewTitle}
                    onChange={(event) => {
                      form.setValue('showingDialogReviewTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogReviewTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-review-contact-label"
                  label="Review contact label"
                  required
                  error={form.errors.showingDialogReviewContactLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogReviewContactLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogReviewContactLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogReviewContactLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-review-slot-label"
                  label="Review slot label"
                  required
                  error={form.errors.showingDialogReviewSlotLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogReviewSlotLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogReviewSlotLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogReviewSlotLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-review-unit-label"
                  label="Review unit label"
                  required
                  error={form.errors.showingDialogReviewUnitLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogReviewUnitLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogReviewUnitLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogReviewUnitLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-review-message-label"
                  label="Review message label"
                  required
                  error={form.errors.showingDialogReviewMessageLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogReviewMessageLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogReviewMessageLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogReviewMessageLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-review-confirmation-note"
                  label="Review confirmation note"
                  required
                  error={form.errors.showingDialogReviewConfirmationNote}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogReviewConfirmationNote}
                    onChange={(event) => {
                      form.setValue('showingDialogReviewConfirmationNote', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogReviewConfirmationNote').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-review-guidance-next"
                  label="Review guidance (next)"
                  required
                  error={form.errors.showingDialogReviewGuidanceNext}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogReviewGuidanceNext}
                    onChange={(event) => {
                      form.setValue('showingDialogReviewGuidanceNext', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogReviewGuidanceNext').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-review-guidance-review"
                  label="Review guidance (review)"
                  required
                  error={form.errors.showingDialogReviewGuidanceReview}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogReviewGuidanceReview}
                    onChange={(event) => {
                      form.setValue('showingDialogReviewGuidanceReview', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogReviewGuidanceReview').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Buttons">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="showing-dialog-button-cancel"
                  label="Cancel button label"
                  required
                  error={form.errors.showingDialogButtonCancelLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogButtonCancelLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogButtonCancelLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogButtonCancelLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-button-back"
                  label="Back button label"
                  required
                  error={form.errors.showingDialogButtonBackLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogButtonBackLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogButtonBackLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogButtonBackLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-button-next"
                  label="Next button label"
                  required
                  error={form.errors.showingDialogButtonNextLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogButtonNextLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogButtonNextLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogButtonNextLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-button-confirm"
                  label="Confirm button label"
                  required
                  error={form.errors.showingDialogButtonConfirmLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogButtonConfirmLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogButtonConfirmLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogButtonConfirmLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-button-done"
                  label="Done button label"
                  required
                  error={form.errors.showingDialogButtonDoneLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogButtonDoneLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogButtonDoneLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogButtonDoneLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Errors">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="showing-dialog-error-contact-missing"
                  label="Contact error message"
                  required
                  error={form.errors.showingDialogErrorContactMissing}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogErrorContactMissing}
                    onChange={(event) => {
                      form.setValue('showingDialogErrorContactMissing', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogErrorContactMissing').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-error-schedule-missing"
                  label="Schedule error message"
                  required
                  error={form.errors.showingDialogErrorScheduleMissing}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogErrorScheduleMissing}
                    onChange={(event) => {
                      form.setValue('showingDialogErrorScheduleMissing', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogErrorScheduleMissing').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-error-submit"
                  label="Submit error message"
                  required
                  error={form.errors.showingDialogErrorSubmit}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogErrorSubmit}
                    onChange={(event) => {
                      form.setValue('showingDialogErrorSubmit', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogErrorSubmit').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Success">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="showing-dialog-success-title"
                  label="Success title"
                  required
                  error={form.errors.showingDialogSuccessTitle}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogSuccessTitle}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-success-description"
                  label="Success description"
                  required
                  error={form.errors.showingDialogSuccessDescription}
                >
                  <textarea
                    value={form.values.showingDialogSuccessDescription}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-success-time-label"
                  label="Success time label"
                  required
                  error={form.errors.showingDialogSuccessTimeLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogSuccessTimeLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessTimeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessTimeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-success-email-label"
                  label="Success email label"
                  required
                  error={form.errors.showingDialogSuccessEmailLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogSuccessEmailLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessEmailLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessEmailLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-success-name-label"
                  label="Success name label"
                  required
                  error={form.errors.showingDialogSuccessNameLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogSuccessNameLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessNameLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessNameLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-success-message-label"
                  label="Success message label"
                  required
                  error={form.errors.showingDialogSuccessMessageLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogSuccessMessageLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessMessageLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessMessageLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-success-calendar-label"
                  label="Success calendar label"
                  required
                  error={form.errors.showingDialogSuccessCalendarLabel}
                >
                  <input
                    type="text"
                    value={form.values.showingDialogSuccessCalendarLabel}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessCalendarLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessCalendarLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-success-portal-queued"
                  label="Portal queued message"
                  required
                  error={form.errors.showingDialogSuccessPortalQueued}
                  description="Use {email} for the recipient email."
                >
                  <textarea
                    value={form.values.showingDialogSuccessPortalQueued}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessPortalQueued', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessPortalQueued').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="showing-dialog-success-portal-sent"
                  label="Portal sent message"
                  required
                  error={form.errors.showingDialogSuccessPortalSent}
                >
                  <textarea
                    value={form.values.showingDialogSuccessPortalSent}
                    onChange={(event) => {
                      form.setValue('showingDialogSuccessPortalSent', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('showingDialogSuccessPortalSent').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Tooltip">
              <FormField
                id="showing-dialog-unavailable-tooltip"
                label="Unavailable tooltip"
                required
                error={form.errors.showingDialogUnavailableTooltip}
              >
                <input
                  type="text"
                  value={form.values.showingDialogUnavailableTooltip}
                  onChange={(event) => {
                    form.setValue('showingDialogUnavailableTooltip', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('showingDialogUnavailableTooltip').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {showPage('shared') ? (
          <SettingsCard
            title="Shared labels"
            id="settings-shared"
            description="Reusable labels and empty states across the public site."
          >
            <SettingsGroup title="Contact section labels">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="shared-contact-address-label"
                  label="Address label"
                  required
                  error={form.errors.sharedContactAddressLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactAddressLabel}
                    onChange={(event) => {
                      form.setValue('sharedContactAddressLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactAddressLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-phone-label"
                  label="Phone label"
                  required
                  error={form.errors.sharedContactPhoneLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactPhoneLabel}
                    onChange={(event) => {
                      form.setValue('sharedContactPhoneLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactPhoneLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-email-label"
                  label="Email label"
                  required
                  error={form.errors.sharedContactEmailLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactEmailLabel}
                    onChange={(event) => {
                      form.setValue('sharedContactEmailLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactEmailLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-hours-label"
                  label="Hours label"
                  required
                  error={form.errors.sharedContactHoursLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactHoursLabel}
                    onChange={(event) => {
                      form.setValue('sharedContactHoursLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactHoursLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-leadership-label"
                  label="Leadership label"
                  required
                  error={form.errors.sharedContactLeadershipLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactLeadershipLabel}
                    onChange={(event) => {
                      form.setValue('sharedContactLeadershipLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactLeadershipLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-map-title"
                  label="Map title"
                  required
                  error={form.errors.sharedContactMapTitle}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactMapTitle}
                    onChange={(event) => {
                      form.setValue('sharedContactMapTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactMapTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-map-preview-title"
                  label="Map preview title"
                  required
                  error={form.errors.sharedContactMapPreviewTitle}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactMapPreviewTitle}
                    onChange={(event) => {
                      form.setValue('sharedContactMapPreviewTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactMapPreviewTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-map-preview-description"
                  label="Map preview description"
                  required
                  error={form.errors.sharedContactMapPreviewDescription}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactMapPreviewDescription}
                    onChange={(event) => {
                      form.setValue('sharedContactMapPreviewDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactMapPreviewDescription').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-map-footer"
                  label="Map footer"
                  required
                  error={form.errors.sharedContactMapFooter}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactMapFooter}
                    onChange={(event) => {
                      form.setValue('sharedContactMapFooter', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactMapFooter').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-contact-map-link-label"
                  label="Map link label"
                  required
                  error={form.errors.sharedContactMapLinkLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedContactMapLinkLabel}
                    onChange={(event) => {
                      form.setValue('sharedContactMapLinkLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedContactMapLinkLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Availability alerts">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="shared-alerts-eyebrow"
                  label="Alerts eyebrow"
                  required
                  error={form.errors.sharedAvailabilityAlertsEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.sharedAvailabilityAlertsEyebrow}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-alerts-title"
                  label="Alerts title"
                  required
                  error={form.errors.sharedAvailabilityAlertsTitle}
                >
                  <input
                    type="text"
                    value={form.values.sharedAvailabilityAlertsTitle}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-alerts-description"
                  label="Alerts description"
                  required
                  error={form.errors.sharedAvailabilityAlertsDescription}
                >
                  <textarea
                    value={form.values.sharedAvailabilityAlertsDescription}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-alerts-email-label"
                  label="Alerts email label"
                  required
                  error={form.errors.sharedAvailabilityAlertsEmailLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedAvailabilityAlertsEmailLabel}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsEmailLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsEmailLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-alerts-email-placeholder"
                  label="Alerts email placeholder"
                  required
                  error={form.errors.sharedAvailabilityAlertsEmailPlaceholder}
                >
                  <input
                    type="text"
                    value={form.values.sharedAvailabilityAlertsEmailPlaceholder}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsEmailPlaceholder', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsEmailPlaceholder').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-alerts-submit-label"
                  label="Alerts submit label"
                  required
                  error={form.errors.sharedAvailabilityAlertsSubmitLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedAvailabilityAlertsSubmitLabel}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsSubmitLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsSubmitLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-alerts-success-message"
                  label="Alerts success message"
                  required
                  error={form.errors.sharedAvailabilityAlertsSuccessMessage}
                >
                  <input
                    type="text"
                    value={form.values.sharedAvailabilityAlertsSuccessMessage}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsSuccessMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsSuccessMessage').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-alerts-error-message"
                  label="Alerts error message"
                  required
                  error={form.errors.sharedAvailabilityAlertsErrorMessage}
                >
                  <input
                    type="text"
                    value={form.values.sharedAvailabilityAlertsErrorMessage}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsErrorMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsErrorMessage').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-alerts-note"
                  label="Alerts note"
                  required
                  error={form.errors.sharedAvailabilityAlertsNote}
                >
                  <textarea
                    value={form.values.sharedAvailabilityAlertsNote}
                    onChange={(event) => {
                      form.setValue('sharedAvailabilityAlertsNote', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedAvailabilityAlertsNote').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Property card labels">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="shared-property-card-photo-fallback"
                  label="Photo fallback"
                  required
                  error={form.errors.sharedPropertyCardPhotoFallback}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardPhotoFallback}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardPhotoFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardPhotoFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-rent-fallback"
                  label="Rent fallback"
                  required
                  error={form.errors.sharedPropertyCardRentFallback}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardRentFallback}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardRentFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardRentFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-availability-fallback"
                  label="Availability fallback"
                  required
                  error={form.errors.sharedPropertyCardAvailabilityFallback}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardAvailabilityFallback}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardAvailabilityFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardAvailabilityFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-divisible-label"
                  label="Divisible label"
                  required
                  error={form.errors.sharedPropertyCardDivisibleLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardDivisibleLabel}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardDivisibleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardDivisibleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-vacancy-label"
                  label="Vacancy label"
                  required
                  error={form.errors.sharedPropertyCardVacancyLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardVacancyLabel}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardVacancyLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardVacancyLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-vacancy-available-label"
                  label="Vacancy available label"
                  required
                  error={form.errors.sharedPropertyCardVacancyAvailableLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardVacancyAvailableLabel}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardVacancyAvailableLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardVacancyAvailableLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-vacancy-leased-label"
                  label="Vacancy leased label"
                  required
                  error={form.errors.sharedPropertyCardVacancyLeasedLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardVacancyLeasedLabel}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardVacancyLeasedLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardVacancyLeasedLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-size-label"
                  label="Size label"
                  required
                  error={form.errors.sharedPropertyCardSizeLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardSizeLabel}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardSizeLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardSizeLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-unit-count-zero-label"
                  label="Unit count zero label"
                  required
                  error={form.errors.sharedPropertyCardUnitCountZeroLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardUnitCountZeroLabel}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardUnitCountZeroLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardUnitCountZeroLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-unit-count-singular-label"
                  label="Unit count singular label"
                  required
                  error={form.errors.sharedPropertyCardUnitCountSingularLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardUnitCountSingularLabel}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardUnitCountSingularLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardUnitCountSingularLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-property-card-unit-count-plural-label"
                  label="Unit count plural label"
                  required
                  error={form.errors.sharedPropertyCardUnitCountPluralLabel}
                  description="Use {count} for the unit count."
                >
                  <input
                    type="text"
                    value={form.values.sharedPropertyCardUnitCountPluralLabel}
                    onChange={(event) => {
                      form.setValue('sharedPropertyCardUnitCountPluralLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedPropertyCardUnitCountPluralLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Unit card labels">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="shared-unit-card-photo-fallback"
                  label="Photo fallback"
                  required
                  error={form.errors.sharedUnitCardPhotoFallback}
                >
                  <input
                    type="text"
                    value={form.values.sharedUnitCardPhotoFallback}
                    onChange={(event) => {
                      form.setValue('sharedUnitCardPhotoFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedUnitCardPhotoFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-unit-card-availability-label"
                  label="Availability label"
                  required
                  error={form.errors.sharedUnitCardAvailabilityLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedUnitCardAvailabilityLabel}
                    onChange={(event) => {
                      form.setValue('sharedUnitCardAvailabilityLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedUnitCardAvailabilityLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-unit-card-availability-fallback"
                  label="Availability fallback"
                  required
                  error={form.errors.sharedUnitCardAvailabilityFallback}
                >
                  <input
                    type="text"
                    value={form.values.sharedUnitCardAvailabilityFallback}
                    onChange={(event) => {
                      form.setValue('sharedUnitCardAvailabilityFallback', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedUnitCardAvailabilityFallback').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-unit-card-floor-label"
                  label="Floor label"
                  required
                  error={form.errors.sharedUnitCardFloorLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedUnitCardFloorLabel}
                    onChange={(event) => {
                      form.setValue('sharedUnitCardFloorLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedUnitCardFloorLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-unit-card-view-details-label"
                  label="View details label"
                  required
                  error={form.errors.sharedUnitCardViewDetailsLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedUnitCardViewDetailsLabel}
                    onChange={(event) => {
                      form.setValue('sharedUnitCardViewDetailsLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedUnitCardViewDetailsLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-unit-card-divisible-label"
                  label="Divisible label"
                  required
                  error={form.errors.sharedUnitCardDivisibleLabel}
                >
                  <input
                    type="text"
                    value={form.values.sharedUnitCardDivisibleLabel}
                    onChange={(event) => {
                      form.setValue('sharedUnitCardDivisibleLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedUnitCardDivisibleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Empty states">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="shared-empty-state-property-grid"
                  label="Property grid empty state"
                  required
                  error={form.errors.sharedEmptyStatePropertyGrid}
                >
                  <textarea
                    value={form.values.sharedEmptyStatePropertyGrid}
                    onChange={(event) => {
                      form.setValue('sharedEmptyStatePropertyGrid', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedEmptyStatePropertyGrid').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="shared-empty-state-unit-group"
                  label="Unit group empty state"
                  required
                  error={form.errors.sharedEmptyStateUnitGroup}
                >
                  <textarea
                    value={form.values.sharedEmptyStateUnitGroup}
                    onChange={(event) => {
                      form.setValue('sharedEmptyStateUnitGroup', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('sharedEmptyStateUnitGroup').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {showCoreSettings ? (
          <SettingsCard
            title="SEO defaults"
            id="settings-seo"
            description="Fallback metadata used when a page does not set its own SEO copy."
          >
            <FormField
              id="seo-default-description"
              label="Default description"
              required
              error={form.errors.seoDefaultDescription}
              description="Used as the fallback meta description for pages that do not set one."
            >
              <textarea
                value={form.values.seoDefaultDescription}
                onChange={(event) => {
                  form.setValue('seoDefaultDescription', event.target.value);
                }}
                onBlur={() => {
                  form.field('seoDefaultDescription').onBlur();
                }}
                rows={3}
                className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
              />
            </FormField>

            <FormField
              id="seo-canonical-origin"
              label="Canonical origin"
              required
              error={form.errors.seoCanonicalOrigin}
              description="Used to build canonical URLs when rendering meta tags."
            >
              <input
                type="url"
                value={form.values.seoCanonicalOrigin}
                onChange={(event) => {
                  form.setValue('seoCanonicalOrigin', event.target.value);
                }}
                onBlur={() => {
                  form.field('seoCanonicalOrigin').onBlur();
                }}
                className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
              />
            </FormField>
          </SettingsCard>
        ) : null}

        {showCoreSettings ? (
          <SettingsCard
            title="Navigation"
            id="settings-navigation"
            description="Header links and mobile menu structure for the public site."
          >
            <NavigationLinksFieldGroup
              title="Header links"
              description="These items appear in the public site header. Drag and drop to reorder or create dropdowns with custom or property links."
              items={form.values.navigationLinks}
              propertyOptions={propertyOptions}
              propertiesAreLoading={propertiesAreLoading}
              propertiesFetchFailed={propertiesFetchFailed}
              error={form.errors.navigationLinks}
              onChange={(next) => {
                form.setValue('navigationLinks', next);
              }}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="navigation-mobile-title"
                label="Mobile menu title"
                required
                error={form.errors.navigationMobileTitle}
              >
                <input
                  type="text"
                  value={form.values.navigationMobileTitle}
                  onChange={(event) => {
                    form.setValue('navigationMobileTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('navigationMobileTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <FormField
                id="navigation-mobile-description"
                label="Mobile menu description"
                required
                error={form.errors.navigationMobileDescription}
              >
                <input
                  type="text"
                  value={form.values.navigationMobileDescription}
                  onChange={(event) => {
                    form.setValue('navigationMobileDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('navigationMobileDescription').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="navigation-shortcut-heading"
                label="Shortcut heading"
                required
                error={form.errors.navigationMobileShortcutHeading}
              >
                <input
                  type="text"
                  value={form.values.navigationMobileShortcutHeading}
                  onChange={(event) => {
                    form.setValue('navigationMobileShortcutHeading', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('navigationMobileShortcutHeading').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <FormField
                id="navigation-shortcut-description"
                label="Shortcut description"
                required
                error={form.errors.navigationMobileShortcutDescription}
              >
                <input
                  type="text"
                  value={form.values.navigationMobileShortcutDescription}
                  onChange={(event) => {
                    form.setValue('navigationMobileShortcutDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('navigationMobileShortcutDescription').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="navigation-shortcut-primary-label"
                label="Primary shortcut label"
                required
                error={form.errors.navigationMobilePrimaryLabel}
              >
                <input
                  type="text"
                  value={form.values.navigationMobilePrimaryLabel}
                  onChange={(event) => {
                    form.setValue('navigationMobilePrimaryLabel', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('navigationMobilePrimaryLabel').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <FormField
                id="navigation-shortcut-primary-href"
                label="Primary shortcut link"
                required
                error={form.errors.navigationMobilePrimaryHref}
              >
                <input
                  type="text"
                  value={form.values.navigationMobilePrimaryHref}
                  onChange={(event) => {
                    form.setValue('navigationMobilePrimaryHref', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('navigationMobilePrimaryHref').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="navigation-shortcut-secondary-label"
                label="Secondary shortcut label"
                required
                error={form.errors.navigationMobileSecondaryLabel}
              >
                <input
                  type="text"
                  value={form.values.navigationMobileSecondaryLabel}
                  onChange={(event) => {
                    form.setValue('navigationMobileSecondaryLabel', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('navigationMobileSecondaryLabel').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <FormField
                id="navigation-shortcut-secondary-href"
                label="Secondary shortcut link"
                required
                error={form.errors.navigationMobileSecondaryHref}
              >
                <input
                  type="text"
                  value={form.values.navigationMobileSecondaryHref}
                  onChange={(event) => {
                    form.setValue('navigationMobileSecondaryHref', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('navigationMobileSecondaryHref').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>
          </SettingsCard>
        ) : null}

        {showCoreSettings ? (
          <SettingsCard
            title="Footer"
            id="settings-footer"
            description="Company and legal text shown in the footer on all pages."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="footer-company-name"
                label="Company name"
                required
                error={form.errors.footerCompanyName}
              >
                <input
                  type="text"
                  value={form.values.footerCompanyName}
                  onChange={(event) => {
                    form.setValue('footerCompanyName', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('footerCompanyName').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <FormField
                id="footer-copyright"
                label="Copyright text"
                required
                error={form.errors.footerCopyrightText}
                description="Use {year} as a placeholder for the current year."
              >
                <input
                  type="text"
                  value={form.values.footerCopyrightText}
                  onChange={(event) => {
                    form.setValue('footerCopyrightText', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('footerCopyrightText').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>
          </SettingsCard>
        ) : null}

        {showCoreSettings ? (
          <SettingsCard
            title="Booking & Contact"
            id="settings-contact"
            description="Primary contact details used in contact sections and showing requests."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="booking-email"
                label="Universal booking email"
                required
                error={form.errors.bookingEmail}
              >
                <input
                  type="email"
                  value={form.values.bookingEmail}
                  onChange={(event) => {
                    form.setValue('bookingEmail', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('bookingEmail').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-email"
                label="General contact email"
                required
                error={form.errors.contactEmail}
              >
                <input
                  type="email"
                  value={form.values.contactEmail}
                  onChange={(event) => {
                    form.setValue('contactEmail', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactEmail').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="contact-phone"
                label="Contact phone"
                required
                error={form.errors.contactPhone}
              >
                <input
                  type="tel"
                  value={form.values.contactPhone}
                  onChange={(event) => {
                    form.setValue('contactPhone', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactPhone').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="map-url"
                label="Map embed URL"
                required
                error={form.errors.mapEmbedUrl}
              >
                <input
                  type="url"
                  value={form.values.mapEmbedUrl}
                  onChange={(event) => {
                    form.setValue('mapEmbedUrl', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('mapEmbedUrl').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="address-line1"
                label="Address line 1"
                required
                error={form.errors.contactAddressLine1}
              >
                <input
                  type="text"
                  value={form.values.contactAddressLine1}
                  onChange={(event) => {
                    form.setValue('contactAddressLine1', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactAddressLine1').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField id="address-line2" label="Address line 2">
                <input
                  type="text"
                  value={form.values.contactAddressLine2}
                  onChange={(event) => {
                    form.setValue('contactAddressLine2', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('contactAddressLine2').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </div>

            <ArrayFieldGroup
              title="Office hours"
              description="Displayed on the contact section across public pages."
              error={form.errors.contactHours}
              items={form.values.contactHours}
              onChange={(next) => {
                form.setValue('contactHours', next);
              }}
            />

            <ArrayFieldGroup
              title="Leadership contacts"
              description="Shown under contact details for credibility."
              error={form.errors.leadershipContacts}
              items={form.values.leadershipContacts}
              onChange={(next) => {
                form.setValue('leadershipContacts', next);
              }}
              type="contact"
            />
          </SettingsCard>
        ) : null}

        {showPage('home') ? (
          <SettingsCard
            title="Home page"
            id="settings-home"
            description="Homepage hero, availability callouts, and contact section copy."
          >
            <SettingsGroup title="SEO">
              <FormField
                id="home-seo-title"
                label="SEO title"
                required
                error={form.errors.homeSeoTitle}
              >
                <input
                  type="text"
                  value={form.values.homeSeoTitle}
                  onChange={(event) => {
                    form.setValue('homeSeoTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('homeSeoTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="home-seo-description"
                label="SEO description"
                required
                error={form.errors.homeSeoDescription}
              >
                <textarea
                  value={form.values.homeSeoDescription}
                  onChange={(event) => {
                    form.setValue('homeSeoDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('homeSeoDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Hero copy">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="home-hero-eyebrow"
                  label="Hero eyebrow"
                  required
                  error={form.errors.homeHeroEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.homeHeroEyebrow}
                    onChange={(event) => {
                      form.setValue('homeHeroEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeHeroEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="home-hero-title"
                  label="Hero title"
                  required
                  error={form.errors.homeHeroTitle}
                >
                  <input
                    type="text"
                    value={form.values.homeHeroTitle}
                    onChange={(event) => {
                      form.setValue('homeHeroTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeHeroTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="home-hero-description"
                label="Hero description"
                required
                error={form.errors.homeHeroDescription}
              >
                <textarea
                  value={form.values.homeHeroDescription}
                  onChange={(event) => {
                    form.setValue('homeHeroDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('homeHeroDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Hero media">
              <FormField
                id="home-hero-media-file"
                label="Hero media file"
                description={`Upload a video or photo. Supported: JPG, PNG, WebP, HEIC, MP4, WebM. Max ${heroMediaMaxSizeGb.toString()}GB (Supabase limit).`}
                error={heroMediaUploadError ?? form.errors.homeHeroMediaUrl}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="flex h-32 w-48 items-center justify-center overflow-hidden rounded-xl border border-surface-subtle bg-surface-subtle/60">
                      {heroMediaUrl.length > 0 ? (
                        heroMediaPreviewType === 'video' ? (
                          <video
                            src={heroMediaUrl}
                            className="h-full w-full object-cover"
                            controls
                            preload="metadata"
                            playsInline
                            aria-label={form.values.homeHeroMediaAriaLabel}
                          />
                        ) : (
                          <img
                            src={heroMediaUrl}
                            alt={form.values.homeHeroMediaAriaLabel}
                            className="h-full w-full object-cover"
                          />
                        )
                      ) : (
                        <div className="h-full w-full" aria-hidden="true" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="inline-flex items-center justify-center rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-brand-primary transition focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-primary/40 hover:bg-brand-primary/5">
                        <input
                          type="file"
                          accept={heroMediaAcceptTypes}
                          className="sr-only"
                          onChange={handleHeroMediaFileChange}
                          disabled={!canInteractWithHeroMedia}
                        />
                        {isHeroMediaUploading
                          ? 'Uploading…'
                          : heroMediaUrl.length > 0
                            ? 'Replace media'
                            : 'Upload media'}
                      </label>
                      {heroMediaUrl.length > 0 ? (
                        <button
                          type="button"
                          onClick={handleHeroMediaRemove}
                          disabled={!canInteractWithHeroMedia}
                          className="text-sm font-semibold text-red-700 underline-offset-4 transition hover:underline disabled:opacity-60"
                        >
                          Remove media
                        </button>
                      ) : null}
                      <p className="text-xs text-text-primary/70">
                        {heroMediaUrl.length > 0
                          ? `Current media: ${heroMediaPreviewLabel}.`
                          : 'Using the default hero media until you upload a file.'}
                      </p>
                    </div>
                  </div>
                  {shouldShowHeroMediaProgress ? (
                    <div className="flex items-center gap-3">
                      <div
                        role="progressbar"
                        aria-label="Hero media upload progress"
                        aria-valuenow={heroMediaProgressValue}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        className="h-2 w-full overflow-hidden rounded-full bg-surface-subtle"
                      >
                        <div
                          className="h-full bg-brand-primary transition-[width] duration-200"
                          style={{ width: `${heroMediaProgressValue.toString()}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-text-primary/70">
                        {heroMediaProgressValue.toString()}%
                      </span>
                    </div>
                  ) : null}
                </div>
              </FormField>
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  id="home-hero-media-label"
                  label="Media label"
                  required
                  error={form.errors.homeHeroMediaLabel}
                >
                  <input
                    type="text"
                    value={form.values.homeHeroMediaLabel}
                    onChange={(event) => {
                      form.setValue('homeHeroMediaLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeHeroMediaLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="home-hero-media-location"
                  label="Media location"
                  required
                  error={form.errors.homeHeroMediaLocation}
                >
                  <input
                    type="text"
                    value={form.values.homeHeroMediaLocation}
                    onChange={(event) => {
                      form.setValue('homeHeroMediaLocation', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeHeroMediaLocation').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="home-hero-media-aria"
                  label="Media aria label"
                  required
                  error={form.errors.homeHeroMediaAriaLabel}
                >
                  <input
                    type="text"
                    value={form.values.homeHeroMediaAriaLabel}
                    onChange={(event) => {
                      form.setValue('homeHeroMediaAriaLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeHeroMediaAriaLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Availability section">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="home-availability-eyebrow"
                  label="Availability eyebrow"
                  required
                  error={form.errors.homeAvailabilityEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.homeAvailabilityEyebrow}
                    onChange={(event) => {
                      form.setValue('homeAvailabilityEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeAvailabilityEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="home-availability-title"
                  label="Availability title"
                  required
                  error={form.errors.homeAvailabilityTitle}
                >
                  <input
                    type="text"
                    value={form.values.homeAvailabilityTitle}
                    onChange={(event) => {
                      form.setValue('homeAvailabilityTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeAvailabilityTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>

              <FormField
                id="home-availability-description"
                label="Availability description"
                required
                error={form.errors.homeAvailabilityDescription}
              >
                <textarea
                  value={form.values.homeAvailabilityDescription}
                  onChange={(event) => {
                    form.setValue('homeAvailabilityDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('homeAvailabilityDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="home-cta-label"
                  label="Availability CTA label"
                  required
                  error={form.errors.homeAvailabilityCtaLabel}
                >
                  <input
                    type="text"
                    value={form.values.homeAvailabilityCtaLabel}
                    onChange={(event) => {
                      form.setValue('homeAvailabilityCtaLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeAvailabilityCtaLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="home-cta-href"
                  label="Availability CTA link"
                  required
                  error={form.errors.homeAvailabilityCtaHref}
                >
                  <input
                    type="text"
                    value={form.values.homeAvailabilityCtaHref}
                    onChange={(event) => {
                      form.setValue('homeAvailabilityCtaHref', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeAvailabilityCtaHref').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Availability messaging">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="home-availability-empty-message"
                  label="Empty state message"
                  required
                  error={form.errors.homeAvailabilityEmptyMessage}
                >
                  <textarea
                    value={form.values.homeAvailabilityEmptyMessage}
                    onChange={(event) => {
                      form.setValue('homeAvailabilityEmptyMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeAvailabilityEmptyMessage').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="home-availability-error-message"
                  label="Error message"
                  required
                  error={form.errors.homeAvailabilityErrorMessage}
                >
                  <textarea
                    value={form.values.homeAvailabilityErrorMessage}
                    onChange={(event) => {
                      form.setValue('homeAvailabilityErrorMessage', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('homeAvailabilityErrorMessage').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Contact section">
              <FormField
                id="home-contact-heading"
                label="Contact heading"
                required
                error={form.errors.homeContactHeading}
              >
                <input
                  type="text"
                  value={form.values.homeContactHeading}
                  onChange={(event) => {
                    form.setValue('homeContactHeading', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('homeContactHeading').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="home-contact-description"
                label="Contact description"
                required
                error={form.errors.homeContactDescription}
              >
                <textarea
                  value={form.values.homeContactDescription}
                  onChange={(event) => {
                    form.setValue('homeContactDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('homeContactDescription').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {showPage('lead') ? (
          <SettingsCard
            title="Lead capture"
            id="settings-lead"
            description="Campaign landing copy, lead form prompts, and thank-you messaging."
          >
            <SettingsGroup title="SEO">
              <FormField
                id="lead-seo-title"
                label="SEO title"
                required
                error={form.errors.leadSeoTitle}
              >
                <input
                  type="text"
                  value={form.values.leadSeoTitle}
                  onChange={(event) => {
                    form.setValue('leadSeoTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('leadSeoTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="lead-seo-description"
                label="SEO description"
                required
                error={form.errors.leadSeoDescription}
              >
                <textarea
                  value={form.values.leadSeoDescription}
                  onChange={(event) => {
                    form.setValue('leadSeoDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('leadSeoDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Header">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="lead-eyebrow"
                  label="Eyebrow"
                  required
                  error={form.errors.leadCaptureEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.leadCaptureEyebrow}
                    onChange={(event) => {
                      form.setValue('leadCaptureEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadCaptureEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-title"
                  label="Title"
                  required
                  error={form.errors.leadCaptureTitle}
                >
                  <input
                    type="text"
                    value={form.values.leadCaptureTitle}
                    onChange={(event) => {
                      form.setValue('leadCaptureTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadCaptureTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>

              <FormField
                id="lead-description"
                label="Description"
                required
                error={form.errors.leadCaptureDescription}
              >
                <textarea
                  value={form.values.leadCaptureDescription}
                  onChange={(event) => {
                    form.setValue('leadCaptureDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('leadCaptureDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Benefits">
              <FormField
                id="lead-benefits-heading"
                label="Benefits heading"
                required
                error={form.errors.leadCaptureBenefitsHeading}
              >
                <input
                  type="text"
                  value={form.values.leadCaptureBenefitsHeading}
                  onChange={(event) => {
                    form.setValue('leadCaptureBenefitsHeading', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('leadCaptureBenefitsHeading').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>

              <TitleDescriptionFieldGroup
                title="Benefit list"
                description="Use short benefit titles with 1-2 sentence descriptions."
                items={form.values.leadCaptureBenefits}
                error={form.errors.leadCaptureBenefits}
                onChange={(next) => {
                  form.setValue('leadCaptureBenefits', next);
                }}
                addLabel="Add benefit"
              />
            </SettingsGroup>

            <SettingsGroup title="Stats">
              <ValueLabelFieldGroup
                title="Stats list"
                description="Short value + supporting label."
                items={form.values.leadCaptureStats}
                error={form.errors.leadCaptureStats}
                onChange={(next) => {
                  form.setValue('leadCaptureStats', next);
                }}
                addLabel="Add stat"
              />
            </SettingsGroup>

            <SettingsGroup title="Form labels">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="lead-form-full-name"
                  label="Full name label"
                  required
                  error={form.errors.leadFormFullNameLabel}
                >
                  <input
                    type="text"
                    value={form.values.leadFormFullNameLabel}
                    onChange={(event) => {
                      form.setValue('leadFormFullNameLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormFullNameLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-form-email"
                  label="Email label"
                  required
                  error={form.errors.leadFormEmailLabel}
                >
                  <input
                    type="text"
                    value={form.values.leadFormEmailLabel}
                    onChange={(event) => {
                      form.setValue('leadFormEmailLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormEmailLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-form-phone"
                  label="Phone label"
                  required
                  error={form.errors.leadFormPhoneLabel}
                >
                  <input
                    type="text"
                    value={form.values.leadFormPhoneLabel}
                    onChange={(event) => {
                      form.setValue('leadFormPhoneLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormPhoneLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-form-budget"
                  label="Budget label"
                  required
                  error={form.errors.leadFormBudgetLabel}
                >
                  <input
                    type="text"
                    value={form.values.leadFormBudgetLabel}
                    onChange={(event) => {
                      form.setValue('leadFormBudgetLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormBudgetLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-form-move-in"
                  label="Move-in label"
                  required
                  error={form.errors.leadFormMoveInLabel}
                >
                  <input
                    type="text"
                    value={form.values.leadFormMoveInLabel}
                    onChange={(event) => {
                      form.setValue('leadFormMoveInLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormMoveInLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-form-notes"
                  label="Notes label"
                  required
                  error={form.errors.leadFormNotesLabel}
                >
                  <input
                    type="text"
                    value={form.values.leadFormNotesLabel}
                    onChange={(event) => {
                      form.setValue('leadFormNotesLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormNotesLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-form-submit"
                  label="Submit label"
                  required
                  error={form.errors.leadFormSubmitLabel}
                >
                  <input
                    type="text"
                    value={form.values.leadFormSubmitLabel}
                    onChange={(event) => {
                      form.setValue('leadFormSubmitLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormSubmitLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-form-success"
                  label="Success toast"
                  required
                  error={form.errors.leadFormSuccessToast}
                >
                  <input
                    type="text"
                    value={form.values.leadFormSuccessToast}
                    onChange={(event) => {
                      form.setValue('leadFormSuccessToast', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormSuccessToast').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="lead-form-error"
                  label="Error toast"
                  required
                  error={form.errors.leadFormErrorToast}
                >
                  <input
                    type="text"
                    value={form.values.leadFormErrorToast}
                    onChange={(event) => {
                      form.setValue('leadFormErrorToast', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('leadFormErrorToast').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="lead-form-consent"
                label="Consent text"
                required
                error={form.errors.leadFormConsentText}
              >
                <textarea
                  value={form.values.leadFormConsentText}
                  onChange={(event) => {
                    form.setValue('leadFormConsentText', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('leadFormConsentText').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Thank you message">
              <FormField
                id="lead-thankyou-title"
                label="Thank you title"
                required
                error={form.errors.leadCaptureThankYouTitle}
              >
                <input
                  type="text"
                  value={form.values.leadCaptureThankYouTitle}
                  onChange={(event) => {
                    form.setValue('leadCaptureThankYouTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('leadCaptureThankYouTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="lead-thankyou"
                label="Thank you message"
                required
                error={form.errors.leadCaptureThankYou}
              >
                <textarea
                  value={form.values.leadCaptureThankYou}
                  onChange={(event) => {
                    form.setValue('leadCaptureThankYou', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('leadCaptureThankYou').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {showPage('about') ? (
          <SettingsCard
            title="About page"
            id="settings-about"
            description="Company story, values, and portfolio highlights."
          >
            <SettingsGroup title="SEO">
              <FormField
                id="about-seo-title"
                label="SEO title"
                required
                error={form.errors.aboutSeoTitle}
              >
                <input
                  type="text"
                  value={form.values.aboutSeoTitle}
                  onChange={(event) => {
                    form.setValue('aboutSeoTitle', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutSeoTitle').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="about-seo-description"
                label="SEO description"
                required
                error={form.errors.aboutSeoDescription}
              >
                <textarea
                  value={form.values.aboutSeoDescription}
                  onChange={(event) => {
                    form.setValue('aboutSeoDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutSeoDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Hero">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-hero-title"
                  label="Hero title"
                  required
                  error={form.errors.aboutHeroTitle}
                >
                  <input
                    type="text"
                    value={form.values.aboutHeroTitle}
                    onChange={(event) => {
                      form.setValue('aboutHeroTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutHeroTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-hero-subtitle"
                  label="Hero subtitle"
                  required
                  error={form.errors.aboutHeroSubtitle}
                >
                  <textarea
                    value={form.values.aboutHeroSubtitle}
                    onChange={(event) => {
                      form.setValue('aboutHeroSubtitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutHeroSubtitle').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-tagline"
                  label="Tagline"
                  required
                  error={form.errors.aboutTagline}
                >
                  <input
                    type="text"
                    value={form.values.aboutTagline}
                    onChange={(event) => {
                      form.setValue('aboutTagline', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutTagline').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-hero-primary-cta-label"
                  label="Primary CTA label"
                  required
                  error={form.errors.aboutHeroPrimaryCtaLabel}
                >
                  <input
                    type="text"
                    value={form.values.aboutHeroPrimaryCtaLabel}
                    onChange={(event) => {
                      form.setValue('aboutHeroPrimaryCtaLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutHeroPrimaryCtaLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-hero-primary-cta-href"
                  label="Primary CTA link"
                  required
                  error={form.errors.aboutHeroPrimaryCtaHref}
                >
                  <input
                    type="text"
                    value={form.values.aboutHeroPrimaryCtaHref}
                    onChange={(event) => {
                      form.setValue('aboutHeroPrimaryCtaHref', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutHeroPrimaryCtaHref').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-hero-secondary-cta-label"
                  label="Secondary CTA label"
                  required
                  error={form.errors.aboutHeroSecondaryCtaLabel}
                >
                  <input
                    type="text"
                    value={form.values.aboutHeroSecondaryCtaLabel}
                    onChange={(event) => {
                      form.setValue('aboutHeroSecondaryCtaLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutHeroSecondaryCtaLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-hero-secondary-cta-href"
                  label="Secondary CTA link"
                  required
                  error={form.errors.aboutHeroSecondaryCtaHref}
                >
                  <input
                    type="text"
                    value={form.values.aboutHeroSecondaryCtaHref}
                    onChange={(event) => {
                      form.setValue('aboutHeroSecondaryCtaHref', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutHeroSecondaryCtaHref').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Highlight card">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-highlight-title"
                  label="Highlight title"
                  required
                  error={form.errors.aboutHighlightTitle}
                >
                  <input
                    type="text"
                    value={form.values.aboutHighlightTitle}
                    onChange={(event) => {
                      form.setValue('aboutHighlightTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutHighlightTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-highlight-description"
                  label="Highlight description"
                  required
                  error={form.errors.aboutHighlightDescription}
                >
                  <textarea
                    value={form.values.aboutHighlightDescription}
                    onChange={(event) => {
                      form.setValue('aboutHighlightDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutHighlightDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="about-featured-properties"
                label="Featured properties (one per line)"
                required
                error={form.errors.aboutFeaturedProperties}
              >
                <textarea
                  value={form.values.aboutFeaturedProperties}
                  onChange={(event) => {
                    form.setValue('aboutFeaturedProperties', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutFeaturedProperties').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>

            <SettingsGroup title="Snapshot">
              <FormField
                id="about-snapshot-eyebrow"
                label="Snapshot eyebrow"
                required
                error={form.errors.aboutSnapshotEyebrow}
              >
                <input
                  type="text"
                  value={form.values.aboutSnapshotEyebrow}
                  onChange={(event) => {
                    form.setValue('aboutSnapshotEyebrow', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutSnapshotEyebrow').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <LabelValueFieldGroup
                title="Snapshot stats"
                description="Label + value pairs shown in the hero panel."
                items={form.values.aboutSnapshotStats}
                error={form.errors.aboutSnapshotStats}
                onChange={(next) => {
                  form.setValue('aboutSnapshotStats', next);
                }}
                addLabel="Add stat"
              />
            </SettingsGroup>

            <SettingsGroup title="Body copy">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-body-heading"
                  label="Body heading"
                  required
                  error={form.errors.aboutBodyHeading}
                >
                  <input
                    type="text"
                    value={form.values.aboutBodyHeading}
                    onChange={(event) => {
                      form.setValue('aboutBodyHeading', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutBodyHeading').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-body-description"
                  label="Body description"
                  required
                  error={form.errors.aboutBodyDescription}
                >
                  <textarea
                    value={form.values.aboutBodyDescription}
                    onChange={(event) => {
                      form.setValue('aboutBodyDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutBodyDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="about-paragraphs"
                label="About paragraphs (separate with blank lines)"
                required
                error={form.errors.aboutParagraphs}
              >
                <textarea
                  value={form.values.aboutParagraphs}
                  onChange={(event) => {
                    form.setValue('aboutParagraphs', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutParagraphs').onBlur();
                  }}
                  rows={6}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-details-summary-label"
                  label="Details summary label"
                  required
                  error={form.errors.aboutDetailsSummaryLabel}
                >
                  <input
                    type="text"
                    value={form.values.aboutDetailsSummaryLabel}
                    onChange={(event) => {
                      form.setValue('aboutDetailsSummaryLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutDetailsSummaryLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-promise-label"
                  label="Promise label"
                  required
                  error={form.errors.aboutPromiseLabel}
                >
                  <input
                    type="text"
                    value={form.values.aboutPromiseLabel}
                    onChange={(event) => {
                      form.setValue('aboutPromiseLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPromiseLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Values section">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-values-title"
                  label="Values title"
                  required
                  error={form.errors.aboutValuesTitle}
                >
                  <input
                    type="text"
                    value={form.values.aboutValuesTitle}
                    onChange={(event) => {
                      form.setValue('aboutValuesTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutValuesTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-values-description"
                  label="Values description"
                  required
                  error={form.errors.aboutValuesDescription}
                >
                  <textarea
                    value={form.values.aboutValuesDescription}
                    onChange={(event) => {
                      form.setValue('aboutValuesDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutValuesDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-values-cta-label"
                  label="Values CTA label"
                  required
                  error={form.errors.aboutValuesCtaLabel}
                >
                  <input
                    type="text"
                    value={form.values.aboutValuesCtaLabel}
                    onChange={(event) => {
                      form.setValue('aboutValuesCtaLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutValuesCtaLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-values-cta-href"
                  label="Values CTA link"
                  required
                  error={form.errors.aboutValuesCtaHref}
                >
                  <input
                    type="text"
                    value={form.values.aboutValuesCtaHref}
                    onChange={(event) => {
                      form.setValue('aboutValuesCtaHref', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutValuesCtaHref').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <TitleDescriptionFieldGroup
                title="Values pillars"
                description="Each pillar includes a short title and supporting description."
                items={form.values.aboutValuesPillars}
                error={form.errors.aboutValuesPillars}
                onChange={(next) => {
                  form.setValue('aboutValuesPillars', next);
                }}
                addLabel="Add pillar"
              />
            </SettingsGroup>

            <SettingsGroup title="Portfolio overview">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-portfolio-title"
                  label="Portfolio title"
                  required
                  error={form.errors.aboutPortfolioTitle}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioTitle}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-description"
                  label="Portfolio description"
                  required
                  error={form.errors.aboutPortfolioDescription}
                >
                  <textarea
                    value={form.values.aboutPortfolioDescription}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioDescription', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioDescription').onBlur();
                    }}
                    rows={2}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-cta-label"
                  label="Portfolio CTA label"
                  required
                  error={form.errors.aboutPortfolioCtaLabel}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioCtaLabel}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioCtaLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioCtaLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-cta-href"
                  label="Portfolio CTA link"
                  required
                  error={form.errors.aboutPortfolioCtaHref}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioCtaHref}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioCtaHref', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioCtaHref').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Portfolio: Flagship property">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-portfolio-flagship-eyebrow"
                  label="Flagship eyebrow"
                  required
                  error={form.errors.aboutPortfolioFlagshipEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioFlagshipEyebrow}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioFlagshipEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioFlagshipEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-flagship-title"
                  label="Flagship title"
                  required
                  error={form.errors.aboutPortfolioFlagshipTitle}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioFlagshipTitle}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioFlagshipTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioFlagshipTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="about-portfolio-flagship-description"
                label="Flagship description"
                required
                error={form.errors.aboutPortfolioFlagshipDescription}
              >
                <textarea
                  value={form.values.aboutPortfolioFlagshipDescription}
                  onChange={(event) => {
                    form.setValue('aboutPortfolioFlagshipDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutPortfolioFlagshipDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <LabelValueFieldGroup
                title="Flagship stats"
                description="Label + value pairs shown in the flagship panel."
                items={form.values.aboutPortfolioFlagshipStats}
                error={form.errors.aboutPortfolioFlagshipStats}
                onChange={(next) => {
                  form.setValue('aboutPortfolioFlagshipStats', next);
                }}
                addLabel="Add stat"
              />
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-portfolio-flagship-features-heading"
                  label="Features heading"
                  required
                  error={form.errors.aboutPortfolioFlagshipFeaturesHeading}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioFlagshipFeaturesHeading}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioFlagshipFeaturesHeading', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioFlagshipFeaturesHeading').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-flagship-features-toggle"
                  label="Features toggle label"
                  required
                  error={form.errors.aboutPortfolioFlagshipFeaturesToggleLabel}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioFlagshipFeaturesToggleLabel}
                    onChange={(event) => {
                      form.setValue(
                        'aboutPortfolioFlagshipFeaturesToggleLabel',
                        event.target.value,
                      );
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioFlagshipFeaturesToggleLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <StringListFieldGroup
                title="Flagship features"
                description="List the features displayed in the accordion."
                items={form.values.aboutPortfolioFlagshipFeatures}
                error={form.errors.aboutPortfolioFlagshipFeatures}
                onChange={(next) => {
                  form.setValue('aboutPortfolioFlagshipFeatures', next);
                }}
                addLabel="Add feature"
              />
            </SettingsGroup>

            <SettingsGroup title="Portfolio: Downtown West">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-portfolio-downtown-eyebrow"
                  label="Downtown eyebrow"
                  required
                  error={form.errors.aboutPortfolioDowntownEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioDowntownEyebrow}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioDowntownEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioDowntownEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-downtown-title"
                  label="Downtown title"
                  required
                  error={form.errors.aboutPortfolioDowntownTitle}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioDowntownTitle}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioDowntownTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioDowntownTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="about-portfolio-downtown-description"
                label="Downtown description"
                required
                error={form.errors.aboutPortfolioDowntownDescription}
              >
                <textarea
                  value={form.values.aboutPortfolioDowntownDescription}
                  onChange={(event) => {
                    form.setValue('aboutPortfolioDowntownDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutPortfolioDowntownDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-portfolio-downtown-ideal-heading"
                  label="Ideal heading"
                  required
                  error={form.errors.aboutPortfolioDowntownIdealHeading}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioDowntownIdealHeading}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioDowntownIdealHeading', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioDowntownIdealHeading').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-downtown-addresses-heading"
                  label="Addresses heading"
                  required
                  error={form.errors.aboutPortfolioDowntownAddressesHeading}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioDowntownAddressesHeading}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioDowntownAddressesHeading', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioDowntownAddressesHeading').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <StringListFieldGroup
                title="Ideal tenants"
                description="List the ideal tenant profiles for downtown assets."
                items={form.values.aboutPortfolioDowntownTenants}
                error={form.errors.aboutPortfolioDowntownTenants}
                onChange={(next) => {
                  form.setValue('aboutPortfolioDowntownTenants', next);
                }}
                addLabel="Add tenant"
              />
            </SettingsGroup>

            <SettingsGroup title="Portfolio: Retail">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-portfolio-retail-eyebrow"
                  label="Retail eyebrow"
                  required
                  error={form.errors.aboutPortfolioRetailEyebrow}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioRetailEyebrow}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioRetailEyebrow', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioRetailEyebrow').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-retail-title"
                  label="Retail title"
                  required
                  error={form.errors.aboutPortfolioRetailTitle}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioRetailTitle}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioRetailTitle', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioRetailTitle').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
              <FormField
                id="about-portfolio-retail-description"
                label="Retail description"
                required
                error={form.errors.aboutPortfolioRetailDescription}
              >
                <textarea
                  value={form.values.aboutPortfolioRetailDescription}
                  onChange={(event) => {
                    form.setValue('aboutPortfolioRetailDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutPortfolioRetailDescription').onBlur();
                  }}
                  rows={3}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <StringListFieldGroup
                title="Retail plazas"
                description="List each plaza or property line."
                items={form.values.aboutPortfolioRetailPlazas}
                error={form.errors.aboutPortfolioRetailPlazas}
                onChange={(next) => {
                  form.setValue('aboutPortfolioRetailPlazas', next);
                }}
                addLabel="Add plaza"
              />
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="about-portfolio-retail-footer"
                  label="Retail footer"
                  required
                  error={form.errors.aboutPortfolioRetailFooter}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioRetailFooter}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioRetailFooter', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioRetailFooter').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
                <FormField
                  id="about-portfolio-retail-footer-link-label"
                  label="Retail footer link label"
                  required
                  error={form.errors.aboutPortfolioRetailFooterLinkLabel}
                >
                  <input
                    type="text"
                    value={form.values.aboutPortfolioRetailFooterLinkLabel}
                    onChange={(event) => {
                      form.setValue('aboutPortfolioRetailFooterLinkLabel', event.target.value);
                    }}
                    onBlur={() => {
                      form.field('aboutPortfolioRetailFooterLinkLabel').onBlur();
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </FormField>
              </div>
            </SettingsGroup>

            <SettingsGroup title="Contact section">
              <FormField
                id="about-contact-heading"
                label="Contact heading"
                required
                error={form.errors.aboutContactHeading}
              >
                <input
                  type="text"
                  value={form.values.aboutContactHeading}
                  onChange={(event) => {
                    form.setValue('aboutContactHeading', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutContactHeading').onBlur();
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
              <FormField
                id="about-contact-description"
                label="Contact description"
                required
                error={form.errors.aboutContactDescription}
              >
                <textarea
                  value={form.values.aboutContactDescription}
                  onChange={(event) => {
                    form.setValue('aboutContactDescription', event.target.value);
                  }}
                  onBlur={() => {
                    form.field('aboutContactDescription').onBlur();
                  }}
                  rows={2}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </FormField>
            </SettingsGroup>
          </SettingsCard>
        ) : null}

        {hasSubmitError ? (
          <FormMessage tone="error" role="alert">
            {form.submitError}
          </FormMessage>
        ) : null}
        {form.submitSuccess ? <FormMessage tone="success">Settings saved</FormMessage> : null}
        {showActionBar ? (
          <div className="sticky bottom-4 z-10">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-surface-subtle bg-white/95 p-4 shadow-card backdrop-blur">
              <p className="text-sm text-text-primary/70">{actionMessage}</p>
              <FormActions justify="end">
                <button
                  type="button"
                  onClick={handleDiscard}
                  disabled={!form.isDirty}
                  className="inline-flex items-center justify-center rounded-xl border border-surface-subtle bg-surface-light px-4 py-2.5 text-base font-semibold text-text-primary/70 transition hover:border-brand-primary/40 hover:bg-brand-primary/5 disabled:cursor-not-allowed disabled:opacity-60 md:py-2 md:text-sm"
                >
                  Discard changes
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-xl bg-brand-primary px-4 py-2.5 text-base font-semibold text-white shadow-md transition hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-60 md:py-2 md:text-sm"
                  disabled={!canSubmit}
                >
                  {form.isSubmitting || updateMutation.isPending ? 'Saving…' : 'Save settings'}
                </button>
              </FormActions>
            </div>
          </div>
        ) : null}
      </Form>
    </section>
  );
}

export function SiteSettings(): JSX.Element {
  return <SiteSettingsView mode="core" />;
}

export function PageSettings(): JSX.Element {
  const { pageId } = useParams<{ pageId?: string }>();
  const resolvedPageId = resolvePageSettingsId(pageId);

  if (!resolvedPageId) {
    return <Navigate to="/dashboard/page-settings/home" replace />;
  }

  return <SiteSettingsView mode="page" activePageId={resolvedPageId} />;
}

function mapSettingsToForm(
  settings: SiteSettings,
  {
    seededProperties = [],
  }: {
    seededProperties?: PublicPropertySummary[];
  } = {},
): SiteSettingsFormValues {
  const siteName = settings.brand.siteName.trim();
  const tagline = settings.brand.tagline.trim();
  const defaultDescription = settings.seo.defaultDescription.trim();
  const canonicalOrigin = settings.seo.canonicalOrigin.trim();

  const navigationLinks =
    settings.navigation.links.length > 0
      ? [...settings.navigation.links]
          .sort((a, b) => a.order - b.order)
          .map((entry) => {
            const children = [...(entry.children ?? [])]
              .sort((a, b) => a.order - b.order)
              .map((child) =>
                createNavigationChild({
                  label: child.label,
                  href: child.href,
                  propertyId: child.propertyId,
                  enabled: child.enabled,
                  external: child.external,
                }),
              );

            return createNavigationItem({
              label: entry.label,
              href: entry.href,
              enabled: entry.enabled,
              external: entry.external,
              action: resolveNavigationAction(entry.action),
              style: resolveNavigationStyle(entry.style),
              children,
            });
          })
      : EMPTY_FORM.navigationLinks.map((entry) =>
          createNavigationItem({
            id: entry.id,
            label: entry.label,
            href: entry.href,
            enabled: entry.enabled,
            external: entry.external,
            action: resolveNavigationAction(entry.action),
            style: resolveNavigationStyle(entry.style),
            children: entry.children,
          }),
        );
  const seededPropertyChildren = seededProperties.map((property) =>
    createNavigationChild({
      propertyId: property.id,
      label: '',
      enabled: true,
    }),
  );
  const hasPropertyChildren = navigationLinks.some((entry) =>
    entry.children.some((child) => child.propertyId.trim().length > 0),
  );
  const navigationLinksWithSeed =
    seededPropertyChildren.length > 0 && !hasPropertyChildren
      ? (() => {
          const propertiesIndex = navigationLinks.findIndex((entry) =>
            entry.label.trim().toLowerCase().includes('properties'),
          );
          if (propertiesIndex >= 0) {
            const next = [...navigationLinks];
            const entry = next[propertiesIndex];
            if (entry.children.length === 0) {
              next[propertiesIndex] = createNavigationItem({
                id: entry.id,
                label: entry.label,
                href: '',
                enabled: entry.enabled,
                external: false,
                action: resolveNavigationAction(entry.action),
                style: resolveNavigationStyle(entry.style),
                children: seededPropertyChildren,
              });
            }
            return next;
          }
          return [
            createNavigationItem({
              label: 'Properties',
              href: '',
              enabled: true,
              external: false,
              action: 'link',
              style: 'link',
              children: seededPropertyChildren,
            }),
            ...navigationLinks,
          ];
        })()
      : navigationLinks;

  const preferText = (value: string, fallback: string) =>
    value.trim().length > 0 ? value : fallback;
  const preferList = <T,>(value: T[], fallback: T[]) =>
    Array.isArray(value) && value.length > 0 ? value : fallback;

  const home = settings.home;
  const leadCapture = settings.leadCapture;
  const about = settings.about;
  const publicContent = settings.publicContent;
  const contactPage = publicContent.contactPage;
  const availabilitiesPage = publicContent.availabilitiesPage;
  const propertyDetail = publicContent.propertyDetail;
  const unitDetail = publicContent.unitDetail;
  const showingDialog = publicContent.showingDialog;
  const sharedContent = publicContent.shared;

  const featuredProperties = preferList(
    about.featuredProperties,
    EMPTY_FORM.aboutFeaturedProperties.split(/\r?\n/),
  );
  const aboutParagraphs = preferList(about.paragraphs, EMPTY_FORM.aboutParagraphs.split(/\n\n+/));

  return {
    ...EMPTY_FORM,
    brandSiteName: siteName.length > 0 ? siteName : EMPTY_FORM.brandSiteName,
    brandTagline: tagline.length > 0 ? tagline : EMPTY_FORM.brandTagline,
    brandLogoUrl: settings.brand.logoUrl ?? '',
    brandFaviconUrl: settings.brand.faviconUrl ?? '',
    brandDefaultOgImageUrl: settings.brand.defaultOgImageUrl ?? '',
    seoDefaultDescription:
      defaultDescription.length > 0 ? defaultDescription : EMPTY_FORM.seoDefaultDescription,
    seoCanonicalOrigin:
      canonicalOrigin.length > 0 ? canonicalOrigin : EMPTY_FORM.seoCanonicalOrigin,
    navigationLinks: navigationLinksWithSeed.map((entry) =>
      createNavigationItem({
        id: entry.id,
        label: entry.label,
        href: entry.href,
        enabled: entry.enabled,
        external: entry.external,
        action: resolveNavigationAction(entry.action),
        style: resolveNavigationStyle(entry.style),
        children: entry.children,
      }),
    ),
    navigationMobileTitle:
      settings.navigation.mobileTitle.trim().length > 0
        ? settings.navigation.mobileTitle
        : EMPTY_FORM.navigationMobileTitle,
    navigationMobileDescription:
      settings.navigation.mobileDescription.trim().length > 0
        ? settings.navigation.mobileDescription
        : EMPTY_FORM.navigationMobileDescription,
    navigationMobileShortcutHeading:
      settings.navigation.mobileShortcutHeading.trim().length > 0
        ? settings.navigation.mobileShortcutHeading
        : EMPTY_FORM.navigationMobileShortcutHeading,
    navigationMobileShortcutDescription:
      settings.navigation.mobileShortcutDescription.trim().length > 0
        ? settings.navigation.mobileShortcutDescription
        : EMPTY_FORM.navigationMobileShortcutDescription,
    navigationMobilePrimaryLabel:
      settings.navigation.mobileShortcutPrimary.label.trim().length > 0
        ? settings.navigation.mobileShortcutPrimary.label
        : EMPTY_FORM.navigationMobilePrimaryLabel,
    navigationMobilePrimaryHref:
      settings.navigation.mobileShortcutPrimary.href.trim().length > 0
        ? settings.navigation.mobileShortcutPrimary.href
        : EMPTY_FORM.navigationMobilePrimaryHref,
    navigationMobileSecondaryLabel:
      settings.navigation.mobileShortcutSecondary.label.trim().length > 0
        ? settings.navigation.mobileShortcutSecondary.label
        : EMPTY_FORM.navigationMobileSecondaryLabel,
    navigationMobileSecondaryHref:
      settings.navigation.mobileShortcutSecondary.href.trim().length > 0
        ? settings.navigation.mobileShortcutSecondary.href
        : EMPTY_FORM.navigationMobileSecondaryHref,
    footerCompanyName:
      settings.footer.companyName.trim().length > 0
        ? settings.footer.companyName
        : EMPTY_FORM.footerCompanyName,
    footerCopyrightText:
      settings.footer.copyrightText.trim().length > 0
        ? settings.footer.copyrightText
        : EMPTY_FORM.footerCopyrightText,
    bookingEmail: settings.bookingEmail,
    contactEmail: settings.contactEmail,
    contactPhone: settings.contactPhone,
    contactAddressLine1: settings.contactAddressLines.at(0) ?? '',
    contactAddressLine2: settings.contactAddressLines.at(1) ?? '',
    mapEmbedUrl: settings.mapEmbedUrl,
    contactHours:
      settings.contactHours.length > 0 ? settings.contactHours : EMPTY_FORM.contactHours,
    leadershipContacts:
      settings.leadershipContacts.length > 0
        ? settings.leadershipContacts.map((entry) => ({
            name: entry.name,
            title: entry.title ?? '',
          }))
        : EMPTY_FORM.leadershipContacts,
    homeSeoTitle: preferText(home.seo.title, EMPTY_FORM.homeSeoTitle),
    homeSeoDescription: preferText(home.seo.description, EMPTY_FORM.homeSeoDescription),
    homeHeroEyebrow: preferText(home.hero.eyebrow, EMPTY_FORM.homeHeroEyebrow),
    homeHeroTitle: preferText(home.hero.title, EMPTY_FORM.homeHeroTitle),
    homeHeroDescription: preferText(home.hero.description, EMPTY_FORM.homeHeroDescription),
    homeHeroMediaLabel: preferText(home.heroMedia.label, EMPTY_FORM.homeHeroMediaLabel),
    homeHeroMediaLocation: preferText(home.heroMedia.location, EMPTY_FORM.homeHeroMediaLocation),
    homeHeroMediaAriaLabel: preferText(home.heroMedia.ariaLabel, EMPTY_FORM.homeHeroMediaAriaLabel),
    homeHeroMediaUrl: preferText(home.heroMedia.url, EMPTY_FORM.homeHeroMediaUrl),
    homeHeroMediaType: home.heroMedia.mediaType === 'image' ? 'image' : 'video',
    homeAvailabilityEyebrow: preferText(
      home.availability.eyebrow,
      EMPTY_FORM.homeAvailabilityEyebrow,
    ),
    homeAvailabilityTitle: preferText(home.availability.title, EMPTY_FORM.homeAvailabilityTitle),
    homeAvailabilityDescription: preferText(
      home.availability.description,
      EMPTY_FORM.homeAvailabilityDescription,
    ),
    homeAvailabilityCtaLabel: preferText(
      home.availability.ctaLabel,
      EMPTY_FORM.homeAvailabilityCtaLabel,
    ),
    homeAvailabilityCtaHref: preferText(
      home.availability.ctaHref,
      EMPTY_FORM.homeAvailabilityCtaHref,
    ),
    homeAvailabilityEmptyMessage: preferText(
      home.availabilityMessages.emptyMessage,
      EMPTY_FORM.homeAvailabilityEmptyMessage,
    ),
    homeAvailabilityErrorMessage: preferText(
      home.availabilityMessages.errorMessage,
      EMPTY_FORM.homeAvailabilityErrorMessage,
    ),
    homeContactHeading: preferText(home.contact.heading, EMPTY_FORM.homeContactHeading),
    homeContactDescription: preferText(home.contact.description, EMPTY_FORM.homeContactDescription),
    leadSeoTitle: preferText(leadCapture.seo.title, EMPTY_FORM.leadSeoTitle),
    leadSeoDescription: preferText(leadCapture.seo.description, EMPTY_FORM.leadSeoDescription),
    leadCaptureEyebrow: preferText(leadCapture.eyebrow, EMPTY_FORM.leadCaptureEyebrow),
    leadCaptureTitle: preferText(leadCapture.title, EMPTY_FORM.leadCaptureTitle),
    leadCaptureDescription: preferText(leadCapture.description, EMPTY_FORM.leadCaptureDescription),
    leadCaptureThankYouTitle: preferText(
      leadCapture.thankYouTitle,
      EMPTY_FORM.leadCaptureThankYouTitle,
    ),
    leadCaptureThankYou: preferText(leadCapture.thankYou, EMPTY_FORM.leadCaptureThankYou),
    leadCaptureBenefitsHeading: preferText(
      leadCapture.benefitsHeading,
      EMPTY_FORM.leadCaptureBenefitsHeading,
    ),
    leadCaptureBenefits: preferList(leadCapture.benefits, EMPTY_FORM.leadCaptureBenefits),
    leadCaptureStats: preferList(leadCapture.stats, EMPTY_FORM.leadCaptureStats),
    leadFormFullNameLabel: preferText(
      leadCapture.form.fullNameLabel,
      EMPTY_FORM.leadFormFullNameLabel,
    ),
    leadFormEmailLabel: preferText(leadCapture.form.emailLabel, EMPTY_FORM.leadFormEmailLabel),
    leadFormPhoneLabel: preferText(leadCapture.form.phoneLabel, EMPTY_FORM.leadFormPhoneLabel),
    leadFormBudgetLabel: preferText(leadCapture.form.budgetLabel, EMPTY_FORM.leadFormBudgetLabel),
    leadFormMoveInLabel: preferText(leadCapture.form.moveInLabel, EMPTY_FORM.leadFormMoveInLabel),
    leadFormNotesLabel: preferText(leadCapture.form.notesLabel, EMPTY_FORM.leadFormNotesLabel),
    leadFormSubmitLabel: preferText(leadCapture.form.submitLabel, EMPTY_FORM.leadFormSubmitLabel),
    leadFormConsentText: preferText(leadCapture.form.consentText, EMPTY_FORM.leadFormConsentText),
    leadFormSuccessToast: preferText(
      leadCapture.form.successToast,
      EMPTY_FORM.leadFormSuccessToast,
    ),
    leadFormErrorToast: preferText(leadCapture.form.errorToast, EMPTY_FORM.leadFormErrorToast),
    aboutSeoTitle: preferText(about.seo.title, EMPTY_FORM.aboutSeoTitle),
    aboutSeoDescription: preferText(about.seo.description, EMPTY_FORM.aboutSeoDescription),
    aboutHeroTitle: preferText(about.heroTitle, EMPTY_FORM.aboutHeroTitle),
    aboutHeroSubtitle: preferText(about.heroSubtitle, EMPTY_FORM.aboutHeroSubtitle),
    aboutTagline: preferText(about.tagline, EMPTY_FORM.aboutTagline),
    aboutHeroPrimaryCtaLabel: preferText(
      about.heroPrimaryCtaLabel,
      EMPTY_FORM.aboutHeroPrimaryCtaLabel,
    ),
    aboutHeroPrimaryCtaHref: preferText(
      about.heroPrimaryCtaHref,
      EMPTY_FORM.aboutHeroPrimaryCtaHref,
    ),
    aboutHeroSecondaryCtaLabel: preferText(
      about.heroSecondaryCtaLabel,
      EMPTY_FORM.aboutHeroSecondaryCtaLabel,
    ),
    aboutHeroSecondaryCtaHref: preferText(
      about.heroSecondaryCtaHref,
      EMPTY_FORM.aboutHeroSecondaryCtaHref,
    ),
    aboutHighlightTitle: preferText(about.highlightTitle, EMPTY_FORM.aboutHighlightTitle),
    aboutHighlightDescription: preferText(
      about.highlightDescription,
      EMPTY_FORM.aboutHighlightDescription,
    ),
    aboutFeaturedProperties: featuredProperties.join('\n'),
    aboutSnapshotEyebrow: preferText(about.snapshotEyebrow, EMPTY_FORM.aboutSnapshotEyebrow),
    aboutSnapshotStats: preferList(about.snapshotStats, EMPTY_FORM.aboutSnapshotStats),
    aboutBodyHeading: preferText(about.bodyHeading, EMPTY_FORM.aboutBodyHeading),
    aboutBodyDescription: preferText(about.bodyDescription, EMPTY_FORM.aboutBodyDescription),
    aboutParagraphs: aboutParagraphs.join('\n\n'),
    aboutDetailsSummaryLabel: preferText(
      about.detailsSummaryLabel,
      EMPTY_FORM.aboutDetailsSummaryLabel,
    ),
    aboutPromiseLabel: preferText(about.promiseLabel, EMPTY_FORM.aboutPromiseLabel),
    aboutValuesTitle: preferText(about.values.title, EMPTY_FORM.aboutValuesTitle),
    aboutValuesDescription: preferText(about.values.description, EMPTY_FORM.aboutValuesDescription),
    aboutValuesCtaLabel: preferText(about.values.ctaLabel, EMPTY_FORM.aboutValuesCtaLabel),
    aboutValuesCtaHref: preferText(about.values.ctaHref, EMPTY_FORM.aboutValuesCtaHref),
    aboutValuesPillars: preferList(about.values.pillars, EMPTY_FORM.aboutValuesPillars),
    aboutPortfolioTitle: preferText(about.portfolio.title, EMPTY_FORM.aboutPortfolioTitle),
    aboutPortfolioDescription: preferText(
      about.portfolio.description,
      EMPTY_FORM.aboutPortfolioDescription,
    ),
    aboutPortfolioCtaLabel: preferText(about.portfolio.ctaLabel, EMPTY_FORM.aboutPortfolioCtaLabel),
    aboutPortfolioCtaHref: preferText(about.portfolio.ctaHref, EMPTY_FORM.aboutPortfolioCtaHref),
    aboutPortfolioFlagshipEyebrow: preferText(
      about.portfolio.flagshipEyebrow,
      EMPTY_FORM.aboutPortfolioFlagshipEyebrow,
    ),
    aboutPortfolioFlagshipTitle: preferText(
      about.portfolio.flagshipTitle,
      EMPTY_FORM.aboutPortfolioFlagshipTitle,
    ),
    aboutPortfolioFlagshipDescription: preferText(
      about.portfolio.flagshipDescription,
      EMPTY_FORM.aboutPortfolioFlagshipDescription,
    ),
    aboutPortfolioFlagshipStats: preferList(
      about.portfolio.flagshipStats,
      EMPTY_FORM.aboutPortfolioFlagshipStats,
    ),
    aboutPortfolioFlagshipFeaturesHeading: preferText(
      about.portfolio.flagshipFeaturesHeading,
      EMPTY_FORM.aboutPortfolioFlagshipFeaturesHeading,
    ),
    aboutPortfolioFlagshipFeaturesToggleLabel: preferText(
      about.portfolio.flagshipFeaturesToggleLabel,
      EMPTY_FORM.aboutPortfolioFlagshipFeaturesToggleLabel,
    ),
    aboutPortfolioFlagshipFeatures: preferList(
      about.portfolio.flagshipFeatures,
      EMPTY_FORM.aboutPortfolioFlagshipFeatures,
    ),
    aboutPortfolioDowntownEyebrow: preferText(
      about.portfolio.downtownEyebrow,
      EMPTY_FORM.aboutPortfolioDowntownEyebrow,
    ),
    aboutPortfolioDowntownTitle: preferText(
      about.portfolio.downtownTitle,
      EMPTY_FORM.aboutPortfolioDowntownTitle,
    ),
    aboutPortfolioDowntownDescription: preferText(
      about.portfolio.downtownDescription,
      EMPTY_FORM.aboutPortfolioDowntownDescription,
    ),
    aboutPortfolioDowntownIdealHeading: preferText(
      about.portfolio.downtownIdealHeading,
      EMPTY_FORM.aboutPortfolioDowntownIdealHeading,
    ),
    aboutPortfolioDowntownTenants: preferList(
      about.portfolio.downtownTenants,
      EMPTY_FORM.aboutPortfolioDowntownTenants,
    ),
    aboutPortfolioDowntownAddressesHeading: preferText(
      about.portfolio.downtownAddressesHeading,
      EMPTY_FORM.aboutPortfolioDowntownAddressesHeading,
    ),
    aboutPortfolioRetailEyebrow: preferText(
      about.portfolio.retailEyebrow,
      EMPTY_FORM.aboutPortfolioRetailEyebrow,
    ),
    aboutPortfolioRetailTitle: preferText(
      about.portfolio.retailTitle,
      EMPTY_FORM.aboutPortfolioRetailTitle,
    ),
    aboutPortfolioRetailDescription: preferText(
      about.portfolio.retailDescription,
      EMPTY_FORM.aboutPortfolioRetailDescription,
    ),
    aboutPortfolioRetailPlazas: preferList(
      about.portfolio.retailPlazas,
      EMPTY_FORM.aboutPortfolioRetailPlazas,
    ),
    aboutPortfolioRetailFooter: preferText(
      about.portfolio.retailFooter,
      EMPTY_FORM.aboutPortfolioRetailFooter,
    ),
    aboutPortfolioRetailFooterLinkLabel: preferText(
      about.portfolio.retailFooterLinkLabel,
      EMPTY_FORM.aboutPortfolioRetailFooterLinkLabel,
    ),
    aboutContactHeading: preferText(about.contactHeading, EMPTY_FORM.aboutContactHeading),
    aboutContactDescription: preferText(
      about.contactDescription,
      EMPTY_FORM.aboutContactDescription,
    ),
    contactPageSeoTitle: preferText(contactPage.seo.title, EMPTY_FORM.contactPageSeoTitle),
    contactPageSeoDescription: preferText(
      contactPage.seo.description,
      EMPTY_FORM.contactPageSeoDescription,
    ),
    contactPageHeroEyebrow: preferText(contactPage.hero.eyebrow, EMPTY_FORM.contactPageHeroEyebrow),
    contactPageHeroTitle: preferText(contactPage.hero.title, EMPTY_FORM.contactPageHeroTitle),
    contactPageHeroDescription: preferText(
      contactPage.hero.description,
      EMPTY_FORM.contactPageHeroDescription,
    ),
    contactPageHeroResponseTime: preferText(
      contactPage.hero.responseTime,
      EMPTY_FORM.contactPageHeroResponseTime,
    ),
    contactPageHeroCardEyebrow: preferText(
      contactPage.heroCard.eyebrow,
      EMPTY_FORM.contactPageHeroCardEyebrow,
    ),
    contactPageHeroCardTitle: preferText(
      contactPage.heroCard.title,
      EMPTY_FORM.contactPageHeroCardTitle,
    ),
    contactPageHeroCardDescription: preferText(
      contactPage.heroCard.description,
      EMPTY_FORM.contactPageHeroCardDescription,
    ),
    contactPageHeroCardCallLabel: preferText(
      contactPage.heroCard.callLabel,
      EMPTY_FORM.contactPageHeroCardCallLabel,
    ),
    contactPageHeroCardEmailLabel: preferText(
      contactPage.heroCard.emailLabel,
      EMPTY_FORM.contactPageHeroCardEmailLabel,
    ),
    contactPageHeroCardMapLabel: preferText(
      contactPage.heroCard.mapLabel,
      EMPTY_FORM.contactPageHeroCardMapLabel,
    ),
    contactPageSupportTitle: preferText(contactPage.body.title, EMPTY_FORM.contactPageSupportTitle),
    contactPageSupportDescription: preferText(
      contactPage.body.description,
      EMPTY_FORM.contactPageSupportDescription,
    ),
    contactPageSupportHighlights: preferList(
      contactPage.body.highlights,
      EMPTY_FORM.contactPageSupportHighlights,
    ),
    contactPageSupportResponseTimeLabel: preferText(
      contactPage.body.responseTimeLabel,
      EMPTY_FORM.contactPageSupportResponseTimeLabel,
    ),
    contactPageSupportResponseTimeValue: preferText(
      contactPage.body.responseTimeValue,
      EMPTY_FORM.contactPageSupportResponseTimeValue,
    ),
    contactPageSupportDirectionsLabel: preferText(
      contactPage.body.directionsLabel,
      EMPTY_FORM.contactPageSupportDirectionsLabel,
    ),
    contactPageSupportAvailabilitiesLabel: preferText(
      contactPage.body.availabilitiesLabel,
      EMPTY_FORM.contactPageSupportAvailabilitiesLabel,
    ),
    contactPageDetailsTitle: preferText(
      contactPage.details.title,
      EMPTY_FORM.contactPageDetailsTitle,
    ),
    contactPageDetailsDescription: preferText(
      contactPage.details.description,
      EMPTY_FORM.contactPageDetailsDescription,
    ),
    contactPageFormTitle: preferText(contactPage.form.title, EMPTY_FORM.contactPageFormTitle),
    contactPageFormDescription: preferText(
      contactPage.form.description,
      EMPTY_FORM.contactPageFormDescription,
    ),
    contactPageFormSuccessMessage: preferText(
      contactPage.form.successMessage,
      EMPTY_FORM.contactPageFormSuccessMessage,
    ),
    contactPageFormErrorMessage: preferText(
      contactPage.form.errorMessage,
      EMPTY_FORM.contactPageFormErrorMessage,
    ),
    contactPageFormHelperText: preferText(
      contactPage.form.helperText,
      EMPTY_FORM.contactPageFormHelperText,
    ),
    contactPageFormSubmitLabel: preferText(
      contactPage.form.submitLabel,
      EMPTY_FORM.contactPageFormSubmitLabel,
    ),
    contactPageFormFullNameLabel: preferText(
      contactPage.form.fields.fullNameLabel,
      EMPTY_FORM.contactPageFormFullNameLabel,
    ),
    contactPageFormFullNamePlaceholder: preferText(
      contactPage.form.fields.fullNamePlaceholder,
      EMPTY_FORM.contactPageFormFullNamePlaceholder,
    ),
    contactPageFormPhoneLabel: preferText(
      contactPage.form.fields.phoneLabel,
      EMPTY_FORM.contactPageFormPhoneLabel,
    ),
    contactPageFormPhonePlaceholder: preferText(
      contactPage.form.fields.phonePlaceholder,
      EMPTY_FORM.contactPageFormPhonePlaceholder,
    ),
    contactPageFormEmailLabel: preferText(
      contactPage.form.fields.emailLabel,
      EMPTY_FORM.contactPageFormEmailLabel,
    ),
    contactPageFormEmailPlaceholder: preferText(
      contactPage.form.fields.emailPlaceholder,
      EMPTY_FORM.contactPageFormEmailPlaceholder,
    ),
    contactPageFormMessageLabel: preferText(
      contactPage.form.fields.messageLabel,
      EMPTY_FORM.contactPageFormMessageLabel,
    ),
    contactPageFormMessagePlaceholder: preferText(
      contactPage.form.fields.messagePlaceholder,
      EMPTY_FORM.contactPageFormMessagePlaceholder,
    ),
    contactPageFormSpaceTypeLabel: preferText(
      contactPage.form.fields.spaceTypeLabel,
      EMPTY_FORM.contactPageFormSpaceTypeLabel,
    ),
    contactPageFormSpaceTypePlaceholder: preferText(
      contactPage.form.fields.spaceTypePlaceholder,
      EMPTY_FORM.contactPageFormSpaceTypePlaceholder,
    ),
    contactPageFormSpaceTypeOptions: preferList(
      contactPage.form.fields.spaceTypeOptions,
      EMPTY_FORM.contactPageFormSpaceTypeOptions,
    ),
    contactPageFormTargetSizeLabel: preferText(
      contactPage.form.fields.targetSizeLabel,
      EMPTY_FORM.contactPageFormTargetSizeLabel,
    ),
    contactPageFormTargetSizePlaceholder: preferText(
      contactPage.form.fields.targetSizePlaceholder,
      EMPTY_FORM.contactPageFormTargetSizePlaceholder,
    ),
    contactPageFormTimelineLabel: preferText(
      contactPage.form.fields.timelineLabel,
      EMPTY_FORM.contactPageFormTimelineLabel,
    ),
    contactPageFormTimelinePlaceholder: preferText(
      contactPage.form.fields.timelinePlaceholder,
      EMPTY_FORM.contactPageFormTimelinePlaceholder,
    ),
    contactPageFormTimelineOptions: preferList(
      contactPage.form.fields.timelineOptions,
      EMPTY_FORM.contactPageFormTimelineOptions,
    ),
    availabilitiesSeoTitle: preferText(
      availabilitiesPage.seo.title,
      EMPTY_FORM.availabilitiesSeoTitle,
    ),
    availabilitiesSeoDescription: preferText(
      availabilitiesPage.seo.description,
      EMPTY_FORM.availabilitiesSeoDescription,
    ),
    availabilitiesHeadingEyebrow: preferText(
      availabilitiesPage.heading.eyebrow,
      EMPTY_FORM.availabilitiesHeadingEyebrow,
    ),
    availabilitiesHeadingTitle: preferText(
      availabilitiesPage.heading.title,
      EMPTY_FORM.availabilitiesHeadingTitle,
    ),
    availabilitiesHeadingDescription: preferText(
      availabilitiesPage.heading.description,
      EMPTY_FORM.availabilitiesHeadingDescription,
    ),
    availabilitiesFiltersTitle: preferText(
      availabilitiesPage.filters.title,
      EMPTY_FORM.availabilitiesFiltersTitle,
    ),
    availabilitiesFiltersSubtitle: preferText(
      availabilitiesPage.filters.subtitle,
      EMPTY_FORM.availabilitiesFiltersSubtitle,
    ),
    availabilitiesFiltersEmptyLabel: preferText(
      availabilitiesPage.filters.emptyLabel,
      EMPTY_FORM.availabilitiesFiltersEmptyLabel,
    ),
    availabilitiesFiltersActiveTemplate: preferText(
      availabilitiesPage.filters.activeTemplate,
      EMPTY_FORM.availabilitiesFiltersActiveTemplate,
    ),
    availabilitiesFiltersResetLabel: preferText(
      availabilitiesPage.filters.resetLabel,
      EMPTY_FORM.availabilitiesFiltersResetLabel,
    ),
    availabilitiesFiltersOpenLabel: preferText(
      availabilitiesPage.filters.openLabel,
      EMPTY_FORM.availabilitiesFiltersOpenLabel,
    ),
    availabilitiesFiltersDrawerTitle: preferText(
      availabilitiesPage.filters.drawerTitle,
      EMPTY_FORM.availabilitiesFiltersDrawerTitle,
    ),
    availabilitiesFiltersDrawerApplyLabel: preferText(
      availabilitiesPage.filters.drawerApplyLabel,
      EMPTY_FORM.availabilitiesFiltersDrawerApplyLabel,
    ),
    availabilitiesFiltersDrawerResetLabel: preferText(
      availabilitiesPage.filters.drawerResetLabel,
      EMPTY_FORM.availabilitiesFiltersDrawerResetLabel,
    ),
    availabilitiesFiltersChipPriceLabel: preferText(
      availabilitiesPage.filters.chipPriceLabel,
      EMPTY_FORM.availabilitiesFiltersChipPriceLabel,
    ),
    availabilitiesFiltersChipSizeLabel: preferText(
      availabilitiesPage.filters.chipSizeLabel,
      EMPTY_FORM.availabilitiesFiltersChipSizeLabel,
    ),
    availabilitiesFiltersChipUseLabel: preferText(
      availabilitiesPage.filters.chipUseLabel,
      EMPTY_FORM.availabilitiesFiltersChipUseLabel,
    ),
    availabilitiesViewLabel: preferText(
      availabilitiesPage.viewToggle.label,
      EMPTY_FORM.availabilitiesViewLabel,
    ),
    availabilitiesViewCardsLabel: preferText(
      availabilitiesPage.viewToggle.cardsLabel,
      EMPTY_FORM.availabilitiesViewCardsLabel,
    ),
    availabilitiesViewMapLabel: preferText(
      availabilitiesPage.viewToggle.mapLabel,
      EMPTY_FORM.availabilitiesViewMapLabel,
    ),
    availabilitiesFilterPriceTitle: preferText(
      availabilitiesPage.filterControls.priceTitle,
      EMPTY_FORM.availabilitiesFilterPriceTitle,
    ),
    availabilitiesFilterPriceDescription: preferText(
      availabilitiesPage.filterControls.priceDescription,
      EMPTY_FORM.availabilitiesFilterPriceDescription,
    ),
    availabilitiesFilterSizeTitle: preferText(
      availabilitiesPage.filterControls.sizeTitle,
      EMPTY_FORM.availabilitiesFilterSizeTitle,
    ),
    availabilitiesFilterSizeDescription: preferText(
      availabilitiesPage.filterControls.sizeDescription,
      EMPTY_FORM.availabilitiesFilterSizeDescription,
    ),
    availabilitiesFilterUsesTitle: preferText(
      availabilitiesPage.filterControls.usesTitle,
      EMPTY_FORM.availabilitiesFilterUsesTitle,
    ),
    availabilitiesFilterUsesDescription: preferText(
      availabilitiesPage.filterControls.usesDescription,
      EMPTY_FORM.availabilitiesFilterUsesDescription,
    ),
    availabilitiesFilterMinLabel: preferText(
      availabilitiesPage.filterControls.minLabel,
      EMPTY_FORM.availabilitiesFilterMinLabel,
    ),
    availabilitiesFilterMaxLabel: preferText(
      availabilitiesPage.filterControls.maxLabel,
      EMPTY_FORM.availabilitiesFilterMaxLabel,
    ),
    availabilitiesFilterPlaceholder: preferText(
      availabilitiesPage.filterControls.placeholder,
      EMPTY_FORM.availabilitiesFilterPlaceholder,
    ),
    availabilitiesResultsNoMatchesMessage: preferText(
      availabilitiesPage.results.noMatchesMessage,
      EMPTY_FORM.availabilitiesResultsNoMatchesMessage,
    ),
    availabilitiesResultsNoAvailabilityMessage: preferText(
      availabilitiesPage.results.noAvailabilityMessage,
      EMPTY_FORM.availabilitiesResultsNoAvailabilityMessage,
    ),
    availabilitiesResultsClearFiltersLabel: preferText(
      availabilitiesPage.results.clearFiltersLabel,
      EMPTY_FORM.availabilitiesResultsClearFiltersLabel,
    ),
    availabilitiesResultsErrorMessage: preferText(
      availabilitiesPage.results.errorMessage,
      EMPTY_FORM.availabilitiesResultsErrorMessage,
    ),
    availabilitiesListAddressEyebrow: preferText(
      availabilitiesPage.list.addressEyebrow,
      EMPTY_FORM.availabilitiesListAddressEyebrow,
    ),
    availabilitiesListUnitCountSingular: preferText(
      availabilitiesPage.list.unitCountSingular,
      EMPTY_FORM.availabilitiesListUnitCountSingular,
    ),
    availabilitiesListUnitCountPlural: preferText(
      availabilitiesPage.list.unitCountPlural,
      EMPTY_FORM.availabilitiesListUnitCountPlural,
    ),
    availabilitiesListViewPropertyLabel: preferText(
      availabilitiesPage.list.viewPropertyLabel,
      EMPTY_FORM.availabilitiesListViewPropertyLabel,
    ),
    availabilitiesMapBackLabel: preferText(
      availabilitiesPage.mapPanel.backLabel,
      EMPTY_FORM.availabilitiesMapBackLabel,
    ),
    availabilitiesMapAddressEyebrow: preferText(
      availabilitiesPage.mapPanel.addressEyebrow,
      EMPTY_FORM.availabilitiesMapAddressEyebrow,
    ),
    availabilitiesMapAddressesEyebrow: preferText(
      availabilitiesPage.mapPanel.addressesEyebrow,
      EMPTY_FORM.availabilitiesMapAddressesEyebrow,
    ),
    availabilitiesMapBrowseTitle: preferText(
      availabilitiesPage.mapPanel.browseTitle,
      EMPTY_FORM.availabilitiesMapBrowseTitle,
    ),
    availabilitiesMapBrowseDescription: preferText(
      availabilitiesPage.mapPanel.browseDescription,
      EMPTY_FORM.availabilitiesMapBrowseDescription,
    ),
    availabilitiesMapSelectUnitEyebrow: preferText(
      availabilitiesPage.mapPanel.selectUnitEyebrow,
      EMPTY_FORM.availabilitiesMapSelectUnitEyebrow,
    ),
    availabilitiesMapSelectUnitDescription: preferText(
      availabilitiesPage.mapPanel.selectUnitDescription,
      EMPTY_FORM.availabilitiesMapSelectUnitDescription,
    ),
    availabilitiesMapTapPinEyebrow: preferText(
      availabilitiesPage.mapPanel.tapPinEyebrow,
      EMPTY_FORM.availabilitiesMapTapPinEyebrow,
    ),
    availabilitiesMapTapPinDescription: preferText(
      availabilitiesPage.mapPanel.tapPinDescription,
      EMPTY_FORM.availabilitiesMapTapPinDescription,
    ),
    availabilitiesMapSuiteCountSingular: preferText(
      availabilitiesPage.mapPanel.suiteCountSingular,
      EMPTY_FORM.availabilitiesMapSuiteCountSingular,
    ),
    availabilitiesMapSuiteCountPlural: preferText(
      availabilitiesPage.mapPanel.suiteCountPlural,
      EMPTY_FORM.availabilitiesMapSuiteCountPlural,
    ),
    availabilitiesMapPinFallback: preferText(
      availabilitiesPage.mapPanel.mapPinFallback,
      EMPTY_FORM.availabilitiesMapPinFallback,
    ),
    availabilitiesMapUnitLabelTemplate: preferText(
      availabilitiesPage.mapUnitRow.unitLabelTemplate,
      EMPTY_FORM.availabilitiesMapUnitLabelTemplate,
    ),
    availabilitiesMapLevelLabelTemplate: preferText(
      availabilitiesPage.mapUnitRow.levelLabelTemplate,
      EMPTY_FORM.availabilitiesMapLevelLabelTemplate,
    ),
    availabilitiesMapDivisibleLabel: preferText(
      availabilitiesPage.mapUnitRow.divisibleLabel,
      EMPTY_FORM.availabilitiesMapDivisibleLabel,
    ),
    availabilitiesMapIncentivesLabel: preferText(
      availabilitiesPage.mapUnitRow.incentivesLabel,
      EMPTY_FORM.availabilitiesMapIncentivesLabel,
    ),
    availabilitiesQuickViewEyebrow: preferText(
      availabilitiesPage.quickView.eyebrow,
      EMPTY_FORM.availabilitiesQuickViewEyebrow,
    ),
    availabilitiesQuickViewTitleTemplate: preferText(
      availabilitiesPage.quickView.titleTemplate,
      EMPTY_FORM.availabilitiesQuickViewTitleTemplate,
    ),
    availabilitiesQuickViewRentLabel: preferText(
      availabilitiesPage.quickView.rentLabel,
      EMPTY_FORM.availabilitiesQuickViewRentLabel,
    ),
    availabilitiesQuickViewRentSuffix: preferText(
      availabilitiesPage.quickView.rentSuffix,
      EMPTY_FORM.availabilitiesQuickViewRentSuffix,
    ),
    availabilitiesQuickViewSizeLabel: preferText(
      availabilitiesPage.quickView.sizeLabel,
      EMPTY_FORM.availabilitiesQuickViewSizeLabel,
    ),
    availabilitiesQuickViewAvailabilityLabel: preferText(
      availabilitiesPage.quickView.availabilityLabel,
      EMPTY_FORM.availabilitiesQuickViewAvailabilityLabel,
    ),
    availabilitiesQuickViewScheduleLabel: preferText(
      availabilitiesPage.quickView.scheduleLabel,
      EMPTY_FORM.availabilitiesQuickViewScheduleLabel,
    ),
    availabilitiesQuickViewDetailsLabel: preferText(
      availabilitiesPage.quickView.detailsLabel,
      EMPTY_FORM.availabilitiesQuickViewDetailsLabel,
    ),
    availabilitiesQuickViewDivisibleLabel: preferText(
      availabilitiesPage.quickView.divisibleLabel,
      EMPTY_FORM.availabilitiesQuickViewDivisibleLabel,
    ),
    availabilitiesQuickViewIncentivesLabel: preferText(
      availabilitiesPage.quickView.incentivesLabel,
      EMPTY_FORM.availabilitiesQuickViewIncentivesLabel,
    ),
    propertyDetailMessagesUnpublished: preferText(
      propertyDetail.messages.unpublishedMessage,
      EMPTY_FORM.propertyDetailMessagesUnpublished,
    ),
    propertyDetailMessagesCtaLabel: preferText(
      propertyDetail.messages.ctaLabel,
      EMPTY_FORM.propertyDetailMessagesCtaLabel,
    ),
    propertyDetailNavOverview: preferText(
      propertyDetail.navigation.overviewLabel,
      EMPTY_FORM.propertyDetailNavOverview,
    ),
    propertyDetailNavHighlights: preferText(
      propertyDetail.navigation.highlightsLabel,
      EMPTY_FORM.propertyDetailNavHighlights,
    ),
    propertyDetailNavLocation: preferText(
      propertyDetail.navigation.locationLabel,
      EMPTY_FORM.propertyDetailNavLocation,
    ),
    propertyDetailNavAvailability: preferText(
      propertyDetail.navigation.availabilityLabel,
      EMPTY_FORM.propertyDetailNavAvailability,
    ),
    propertyDetailAvailabilityNoUnits: preferText(
      propertyDetail.availabilityStrip.noUnitsLabel,
      EMPTY_FORM.propertyDetailAvailabilityNoUnits,
    ),
    propertyDetailAvailabilityTemplate: preferText(
      propertyDetail.availabilityStrip.availableUnitsTemplate,
      EMPTY_FORM.propertyDetailAvailabilityTemplate,
    ),
    propertyDetailAvailabilityUpdatedLabel: preferText(
      propertyDetail.availabilityStrip.updatedLabel,
      EMPTY_FORM.propertyDetailAvailabilityUpdatedLabel,
    ),
    propertyDetailOverviewEyebrow: preferText(
      propertyDetail.overview.eyebrow,
      EMPTY_FORM.propertyDetailOverviewEyebrow,
    ),
    propertyDetailOverviewViewOnMapLabel: preferText(
      propertyDetail.overview.viewOnMapLabel,
      EMPTY_FORM.propertyDetailOverviewViewOnMapLabel,
    ),
    propertyDetailOverviewTitleTemplate: preferText(
      propertyDetail.overview.titleTemplate,
      EMPTY_FORM.propertyDetailOverviewTitleTemplate,
    ),
    propertyDetailUnitsEyebrow: preferText(
      propertyDetail.units.eyebrow,
      EMPTY_FORM.propertyDetailUnitsEyebrow,
    ),
    propertyDetailUnitsTitle: preferText(
      propertyDetail.units.title,
      EMPTY_FORM.propertyDetailUnitsTitle,
    ),
    propertyDetailUnitsListedTemplate: preferText(
      propertyDetail.units.listedTemplate,
      EMPTY_FORM.propertyDetailUnitsListedTemplate,
    ),
    propertyDetailUnitsEmptyAll: preferText(
      propertyDetail.units.emptyAllMessage,
      EMPTY_FORM.propertyDetailUnitsEmptyAll,
    ),
    propertyDetailUnitsEmptyFiltered: preferText(
      propertyDetail.units.emptyFilteredMessage,
      EMPTY_FORM.propertyDetailUnitsEmptyFiltered,
    ),
    propertyDetailUnitsCountSingular: preferText(
      propertyDetail.units.unitCountSingular,
      EMPTY_FORM.propertyDetailUnitsCountSingular,
    ),
    propertyDetailUnitsCountPlural: preferText(
      propertyDetail.units.unitCountPlural,
      EMPTY_FORM.propertyDetailUnitsCountPlural,
    ),
    propertyDetailUnitsFloorPlanFallback: preferText(
      propertyDetail.units.floorPlanFallback,
      EMPTY_FORM.propertyDetailUnitsFloorPlanFallback,
    ),
    propertyDetailUnitsMonthlyLeaseLabel: preferText(
      propertyDetail.units.monthlyLeaseLabel,
      EMPTY_FORM.propertyDetailUnitsMonthlyLeaseLabel,
    ),
    propertyDetailUnitsContactLabel: preferText(
      propertyDetail.units.contactLabel,
      EMPTY_FORM.propertyDetailUnitsContactLabel,
    ),
    propertyDetailUnitsContactFallback: preferText(
      propertyDetail.units.contactFallback,
      EMPTY_FORM.propertyDetailUnitsContactFallback,
    ),
    propertyDetailUnitsBookButtonLabel: preferText(
      propertyDetail.units.bookButtonLabel,
      EMPTY_FORM.propertyDetailUnitsBookButtonLabel,
    ),
    propertyDetailUnitsViewDetailsLabel: preferText(
      propertyDetail.units.viewDetailsLabel,
      EMPTY_FORM.propertyDetailUnitsViewDetailsLabel,
    ),
    propertyDetailUnitsTableSuiteHeader: preferText(
      propertyDetail.unitsTable.suiteHeader,
      EMPTY_FORM.propertyDetailUnitsTableSuiteHeader,
    ),
    propertyDetailUnitsTableFloorHeader: preferText(
      propertyDetail.unitsTable.floorHeader,
      EMPTY_FORM.propertyDetailUnitsTableFloorHeader,
    ),
    propertyDetailUnitsTableSizeHeader: preferText(
      propertyDetail.unitsTable.sizeHeader,
      EMPTY_FORM.propertyDetailUnitsTableSizeHeader,
    ),
    propertyDetailUnitsTableMonthlyLeaseHeader: preferText(
      propertyDetail.unitsTable.monthlyLeaseHeader,
      EMPTY_FORM.propertyDetailUnitsTableMonthlyLeaseHeader,
    ),
    propertyDetailUnitsTableRateHeader: preferText(
      propertyDetail.unitsTable.rateHeader,
      EMPTY_FORM.propertyDetailUnitsTableRateHeader,
    ),
    propertyDetailUnitsTableActionHeader: preferText(
      propertyDetail.unitsTable.actionHeader,
      EMPTY_FORM.propertyDetailUnitsTableActionHeader,
    ),
    propertyDetailUnitsTableNoUnitsMessage: preferText(
      propertyDetail.unitsTable.noUnitsMessage,
      EMPTY_FORM.propertyDetailUnitsTableNoUnitsMessage,
    ),
    propertyDetailUnitsTableNoMatchesMessage: preferText(
      propertyDetail.unitsTable.noMatchesMessage,
      EMPTY_FORM.propertyDetailUnitsTableNoMatchesMessage,
    ),
    propertyDetailUnitFiltersSizeLabel: preferText(
      propertyDetail.unitFilters.sizeLabel,
      EMPTY_FORM.propertyDetailUnitFiltersSizeLabel,
    ),
    propertyDetailUnitFiltersFloorLabel: preferText(
      propertyDetail.unitFilters.floorLabel,
      EMPTY_FORM.propertyDetailUnitFiltersFloorLabel,
    ),
    propertyDetailUnitFiltersRateLabel: preferText(
      propertyDetail.unitFilters.rateLabel,
      EMPTY_FORM.propertyDetailUnitFiltersRateLabel,
    ),
    propertyDetailUnitFiltersSizeAllLabel: preferText(
      propertyDetail.unitFilters.sizeAllLabel,
      EMPTY_FORM.propertyDetailUnitFiltersSizeAllLabel,
    ),
    propertyDetailUnitFiltersFloorAllLabel: preferText(
      propertyDetail.unitFilters.floorAllLabel,
      EMPTY_FORM.propertyDetailUnitFiltersFloorAllLabel,
    ),
    propertyDetailUnitFiltersRateAllLabel: preferText(
      propertyDetail.unitFilters.rateAllLabel,
      EMPTY_FORM.propertyDetailUnitFiltersRateAllLabel,
    ),
    propertyDetailHighlightsEyebrow: preferText(
      propertyDetail.highlights.eyebrow,
      EMPTY_FORM.propertyDetailHighlightsEyebrow,
    ),
    propertyDetailHighlightsTitle: preferText(
      propertyDetail.highlights.title,
      EMPTY_FORM.propertyDetailHighlightsTitle,
    ),
    propertyDetailHighlightsDescription: preferText(
      propertyDetail.highlights.description,
      EMPTY_FORM.propertyDetailHighlightsDescription,
    ),
    propertyDetailHighlightsDivisibleLabel: preferText(
      propertyDetail.highlights.divisibleLabel,
      EMPTY_FORM.propertyDetailHighlightsDivisibleLabel,
    ),
    propertyDetailHighlightsIncentivesLabel: preferText(
      propertyDetail.highlights.incentivesLabel,
      EMPTY_FORM.propertyDetailHighlightsIncentivesLabel,
    ),
    propertyDetailHighlightsFloorPlanLabel: preferText(
      propertyDetail.highlights.floorPlanLabel,
      EMPTY_FORM.propertyDetailHighlightsFloorPlanLabel,
    ),
    propertyDetailHighlightsEmptyMessage: preferText(
      propertyDetail.highlights.emptyMessage,
      EMPTY_FORM.propertyDetailHighlightsEmptyMessage,
    ),
    propertyDetailLocationTitle: preferText(
      propertyDetail.location.title,
      EMPTY_FORM.propertyDetailLocationTitle,
    ),
    propertyDetailLocationDirectionsLabel: preferText(
      propertyDetail.location.directionsLabel,
      EMPTY_FORM.propertyDetailLocationDirectionsLabel,
    ),
    propertyDetailKeyFiguresTitle: preferText(
      propertyDetail.keyFigures.title,
      EMPTY_FORM.propertyDetailKeyFiguresTitle,
    ),
    propertyDetailKeyFiguresSubtitle: preferText(
      propertyDetail.keyFigures.subtitle,
      EMPTY_FORM.propertyDetailKeyFiguresSubtitle,
    ),
    propertyDetailKeyFiguresTotalRentableLabel: preferText(
      propertyDetail.keyFigures.totalRentableLabel,
      EMPTY_FORM.propertyDetailKeyFiguresTotalRentableLabel,
    ),
    propertyDetailKeyFiguresUnitsLiveLabel: preferText(
      propertyDetail.keyFigures.unitsLiveLabel,
      EMPTY_FORM.propertyDetailKeyFiguresUnitsLiveLabel,
    ),
    propertyDetailKeyFiguresIncentivesLabel: preferText(
      propertyDetail.keyFigures.incentivesLabel,
      EMPTY_FORM.propertyDetailKeyFiguresIncentivesLabel,
    ),
    propertyDetailKeyFiguresDivisibleLabel: preferText(
      propertyDetail.keyFigures.divisibleLabel,
      EMPTY_FORM.propertyDetailKeyFiguresDivisibleLabel,
    ),
    propertyDetailLabelsRentFallback: preferText(
      propertyDetail.labels.rentFallback,
      EMPTY_FORM.propertyDetailLabelsRentFallback,
    ),
    propertyDetailLabelsAvailabilityRangeFallback: preferText(
      propertyDetail.labels.availabilityRangeFallback,
      EMPTY_FORM.propertyDetailLabelsAvailabilityRangeFallback,
    ),
    propertyDetailLabelsAvailabilitySingleFallback: preferText(
      propertyDetail.labels.availabilitySingleFallback,
      EMPTY_FORM.propertyDetailLabelsAvailabilitySingleFallback,
    ),
    propertyDetailLabelsUnitImmediateLabel: preferText(
      propertyDetail.labels.unitAvailabilityImmediateLabel,
      EMPTY_FORM.propertyDetailLabelsUnitImmediateLabel,
    ),
    propertyDetailLabelsUnitNegotiableLabel: preferText(
      propertyDetail.labels.unitAvailabilityNegotiableLabel,
      EMPTY_FORM.propertyDetailLabelsUnitNegotiableLabel,
    ),
    unitDetailNotFoundMessage: preferText(
      unitDetail.messages.notFoundMessage,
      EMPTY_FORM.unitDetailNotFoundMessage,
    ),
    unitDetailUnavailableMessage: preferText(
      unitDetail.messages.unavailableMessage,
      EMPTY_FORM.unitDetailUnavailableMessage,
    ),
    unitDetailViewPropertyLabel: preferText(
      unitDetail.actions.viewPropertyLabel,
      EMPTY_FORM.unitDetailViewPropertyLabel,
    ),
    unitDetailBrowseAvailabilitiesLabel: preferText(
      unitDetail.actions.browseAvailabilitiesLabel,
      EMPTY_FORM.unitDetailBrowseAvailabilitiesLabel,
    ),
    unitDetailViewOtherUnitsLabel: preferText(
      unitDetail.actions.viewOtherUnitsLabel,
      EMPTY_FORM.unitDetailViewOtherUnitsLabel,
    ),
    unitDetailStickyEyebrow: preferText(
      unitDetail.actions.stickyEyebrow,
      EMPTY_FORM.unitDetailStickyEyebrow,
    ),
    unitDetailStickyButtonLabel: preferText(
      unitDetail.actions.stickyButtonLabel,
      EMPTY_FORM.unitDetailStickyButtonLabel,
    ),
    unitDetailAtAGlanceTitle: preferText(
      unitDetail.headings.atAGlanceTitle,
      EMPTY_FORM.unitDetailAtAGlanceTitle,
    ),
    unitDetailPropertyDetailsTitle: preferText(
      unitDetail.headings.propertyDetailsTitle,
      EMPTY_FORM.unitDetailPropertyDetailsTitle,
    ),
    unitDetailLeaseDetailsTitle: preferText(
      unitDetail.headings.leaseDetailsTitle,
      EMPTY_FORM.unitDetailLeaseDetailsTitle,
    ),
    unitDetailPropertyDescriptionEyebrow: preferText(
      unitDetail.headings.propertyDescriptionEyebrow,
      EMPTY_FORM.unitDetailPropertyDescriptionEyebrow,
    ),
    unitDetailPropertyDescriptionTitle: preferText(
      unitDetail.headings.propertyDescriptionTitle,
      EMPTY_FORM.unitDetailPropertyDescriptionTitle,
    ),
    unitDetailScheduleEyebrow: preferText(
      unitDetail.headings.scheduleEyebrow,
      EMPTY_FORM.unitDetailScheduleEyebrow,
    ),
    unitDetailScheduleTitle: preferText(
      unitDetail.headings.scheduleTitle,
      EMPTY_FORM.unitDetailScheduleTitle,
    ),
    unitDetailLeasingDetailsEyebrow: preferText(
      unitDetail.headings.leasingDetailsEyebrow,
      EMPTY_FORM.unitDetailLeasingDetailsEyebrow,
    ),
    unitDetailLeasingDetailsTitle: preferText(
      unitDetail.headings.leasingDetailsTitle,
      EMPTY_FORM.unitDetailLeasingDetailsTitle,
    ),
    unitDetailPhotoGalleryTitle: preferText(
      unitDetail.headings.photoGalleryTitle,
      EMPTY_FORM.unitDetailPhotoGalleryTitle,
    ),
    unitDetailSizeLabel: preferText(unitDetail.labels.sizeLabel, EMPTY_FORM.unitDetailSizeLabel),
    unitDetailFloorLabel: preferText(unitDetail.labels.floorLabel, EMPTY_FORM.unitDetailFloorLabel),
    unitDetailDivisibleLabel: preferText(
      unitDetail.labels.divisibleLabel,
      EMPTY_FORM.unitDetailDivisibleLabel,
    ),
    unitDetailStatusLabel: preferText(
      unitDetail.labels.statusLabel,
      EMPTY_FORM.unitDetailStatusLabel,
    ),
    unitDetailAvailabilityLabel: preferText(
      unitDetail.labels.availabilityLabel,
      EMPTY_FORM.unitDetailAvailabilityLabel,
    ),
    unitDetailPossessionLabel: preferText(
      unitDetail.labels.possessionLabel,
      EMPTY_FORM.unitDetailPossessionLabel,
    ),
    unitDetailUpdatedLabel: preferText(
      unitDetail.labels.updatedLabel,
      EMPTY_FORM.unitDetailUpdatedLabel,
    ),
    unitDetailImmediateLabel: preferText(
      unitDetail.labels.immediateLabel,
      EMPTY_FORM.unitDetailImmediateLabel,
    ),
    unitDetailNegotiableLabel: preferText(
      unitDetail.labels.negotiableLabel,
      EMPTY_FORM.unitDetailNegotiableLabel,
    ),
    unitDetailImmediatePossessionLabel: preferText(
      unitDetail.labels.immediatePossessionLabel,
      EMPTY_FORM.unitDetailImmediatePossessionLabel,
    ),
    unitDetailNegotiablePossessionLabel: preferText(
      unitDetail.labels.negotiablePossessionLabel,
      EMPTY_FORM.unitDetailNegotiablePossessionLabel,
    ),
    unitDetailUpdatedRecentlyLabel: preferText(
      unitDetail.labels.updatedRecentlyLabel,
      EMPTY_FORM.unitDetailUpdatedRecentlyLabel,
    ),
    unitDetailLeasingPromptIndustrial: preferText(
      unitDetail.leasingPrompts.industrial,
      EMPTY_FORM.unitDetailLeasingPromptIndustrial,
    ),
    unitDetailLeasingPromptFlex: preferText(
      unitDetail.leasingPrompts.flex,
      EMPTY_FORM.unitDetailLeasingPromptFlex,
    ),
    unitDetailLeasingPromptRetail: preferText(
      unitDetail.leasingPrompts.retail,
      EMPTY_FORM.unitDetailLeasingPromptRetail,
    ),
    unitDetailLeasingPromptOffice: preferText(
      unitDetail.leasingPrompts.office,
      EMPTY_FORM.unitDetailLeasingPromptOffice,
    ),
    unitDetailLeasingPromptFallback: preferText(
      unitDetail.leasingPrompts.fallback,
      EMPTY_FORM.unitDetailLeasingPromptFallback,
    ),
    unitDetailPhotoGalleryEmptyMessage: preferText(
      unitDetail.photoGalleryEmptyMessage,
      EMPTY_FORM.unitDetailPhotoGalleryEmptyMessage,
    ),
    showingDialogTitle: preferText(showingDialog.title, EMPTY_FORM.showingDialogTitle),
    showingDialogDescription: preferText(
      showingDialog.description,
      EMPTY_FORM.showingDialogDescription,
    ),
    showingDialogStepIndicatorTemplate: preferText(
      showingDialog.stepIndicatorTemplate,
      EMPTY_FORM.showingDialogStepIndicatorTemplate,
    ),
    showingDialogSelectedUnitLabel: preferText(
      showingDialog.selectedUnitLabel,
      EMPTY_FORM.showingDialogSelectedUnitLabel,
    ),
    showingDialogSelectedUnitDescription: preferText(
      showingDialog.selectedUnitDescription,
      EMPTY_FORM.showingDialogSelectedUnitDescription,
    ),
    showingDialogStepContactLabel: preferText(
      showingDialog.steps.contactLabel,
      EMPTY_FORM.showingDialogStepContactLabel,
    ),
    showingDialogStepScheduleLabel: preferText(
      showingDialog.steps.scheduleLabel,
      EMPTY_FORM.showingDialogStepScheduleLabel,
    ),
    showingDialogStepReviewLabel: preferText(
      showingDialog.steps.reviewLabel,
      EMPTY_FORM.showingDialogStepReviewLabel,
    ),
    showingDialogFieldFirstNameLabel: preferText(
      showingDialog.fields.firstNameLabel,
      EMPTY_FORM.showingDialogFieldFirstNameLabel,
    ),
    showingDialogFieldLastNameLabel: preferText(
      showingDialog.fields.lastNameLabel,
      EMPTY_FORM.showingDialogFieldLastNameLabel,
    ),
    showingDialogFieldEmailLabel: preferText(
      showingDialog.fields.emailLabel,
      EMPTY_FORM.showingDialogFieldEmailLabel,
    ),
    showingDialogFieldPhoneLabel: preferText(
      showingDialog.fields.phoneLabel,
      EMPTY_FORM.showingDialogFieldPhoneLabel,
    ),
    showingDialogFieldDateLabel: preferText(
      showingDialog.fields.dateLabel,
      EMPTY_FORM.showingDialogFieldDateLabel,
    ),
    showingDialogFieldTimeLabel: preferText(
      showingDialog.fields.timeLabel,
      EMPTY_FORM.showingDialogFieldTimeLabel,
    ),
    showingDialogFieldMessageLabel: preferText(
      showingDialog.fields.messageLabel,
      EMPTY_FORM.showingDialogFieldMessageLabel,
    ),
    showingDialogFieldDateHint: preferText(
      showingDialog.fieldHints.dateHint,
      EMPTY_FORM.showingDialogFieldDateHint,
    ),
    showingDialogFieldTimeHint: preferText(
      showingDialog.fieldHints.timeHint,
      EMPTY_FORM.showingDialogFieldTimeHint,
    ),
    showingDialogMessagePlaceholder: preferText(
      showingDialog.messagePlaceholder,
      EMPTY_FORM.showingDialogMessagePlaceholder,
    ),
    showingDialogReviewTitle: preferText(
      showingDialog.review.title,
      EMPTY_FORM.showingDialogReviewTitle,
    ),
    showingDialogReviewContactLabel: preferText(
      showingDialog.review.contactLabel,
      EMPTY_FORM.showingDialogReviewContactLabel,
    ),
    showingDialogReviewSlotLabel: preferText(
      showingDialog.review.slotLabel,
      EMPTY_FORM.showingDialogReviewSlotLabel,
    ),
    showingDialogReviewUnitLabel: preferText(
      showingDialog.review.unitLabel,
      EMPTY_FORM.showingDialogReviewUnitLabel,
    ),
    showingDialogReviewMessageLabel: preferText(
      showingDialog.review.messageLabel,
      EMPTY_FORM.showingDialogReviewMessageLabel,
    ),
    showingDialogReviewConfirmationNote: preferText(
      showingDialog.review.confirmationNote,
      EMPTY_FORM.showingDialogReviewConfirmationNote,
    ),
    showingDialogReviewGuidanceNext: preferText(
      showingDialog.review.guidanceNext,
      EMPTY_FORM.showingDialogReviewGuidanceNext,
    ),
    showingDialogReviewGuidanceReview: preferText(
      showingDialog.review.guidanceReview,
      EMPTY_FORM.showingDialogReviewGuidanceReview,
    ),
    showingDialogButtonCancelLabel: preferText(
      showingDialog.buttons.cancelLabel,
      EMPTY_FORM.showingDialogButtonCancelLabel,
    ),
    showingDialogButtonBackLabel: preferText(
      showingDialog.buttons.backLabel,
      EMPTY_FORM.showingDialogButtonBackLabel,
    ),
    showingDialogButtonNextLabel: preferText(
      showingDialog.buttons.nextLabel,
      EMPTY_FORM.showingDialogButtonNextLabel,
    ),
    showingDialogButtonConfirmLabel: preferText(
      showingDialog.buttons.confirmLabel,
      EMPTY_FORM.showingDialogButtonConfirmLabel,
    ),
    showingDialogButtonDoneLabel: preferText(
      showingDialog.buttons.doneLabel,
      EMPTY_FORM.showingDialogButtonDoneLabel,
    ),
    showingDialogErrorContactMissing: preferText(
      showingDialog.errors.contactMissing,
      EMPTY_FORM.showingDialogErrorContactMissing,
    ),
    showingDialogErrorScheduleMissing: preferText(
      showingDialog.errors.scheduleMissing,
      EMPTY_FORM.showingDialogErrorScheduleMissing,
    ),
    showingDialogErrorSubmit: preferText(
      showingDialog.errors.submitError,
      EMPTY_FORM.showingDialogErrorSubmit,
    ),
    showingDialogSuccessTitle: preferText(
      showingDialog.success.title,
      EMPTY_FORM.showingDialogSuccessTitle,
    ),
    showingDialogSuccessDescription: preferText(
      showingDialog.success.description,
      EMPTY_FORM.showingDialogSuccessDescription,
    ),
    showingDialogSuccessTimeLabel: preferText(
      showingDialog.success.timeLabel,
      EMPTY_FORM.showingDialogSuccessTimeLabel,
    ),
    showingDialogSuccessEmailLabel: preferText(
      showingDialog.success.emailLabel,
      EMPTY_FORM.showingDialogSuccessEmailLabel,
    ),
    showingDialogSuccessNameLabel: preferText(
      showingDialog.success.nameLabel,
      EMPTY_FORM.showingDialogSuccessNameLabel,
    ),
    showingDialogSuccessMessageLabel: preferText(
      showingDialog.success.messageLabel,
      EMPTY_FORM.showingDialogSuccessMessageLabel,
    ),
    showingDialogSuccessCalendarLabel: preferText(
      showingDialog.success.calendarLabel,
      EMPTY_FORM.showingDialogSuccessCalendarLabel,
    ),
    showingDialogSuccessPortalQueued: preferText(
      showingDialog.success.portalInviteQueued,
      EMPTY_FORM.showingDialogSuccessPortalQueued,
    ),
    showingDialogSuccessPortalSent: preferText(
      showingDialog.success.portalInviteSent,
      EMPTY_FORM.showingDialogSuccessPortalSent,
    ),
    showingDialogUnavailableTooltip: preferText(
      showingDialog.unavailableTooltip,
      EMPTY_FORM.showingDialogUnavailableTooltip,
    ),
    sharedContactAddressLabel: preferText(
      sharedContent.contactSection.addressLabel,
      EMPTY_FORM.sharedContactAddressLabel,
    ),
    sharedContactPhoneLabel: preferText(
      sharedContent.contactSection.phoneLabel,
      EMPTY_FORM.sharedContactPhoneLabel,
    ),
    sharedContactEmailLabel: preferText(
      sharedContent.contactSection.emailLabel,
      EMPTY_FORM.sharedContactEmailLabel,
    ),
    sharedContactHoursLabel: preferText(
      sharedContent.contactSection.hoursLabel,
      EMPTY_FORM.sharedContactHoursLabel,
    ),
    sharedContactLeadershipLabel: preferText(
      sharedContent.contactSection.leadershipLabel,
      EMPTY_FORM.sharedContactLeadershipLabel,
    ),
    sharedContactMapTitle: preferText(
      sharedContent.contactSection.mapTitle,
      EMPTY_FORM.sharedContactMapTitle,
    ),
    sharedContactMapPreviewTitle: preferText(
      sharedContent.contactSection.mapPreviewTitle,
      EMPTY_FORM.sharedContactMapPreviewTitle,
    ),
    sharedContactMapPreviewDescription: preferText(
      sharedContent.contactSection.mapPreviewDescription,
      EMPTY_FORM.sharedContactMapPreviewDescription,
    ),
    sharedContactMapFooter: preferText(
      sharedContent.contactSection.mapFooter,
      EMPTY_FORM.sharedContactMapFooter,
    ),
    sharedContactMapLinkLabel: preferText(
      sharedContent.contactSection.mapLinkLabel,
      EMPTY_FORM.sharedContactMapLinkLabel,
    ),
    sharedAvailabilityAlertsEyebrow: preferText(
      sharedContent.availabilityAlerts.eyebrow,
      EMPTY_FORM.sharedAvailabilityAlertsEyebrow,
    ),
    sharedAvailabilityAlertsTitle: preferText(
      sharedContent.availabilityAlerts.title,
      EMPTY_FORM.sharedAvailabilityAlertsTitle,
    ),
    sharedAvailabilityAlertsDescription: preferText(
      sharedContent.availabilityAlerts.description,
      EMPTY_FORM.sharedAvailabilityAlertsDescription,
    ),
    sharedAvailabilityAlertsEmailLabel: preferText(
      sharedContent.availabilityAlerts.emailLabel,
      EMPTY_FORM.sharedAvailabilityAlertsEmailLabel,
    ),
    sharedAvailabilityAlertsEmailPlaceholder: preferText(
      sharedContent.availabilityAlerts.emailPlaceholder,
      EMPTY_FORM.sharedAvailabilityAlertsEmailPlaceholder,
    ),
    sharedAvailabilityAlertsSubmitLabel: preferText(
      sharedContent.availabilityAlerts.submitLabel,
      EMPTY_FORM.sharedAvailabilityAlertsSubmitLabel,
    ),
    sharedAvailabilityAlertsSuccessMessage: preferText(
      sharedContent.availabilityAlerts.successMessage,
      EMPTY_FORM.sharedAvailabilityAlertsSuccessMessage,
    ),
    sharedAvailabilityAlertsErrorMessage: preferText(
      sharedContent.availabilityAlerts.errorMessage,
      EMPTY_FORM.sharedAvailabilityAlertsErrorMessage,
    ),
    sharedAvailabilityAlertsNote: preferText(
      sharedContent.availabilityAlerts.note,
      EMPTY_FORM.sharedAvailabilityAlertsNote,
    ),
    sharedPropertyCardPhotoFallback: preferText(
      sharedContent.propertyCard.photoFallback,
      EMPTY_FORM.sharedPropertyCardPhotoFallback,
    ),
    sharedPropertyCardRentFallback: preferText(
      sharedContent.propertyCard.rentFallback,
      EMPTY_FORM.sharedPropertyCardRentFallback,
    ),
    sharedPropertyCardAvailabilityFallback: preferText(
      sharedContent.propertyCard.availabilityFallback,
      EMPTY_FORM.sharedPropertyCardAvailabilityFallback,
    ),
    sharedPropertyCardDivisibleLabel: preferText(
      sharedContent.propertyCard.divisibleLabel,
      EMPTY_FORM.sharedPropertyCardDivisibleLabel,
    ),
    sharedPropertyCardVacancyLabel: preferText(
      sharedContent.propertyCard.vacancyLabel,
      EMPTY_FORM.sharedPropertyCardVacancyLabel,
    ),
    sharedPropertyCardVacancyAvailableLabel: preferText(
      sharedContent.propertyCard.vacancyAvailableLabel,
      EMPTY_FORM.sharedPropertyCardVacancyAvailableLabel,
    ),
    sharedPropertyCardVacancyLeasedLabel: preferText(
      sharedContent.propertyCard.vacancyLeasedLabel,
      EMPTY_FORM.sharedPropertyCardVacancyLeasedLabel,
    ),
    sharedPropertyCardSizeLabel: preferText(
      sharedContent.propertyCard.sizeLabel,
      EMPTY_FORM.sharedPropertyCardSizeLabel,
    ),
    sharedPropertyCardUnitCountZeroLabel: preferText(
      sharedContent.propertyCard.unitCountZeroLabel,
      EMPTY_FORM.sharedPropertyCardUnitCountZeroLabel,
    ),
    sharedPropertyCardUnitCountSingularLabel: preferText(
      sharedContent.propertyCard.unitCountSingularLabel,
      EMPTY_FORM.sharedPropertyCardUnitCountSingularLabel,
    ),
    sharedPropertyCardUnitCountPluralLabel: preferText(
      sharedContent.propertyCard.unitCountPluralLabel,
      EMPTY_FORM.sharedPropertyCardUnitCountPluralLabel,
    ),
    sharedUnitCardPhotoFallback: preferText(
      sharedContent.unitCard.photoFallback,
      EMPTY_FORM.sharedUnitCardPhotoFallback,
    ),
    sharedUnitCardAvailabilityLabel: preferText(
      sharedContent.unitCard.availabilityLabel,
      EMPTY_FORM.sharedUnitCardAvailabilityLabel,
    ),
    sharedUnitCardAvailabilityFallback: preferText(
      sharedContent.unitCard.availabilityFallback,
      EMPTY_FORM.sharedUnitCardAvailabilityFallback,
    ),
    sharedUnitCardFloorLabel: preferText(
      sharedContent.unitCard.floorLabel,
      EMPTY_FORM.sharedUnitCardFloorLabel,
    ),
    sharedUnitCardViewDetailsLabel: preferText(
      sharedContent.unitCard.viewDetailsLabel,
      EMPTY_FORM.sharedUnitCardViewDetailsLabel,
    ),
    sharedUnitCardDivisibleLabel: preferText(
      sharedContent.unitCard.divisibleLabel,
      EMPTY_FORM.sharedUnitCardDivisibleLabel,
    ),
    sharedEmptyStatePropertyGrid: preferText(
      sharedContent.emptyStates.propertyGridEmpty,
      EMPTY_FORM.sharedEmptyStatePropertyGrid,
    ),
    sharedEmptyStateUnitGroup: preferText(
      sharedContent.emptyStates.unitGroupEmpty,
      EMPTY_FORM.sharedEmptyStateUnitGroup,
    ),
  };
}

function mapFormToUpdateInput(values: SiteSettingsFormValues): SiteSettingsUpdateInput {
  const brandLogoUrl = values.brandLogoUrl.trim();
  const brandFaviconUrl = values.brandFaviconUrl.trim();
  const brandDefaultOgImageUrl = values.brandDefaultOgImageUrl.trim();
  const normalizeStringList = (items: string[]) =>
    items.map((entry) => entry.trim()).filter((entry) => entry.length > 0);
  const normalizeTitleDescriptionList = (items: Array<{ title: string; description: string }>) =>
    items
      .map((entry) => ({
        title: entry.title.trim(),
        description: entry.description.trim(),
      }))
      .filter((entry) => entry.title.length > 0 || entry.description.length > 0);
  const normalizeLabelValueList = (items: Array<{ label: string; value: string }>) =>
    items
      .map((entry) => ({ label: entry.label.trim(), value: entry.value.trim() }))
      .filter((entry) => entry.label.length > 0 || entry.value.length > 0);
  const normalizeValueLabelList = (items: Array<{ value: string; label: string }>) =>
    items
      .map((entry) => ({ value: entry.value.trim(), label: entry.label.trim() }))
      .filter((entry) => entry.value.length > 0 || entry.label.length > 0);

  const contactAddressLines = [values.contactAddressLine1, values.contactAddressLine2 ?? '']
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

  const contactHours = values.contactHours
    .map((entry) => ({ label: entry.label.trim(), value: entry.value.trim() }))
    .filter((entry) => entry.label.length > 0 || entry.value.length > 0);

  const leadershipContacts = values.leadershipContacts
    .map((entry) => ({ name: entry.name.trim(), title: (entry.title ?? '').trim() }))
    .filter((entry) => entry.name.length > 0 || entry.title.length > 0);

  const featuredProperties = values.aboutFeaturedProperties
    .split(/\r?\n/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

  const paragraphs = values.aboutParagraphs
    .split(/\n\n+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

  const navigationLinks = values.navigationLinks
    .map((entry, index) => {
      const label = entry.label.trim();
      const rawHref = entry.href.trim();
      const children = entry.children
        .map((child, childIndex) => {
          const propertyId = child.propertyId.trim();
          const labelText = child.label.trim();
          const hrefText = child.href.trim();
          const enabled = child.enabled;
          const external = child.external;
          const order = childIndex + 1;
          const hasProperty = propertyId.length > 0;

          if (hasProperty) {
            return {
              label: labelText,
              href: '',
              enabled,
              external: false,
              order,
              propertyId,
            };
          }

          if (labelText.length === 0 || hrefText.length === 0) {
            return null;
          }

          return {
            label: labelText,
            href: hrefText,
            enabled,
            external,
            order,
          };
        })
        .filter((child): child is NonNullable<typeof child> => child !== null);
      const hasChildren = children.length > 0;
      const action = hasChildren ? 'link' : resolveNavigationAction(entry.action);
      const style = hasChildren ? 'link' : resolveNavigationStyle(entry.style);
      const href = action === 'link' ? rawHref : action === 'call' ? rawHref : '';
      const sanitizedHref =
        action === 'call' && href.length > 0 && !href.startsWith('tel:') ? '' : href;
      const external = action === 'link' ? entry.external : false;

      if (
        label.length === 0 ||
        (children.length === 0 && sanitizedHref.length === 0 && action === 'link')
      ) {
        return null;
      }

      return {
        label,
        href: hasChildren ? '' : sanitizedHref,
        enabled: entry.enabled,
        external,
        order: index + 1,
        action,
        style,
        ...(hasChildren ? { children } : {}),
      };
    })
    .filter(
      (entry): entry is SiteSettingsUpdateInput['navigation']['links'][number] => entry !== null,
    );

  return {
    brand: {
      siteName: values.brandSiteName,
      tagline: values.brandTagline,
      logoUrl: brandLogoUrl.length > 0 ? brandLogoUrl : null,
      faviconUrl: brandFaviconUrl.length > 0 ? brandFaviconUrl : null,
      defaultOgImageUrl: brandDefaultOgImageUrl.length > 0 ? brandDefaultOgImageUrl : null,
    },
    navigation: {
      links: navigationLinks,
      mobileTitle: values.navigationMobileTitle,
      mobileDescription: values.navigationMobileDescription,
      mobileShortcutHeading: values.navigationMobileShortcutHeading,
      mobileShortcutDescription: values.navigationMobileShortcutDescription,
      mobileShortcutPrimary: {
        label: values.navigationMobilePrimaryLabel,
        href: values.navigationMobilePrimaryHref,
      },
      mobileShortcutSecondary: {
        label: values.navigationMobileSecondaryLabel,
        href: values.navigationMobileSecondaryHref,
      },
    },
    footer: {
      companyName: values.footerCompanyName,
      copyrightText: values.footerCopyrightText,
    },
    seo: {
      defaultDescription: values.seoDefaultDescription,
      canonicalOrigin: values.seoCanonicalOrigin,
    },
    bookingEmail: values.bookingEmail,
    contactEmail: values.contactEmail,
    contactPhone: values.contactPhone,
    contactAddressLines,
    contactHours,
    leadershipContacts,
    mapEmbedUrl: values.mapEmbedUrl,
    home: {
      hero: {
        eyebrow: values.homeHeroEyebrow,
        title: values.homeHeroTitle,
        description: values.homeHeroDescription,
      },
      availability: {
        eyebrow: values.homeAvailabilityEyebrow,
        title: values.homeAvailabilityTitle,
        description: values.homeAvailabilityDescription,
        ctaLabel: values.homeAvailabilityCtaLabel,
        ctaHref: values.homeAvailabilityCtaHref,
      },
      contact: {
        heading: values.homeContactHeading,
        description: values.homeContactDescription,
      },
      seo: {
        title: values.homeSeoTitle,
        description: values.homeSeoDescription,
      },
      heroMedia: {
        label: values.homeHeroMediaLabel,
        location: values.homeHeroMediaLocation,
        ariaLabel: values.homeHeroMediaAriaLabel,
        url: values.homeHeroMediaUrl,
        mediaType: values.homeHeroMediaType,
      },
      availabilityMessages: {
        emptyMessage: values.homeAvailabilityEmptyMessage,
        errorMessage: values.homeAvailabilityErrorMessage,
      },
    },
    leadCapture: {
      eyebrow: values.leadCaptureEyebrow,
      title: values.leadCaptureTitle,
      description: values.leadCaptureDescription,
      thankYou: values.leadCaptureThankYou,
      seo: {
        title: values.leadSeoTitle,
        description: values.leadSeoDescription,
      },
      thankYouTitle: values.leadCaptureThankYouTitle,
      benefitsHeading: values.leadCaptureBenefitsHeading,
      benefits: normalizeTitleDescriptionList(values.leadCaptureBenefits),
      stats: normalizeValueLabelList(values.leadCaptureStats),
      form: {
        fullNameLabel: values.leadFormFullNameLabel,
        emailLabel: values.leadFormEmailLabel,
        phoneLabel: values.leadFormPhoneLabel,
        budgetLabel: values.leadFormBudgetLabel,
        moveInLabel: values.leadFormMoveInLabel,
        notesLabel: values.leadFormNotesLabel,
        submitLabel: values.leadFormSubmitLabel,
        consentText: values.leadFormConsentText,
        successToast: values.leadFormSuccessToast,
        errorToast: values.leadFormErrorToast,
      },
    },
    about: {
      heroTitle: values.aboutHeroTitle,
      heroSubtitle: values.aboutHeroSubtitle,
      tagline: values.aboutTagline,
      heroPrimaryCtaLabel: values.aboutHeroPrimaryCtaLabel,
      heroPrimaryCtaHref: values.aboutHeroPrimaryCtaHref,
      heroSecondaryCtaLabel: values.aboutHeroSecondaryCtaLabel,
      heroSecondaryCtaHref: values.aboutHeroSecondaryCtaHref,
      highlightTitle: values.aboutHighlightTitle,
      highlightDescription: values.aboutHighlightDescription,
      featuredProperties,
      snapshotEyebrow: values.aboutSnapshotEyebrow,
      snapshotStats: normalizeLabelValueList(values.aboutSnapshotStats),
      bodyHeading: values.aboutBodyHeading,
      bodyDescription: values.aboutBodyDescription,
      paragraphs,
      detailsSummaryLabel: values.aboutDetailsSummaryLabel,
      promiseLabel: values.aboutPromiseLabel,
      seo: {
        title: values.aboutSeoTitle,
        description: values.aboutSeoDescription,
      },
      values: {
        title: values.aboutValuesTitle,
        description: values.aboutValuesDescription,
        ctaLabel: values.aboutValuesCtaLabel,
        ctaHref: values.aboutValuesCtaHref,
        pillars: normalizeTitleDescriptionList(values.aboutValuesPillars),
      },
      portfolio: {
        title: values.aboutPortfolioTitle,
        description: values.aboutPortfolioDescription,
        ctaLabel: values.aboutPortfolioCtaLabel,
        ctaHref: values.aboutPortfolioCtaHref,
        flagshipEyebrow: values.aboutPortfolioFlagshipEyebrow,
        flagshipTitle: values.aboutPortfolioFlagshipTitle,
        flagshipDescription: values.aboutPortfolioFlagshipDescription,
        flagshipStats: normalizeLabelValueList(values.aboutPortfolioFlagshipStats),
        flagshipFeaturesHeading: values.aboutPortfolioFlagshipFeaturesHeading,
        flagshipFeaturesToggleLabel: values.aboutPortfolioFlagshipFeaturesToggleLabel,
        flagshipFeatures: normalizeStringList(values.aboutPortfolioFlagshipFeatures),
        downtownEyebrow: values.aboutPortfolioDowntownEyebrow,
        downtownTitle: values.aboutPortfolioDowntownTitle,
        downtownDescription: values.aboutPortfolioDowntownDescription,
        downtownIdealHeading: values.aboutPortfolioDowntownIdealHeading,
        downtownTenants: normalizeStringList(values.aboutPortfolioDowntownTenants),
        downtownAddressesHeading: values.aboutPortfolioDowntownAddressesHeading,
        retailEyebrow: values.aboutPortfolioRetailEyebrow,
        retailTitle: values.aboutPortfolioRetailTitle,
        retailDescription: values.aboutPortfolioRetailDescription,
        retailPlazas: normalizeStringList(values.aboutPortfolioRetailPlazas),
        retailFooter: values.aboutPortfolioRetailFooter,
        retailFooterLinkLabel: values.aboutPortfolioRetailFooterLinkLabel,
      },
      contactHeading: values.aboutContactHeading,
      contactDescription: values.aboutContactDescription,
    },
    publicContent: {
      contactPage: {
        seo: {
          title: values.contactPageSeoTitle,
          description: values.contactPageSeoDescription,
        },
        hero: {
          eyebrow: values.contactPageHeroEyebrow,
          title: values.contactPageHeroTitle,
          description: values.contactPageHeroDescription,
          responseTime: values.contactPageHeroResponseTime,
        },
        heroCard: {
          eyebrow: values.contactPageHeroCardEyebrow,
          title: values.contactPageHeroCardTitle,
          description: values.contactPageHeroCardDescription,
          callLabel: values.contactPageHeroCardCallLabel,
          emailLabel: values.contactPageHeroCardEmailLabel,
          mapLabel: values.contactPageHeroCardMapLabel,
        },
        body: {
          title: values.contactPageSupportTitle,
          description: values.contactPageSupportDescription,
          highlights: normalizeStringList(values.contactPageSupportHighlights),
          responseTimeLabel: values.contactPageSupportResponseTimeLabel,
          responseTimeValue: values.contactPageSupportResponseTimeValue,
          directionsLabel: values.contactPageSupportDirectionsLabel,
          availabilitiesLabel: values.contactPageSupportAvailabilitiesLabel,
        },
        details: {
          title: values.contactPageDetailsTitle,
          description: values.contactPageDetailsDescription,
        },
        form: {
          title: values.contactPageFormTitle,
          description: values.contactPageFormDescription,
          successMessage: values.contactPageFormSuccessMessage,
          errorMessage: values.contactPageFormErrorMessage,
          helperText: values.contactPageFormHelperText,
          submitLabel: values.contactPageFormSubmitLabel,
          fields: {
            fullNameLabel: values.contactPageFormFullNameLabel,
            phoneLabel: values.contactPageFormPhoneLabel,
            emailLabel: values.contactPageFormEmailLabel,
            messageLabel: values.contactPageFormMessageLabel,
            fullNamePlaceholder: values.contactPageFormFullNamePlaceholder,
            phonePlaceholder: values.contactPageFormPhonePlaceholder,
            emailPlaceholder: values.contactPageFormEmailPlaceholder,
            messagePlaceholder: values.contactPageFormMessagePlaceholder,
            spaceTypeLabel: values.contactPageFormSpaceTypeLabel,
            spaceTypePlaceholder: values.contactPageFormSpaceTypePlaceholder,
            spaceTypeOptions: normalizeStringList(values.contactPageFormSpaceTypeOptions),
            targetSizeLabel: values.contactPageFormTargetSizeLabel,
            targetSizePlaceholder: values.contactPageFormTargetSizePlaceholder,
            timelineLabel: values.contactPageFormTimelineLabel,
            timelinePlaceholder: values.contactPageFormTimelinePlaceholder,
            timelineOptions: normalizeStringList(values.contactPageFormTimelineOptions),
          },
        },
      },
      availabilitiesPage: {
        seo: {
          title: values.availabilitiesSeoTitle,
          description: values.availabilitiesSeoDescription,
        },
        heading: {
          eyebrow: values.availabilitiesHeadingEyebrow,
          title: values.availabilitiesHeadingTitle,
          description: values.availabilitiesHeadingDescription,
        },
        filters: {
          title: values.availabilitiesFiltersTitle,
          subtitle: values.availabilitiesFiltersSubtitle,
          emptyLabel: values.availabilitiesFiltersEmptyLabel,
          activeTemplate: values.availabilitiesFiltersActiveTemplate,
          resetLabel: values.availabilitiesFiltersResetLabel,
          openLabel: values.availabilitiesFiltersOpenLabel,
          drawerTitle: values.availabilitiesFiltersDrawerTitle,
          drawerApplyLabel: values.availabilitiesFiltersDrawerApplyLabel,
          drawerResetLabel: values.availabilitiesFiltersDrawerResetLabel,
          chipPriceLabel: values.availabilitiesFiltersChipPriceLabel,
          chipSizeLabel: values.availabilitiesFiltersChipSizeLabel,
          chipUseLabel: values.availabilitiesFiltersChipUseLabel,
        },
        viewToggle: {
          label: values.availabilitiesViewLabel,
          cardsLabel: values.availabilitiesViewCardsLabel,
          mapLabel: values.availabilitiesViewMapLabel,
        },
        filterControls: {
          priceTitle: values.availabilitiesFilterPriceTitle,
          priceDescription: values.availabilitiesFilterPriceDescription,
          sizeTitle: values.availabilitiesFilterSizeTitle,
          sizeDescription: values.availabilitiesFilterSizeDescription,
          usesTitle: values.availabilitiesFilterUsesTitle,
          usesDescription: values.availabilitiesFilterUsesDescription,
          minLabel: values.availabilitiesFilterMinLabel,
          maxLabel: values.availabilitiesFilterMaxLabel,
          placeholder: values.availabilitiesFilterPlaceholder,
        },
        results: {
          noMatchesMessage: values.availabilitiesResultsNoMatchesMessage,
          noAvailabilityMessage: values.availabilitiesResultsNoAvailabilityMessage,
          clearFiltersLabel: values.availabilitiesResultsClearFiltersLabel,
          errorMessage: values.availabilitiesResultsErrorMessage,
        },
        list: {
          addressEyebrow: values.availabilitiesListAddressEyebrow,
          unitCountSingular: values.availabilitiesListUnitCountSingular,
          unitCountPlural: values.availabilitiesListUnitCountPlural,
          viewPropertyLabel: values.availabilitiesListViewPropertyLabel,
        },
        mapPanel: {
          backLabel: values.availabilitiesMapBackLabel,
          addressEyebrow: values.availabilitiesMapAddressEyebrow,
          addressesEyebrow: values.availabilitiesMapAddressesEyebrow,
          browseTitle: values.availabilitiesMapBrowseTitle,
          browseDescription: values.availabilitiesMapBrowseDescription,
          selectUnitEyebrow: values.availabilitiesMapSelectUnitEyebrow,
          selectUnitDescription: values.availabilitiesMapSelectUnitDescription,
          tapPinEyebrow: values.availabilitiesMapTapPinEyebrow,
          tapPinDescription: values.availabilitiesMapTapPinDescription,
          suiteCountSingular: values.availabilitiesMapSuiteCountSingular,
          suiteCountPlural: values.availabilitiesMapSuiteCountPlural,
          mapPinFallback: values.availabilitiesMapPinFallback,
        },
        mapUnitRow: {
          unitLabelTemplate: values.availabilitiesMapUnitLabelTemplate,
          levelLabelTemplate: values.availabilitiesMapLevelLabelTemplate,
          divisibleLabel: values.availabilitiesMapDivisibleLabel,
          incentivesLabel: values.availabilitiesMapIncentivesLabel,
        },
        quickView: {
          eyebrow: values.availabilitiesQuickViewEyebrow,
          titleTemplate: values.availabilitiesQuickViewTitleTemplate,
          rentLabel: values.availabilitiesQuickViewRentLabel,
          rentSuffix: values.availabilitiesQuickViewRentSuffix,
          sizeLabel: values.availabilitiesQuickViewSizeLabel,
          availabilityLabel: values.availabilitiesQuickViewAvailabilityLabel,
          scheduleLabel: values.availabilitiesQuickViewScheduleLabel,
          detailsLabel: values.availabilitiesQuickViewDetailsLabel,
          divisibleLabel: values.availabilitiesQuickViewDivisibleLabel,
          incentivesLabel: values.availabilitiesQuickViewIncentivesLabel,
        },
      },
      propertyDetail: {
        messages: {
          unpublishedMessage: values.propertyDetailMessagesUnpublished,
          ctaLabel: values.propertyDetailMessagesCtaLabel,
        },
        navigation: {
          overviewLabel: values.propertyDetailNavOverview,
          highlightsLabel: values.propertyDetailNavHighlights,
          locationLabel: values.propertyDetailNavLocation,
          availabilityLabel: values.propertyDetailNavAvailability,
        },
        availabilityStrip: {
          noUnitsLabel: values.propertyDetailAvailabilityNoUnits,
          availableUnitsTemplate: values.propertyDetailAvailabilityTemplate,
          updatedLabel: values.propertyDetailAvailabilityUpdatedLabel,
        },
        overview: {
          eyebrow: values.propertyDetailOverviewEyebrow,
          viewOnMapLabel: values.propertyDetailOverviewViewOnMapLabel,
          titleTemplate: values.propertyDetailOverviewTitleTemplate,
        },
        units: {
          eyebrow: values.propertyDetailUnitsEyebrow,
          title: values.propertyDetailUnitsTitle,
          listedTemplate: values.propertyDetailUnitsListedTemplate,
          emptyAllMessage: values.propertyDetailUnitsEmptyAll,
          emptyFilteredMessage: values.propertyDetailUnitsEmptyFiltered,
          unitCountSingular: values.propertyDetailUnitsCountSingular,
          unitCountPlural: values.propertyDetailUnitsCountPlural,
          floorPlanFallback: values.propertyDetailUnitsFloorPlanFallback,
          monthlyLeaseLabel: values.propertyDetailUnitsMonthlyLeaseLabel,
          contactLabel: values.propertyDetailUnitsContactLabel,
          contactFallback: values.propertyDetailUnitsContactFallback,
          bookButtonLabel: values.propertyDetailUnitsBookButtonLabel,
          viewDetailsLabel: values.propertyDetailUnitsViewDetailsLabel,
        },
        unitsTable: {
          suiteHeader: values.propertyDetailUnitsTableSuiteHeader,
          floorHeader: values.propertyDetailUnitsTableFloorHeader,
          sizeHeader: values.propertyDetailUnitsTableSizeHeader,
          monthlyLeaseHeader: values.propertyDetailUnitsTableMonthlyLeaseHeader,
          rateHeader: values.propertyDetailUnitsTableRateHeader,
          actionHeader: values.propertyDetailUnitsTableActionHeader,
          noUnitsMessage: values.propertyDetailUnitsTableNoUnitsMessage,
          noMatchesMessage: values.propertyDetailUnitsTableNoMatchesMessage,
        },
        unitFilters: {
          sizeLabel: values.propertyDetailUnitFiltersSizeLabel,
          floorLabel: values.propertyDetailUnitFiltersFloorLabel,
          rateLabel: values.propertyDetailUnitFiltersRateLabel,
          sizeAllLabel: values.propertyDetailUnitFiltersSizeAllLabel,
          floorAllLabel: values.propertyDetailUnitFiltersFloorAllLabel,
          rateAllLabel: values.propertyDetailUnitFiltersRateAllLabel,
        },
        highlights: {
          eyebrow: values.propertyDetailHighlightsEyebrow,
          title: values.propertyDetailHighlightsTitle,
          description: values.propertyDetailHighlightsDescription,
          divisibleLabel: values.propertyDetailHighlightsDivisibleLabel,
          incentivesLabel: values.propertyDetailHighlightsIncentivesLabel,
          floorPlanLabel: values.propertyDetailHighlightsFloorPlanLabel,
          emptyMessage: values.propertyDetailHighlightsEmptyMessage,
        },
        location: {
          title: values.propertyDetailLocationTitle,
          directionsLabel: values.propertyDetailLocationDirectionsLabel,
        },
        keyFigures: {
          title: values.propertyDetailKeyFiguresTitle,
          subtitle: values.propertyDetailKeyFiguresSubtitle,
          totalRentableLabel: values.propertyDetailKeyFiguresTotalRentableLabel,
          unitsLiveLabel: values.propertyDetailKeyFiguresUnitsLiveLabel,
          incentivesLabel: values.propertyDetailKeyFiguresIncentivesLabel,
          divisibleLabel: values.propertyDetailKeyFiguresDivisibleLabel,
        },
        labels: {
          rentFallback: values.propertyDetailLabelsRentFallback,
          availabilityRangeFallback: values.propertyDetailLabelsAvailabilityRangeFallback,
          availabilitySingleFallback: values.propertyDetailLabelsAvailabilitySingleFallback,
          unitAvailabilityImmediateLabel: values.propertyDetailLabelsUnitImmediateLabel,
          unitAvailabilityNegotiableLabel: values.propertyDetailLabelsUnitNegotiableLabel,
        },
      },
      unitDetail: {
        messages: {
          notFoundMessage: values.unitDetailNotFoundMessage,
          unavailableMessage: values.unitDetailUnavailableMessage,
        },
        actions: {
          viewPropertyLabel: values.unitDetailViewPropertyLabel,
          browseAvailabilitiesLabel: values.unitDetailBrowseAvailabilitiesLabel,
          viewOtherUnitsLabel: values.unitDetailViewOtherUnitsLabel,
          stickyEyebrow: values.unitDetailStickyEyebrow,
          stickyButtonLabel: values.unitDetailStickyButtonLabel,
        },
        headings: {
          atAGlanceTitle: values.unitDetailAtAGlanceTitle,
          propertyDetailsTitle: values.unitDetailPropertyDetailsTitle,
          leaseDetailsTitle: values.unitDetailLeaseDetailsTitle,
          propertyDescriptionEyebrow: values.unitDetailPropertyDescriptionEyebrow,
          propertyDescriptionTitle: values.unitDetailPropertyDescriptionTitle,
          scheduleEyebrow: values.unitDetailScheduleEyebrow,
          scheduleTitle: values.unitDetailScheduleTitle,
          leasingDetailsEyebrow: values.unitDetailLeasingDetailsEyebrow,
          leasingDetailsTitle: values.unitDetailLeasingDetailsTitle,
          photoGalleryTitle: values.unitDetailPhotoGalleryTitle,
        },
        labels: {
          sizeLabel: values.unitDetailSizeLabel,
          floorLabel: values.unitDetailFloorLabel,
          divisibleLabel: values.unitDetailDivisibleLabel,
          statusLabel: values.unitDetailStatusLabel,
          availabilityLabel: values.unitDetailAvailabilityLabel,
          possessionLabel: values.unitDetailPossessionLabel,
          updatedLabel: values.unitDetailUpdatedLabel,
          immediateLabel: values.unitDetailImmediateLabel,
          negotiableLabel: values.unitDetailNegotiableLabel,
          immediatePossessionLabel: values.unitDetailImmediatePossessionLabel,
          negotiablePossessionLabel: values.unitDetailNegotiablePossessionLabel,
          updatedRecentlyLabel: values.unitDetailUpdatedRecentlyLabel,
        },
        leasingPrompts: {
          industrial: values.unitDetailLeasingPromptIndustrial,
          flex: values.unitDetailLeasingPromptFlex,
          retail: values.unitDetailLeasingPromptRetail,
          office: values.unitDetailLeasingPromptOffice,
          fallback: values.unitDetailLeasingPromptFallback,
        },
        photoGalleryEmptyMessage: values.unitDetailPhotoGalleryEmptyMessage,
      },
      showingDialog: {
        title: values.showingDialogTitle,
        description: values.showingDialogDescription,
        stepIndicatorTemplate: values.showingDialogStepIndicatorTemplate,
        selectedUnitLabel: values.showingDialogSelectedUnitLabel,
        selectedUnitDescription: values.showingDialogSelectedUnitDescription,
        steps: {
          contactLabel: values.showingDialogStepContactLabel,
          scheduleLabel: values.showingDialogStepScheduleLabel,
          reviewLabel: values.showingDialogStepReviewLabel,
        },
        fields: {
          firstNameLabel: values.showingDialogFieldFirstNameLabel,
          lastNameLabel: values.showingDialogFieldLastNameLabel,
          emailLabel: values.showingDialogFieldEmailLabel,
          phoneLabel: values.showingDialogFieldPhoneLabel,
          dateLabel: values.showingDialogFieldDateLabel,
          timeLabel: values.showingDialogFieldTimeLabel,
          messageLabel: values.showingDialogFieldMessageLabel,
        },
        fieldHints: {
          dateHint: values.showingDialogFieldDateHint,
          timeHint: values.showingDialogFieldTimeHint,
        },
        messagePlaceholder: values.showingDialogMessagePlaceholder,
        review: {
          title: values.showingDialogReviewTitle,
          contactLabel: values.showingDialogReviewContactLabel,
          slotLabel: values.showingDialogReviewSlotLabel,
          unitLabel: values.showingDialogReviewUnitLabel,
          messageLabel: values.showingDialogReviewMessageLabel,
          confirmationNote: values.showingDialogReviewConfirmationNote,
          guidanceNext: values.showingDialogReviewGuidanceNext,
          guidanceReview: values.showingDialogReviewGuidanceReview,
        },
        buttons: {
          cancelLabel: values.showingDialogButtonCancelLabel,
          backLabel: values.showingDialogButtonBackLabel,
          nextLabel: values.showingDialogButtonNextLabel,
          confirmLabel: values.showingDialogButtonConfirmLabel,
          doneLabel: values.showingDialogButtonDoneLabel,
        },
        errors: {
          contactMissing: values.showingDialogErrorContactMissing,
          scheduleMissing: values.showingDialogErrorScheduleMissing,
          submitError: values.showingDialogErrorSubmit,
        },
        success: {
          title: values.showingDialogSuccessTitle,
          description: values.showingDialogSuccessDescription,
          timeLabel: values.showingDialogSuccessTimeLabel,
          emailLabel: values.showingDialogSuccessEmailLabel,
          nameLabel: values.showingDialogSuccessNameLabel,
          messageLabel: values.showingDialogSuccessMessageLabel,
          calendarLabel: values.showingDialogSuccessCalendarLabel,
          portalInviteQueued: values.showingDialogSuccessPortalQueued,
          portalInviteSent: values.showingDialogSuccessPortalSent,
        },
        unavailableTooltip: values.showingDialogUnavailableTooltip,
      },
      shared: {
        contactSection: {
          addressLabel: values.sharedContactAddressLabel,
          phoneLabel: values.sharedContactPhoneLabel,
          emailLabel: values.sharedContactEmailLabel,
          hoursLabel: values.sharedContactHoursLabel,
          leadershipLabel: values.sharedContactLeadershipLabel,
          mapTitle: values.sharedContactMapTitle,
          mapPreviewTitle: values.sharedContactMapPreviewTitle,
          mapPreviewDescription: values.sharedContactMapPreviewDescription,
          mapFooter: values.sharedContactMapFooter,
          mapLinkLabel: values.sharedContactMapLinkLabel,
        },
        availabilityAlerts: {
          eyebrow: values.sharedAvailabilityAlertsEyebrow,
          title: values.sharedAvailabilityAlertsTitle,
          description: values.sharedAvailabilityAlertsDescription,
          emailLabel: values.sharedAvailabilityAlertsEmailLabel,
          emailPlaceholder: values.sharedAvailabilityAlertsEmailPlaceholder,
          submitLabel: values.sharedAvailabilityAlertsSubmitLabel,
          successMessage: values.sharedAvailabilityAlertsSuccessMessage,
          errorMessage: values.sharedAvailabilityAlertsErrorMessage,
          note: values.sharedAvailabilityAlertsNote,
        },
        propertyCard: {
          photoFallback: values.sharedPropertyCardPhotoFallback,
          rentFallback: values.sharedPropertyCardRentFallback,
          availabilityFallback: values.sharedPropertyCardAvailabilityFallback,
          divisibleLabel: values.sharedPropertyCardDivisibleLabel,
          vacancyLabel: values.sharedPropertyCardVacancyLabel,
          vacancyAvailableLabel: values.sharedPropertyCardVacancyAvailableLabel,
          vacancyLeasedLabel: values.sharedPropertyCardVacancyLeasedLabel,
          sizeLabel: values.sharedPropertyCardSizeLabel,
          unitCountZeroLabel: values.sharedPropertyCardUnitCountZeroLabel,
          unitCountSingularLabel: values.sharedPropertyCardUnitCountSingularLabel,
          unitCountPluralLabel: values.sharedPropertyCardUnitCountPluralLabel,
        },
        unitCard: {
          photoFallback: values.sharedUnitCardPhotoFallback,
          availabilityLabel: values.sharedUnitCardAvailabilityLabel,
          availabilityFallback: values.sharedUnitCardAvailabilityFallback,
          floorLabel: values.sharedUnitCardFloorLabel,
          viewDetailsLabel: values.sharedUnitCardViewDetailsLabel,
          divisibleLabel: values.sharedUnitCardDivisibleLabel,
        },
        emptyStates: {
          propertyGridEmpty: values.sharedEmptyStatePropertyGrid,
          unitGroupEmpty: values.sharedEmptyStateUnitGroup,
        },
      },
    },
  };
}

function SettingsCard({
  title,
  description,
  children,
  id,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  id?: string;
}): JSX.Element {
  return (
    <section
      id={id}
      className="flex flex-col gap-4 rounded-2xl border border-surface-subtle bg-white p-6 shadow-card"
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-brand-primary">{title}</h2>
        {description !== undefined ? (
          <p className="text-sm text-text-primary/70">{description}</p>
        ) : null}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function SettingsGroup({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-surface-subtle bg-surface-subtle/40 p-4">
      <div>
        <p className="text-sm font-semibold text-brand-primary">{title}</p>
        {description !== undefined ? (
          <p className="text-xs text-text-primary/70">{description}</p>
        ) : null}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

type ContactHoursFormValues = SiteSettingsFormValues['contactHours'];
type LeadershipContactsFormValues = SiteSettingsFormValues['leadershipContacts'];

type ArrayFieldGroupProps =
  | {
      title: string;
      description: string;
      items: ContactHoursFormValues;
      onChange: (next: ContactHoursFormValues) => void;
      error?: string;
      type?: 'hours';
    }
  | {
      title: string;
      description: string;
      items: LeadershipContactsFormValues;
      onChange: (next: LeadershipContactsFormValues) => void;
      error?: string;
      type: 'contact';
    };

function ArrayFieldGroup(props: ArrayFieldGroupProps): JSX.Element {
  if (props.type === 'contact') {
    return <ContactFieldGroup {...props} />;
  }

  return <HoursFieldGroup {...props} />;
}

type NavigationItemFieldKey = 'label' | 'href' | 'enabled' | 'external';
type NavigationChildFieldKey = 'label' | 'href' | 'enabled' | 'external' | 'propertyId';

function useSortableSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

function NavigationLinksFieldGroup({
  title,
  description,
  items,
  propertyOptions,
  propertiesAreLoading,
  propertiesFetchFailed,
  onChange,
  error,
}: FieldGroupBaseProps & {
  items: NavigationLinksFormValues;
  propertyOptions: NavigationPropertyOption[];
  propertiesAreLoading: boolean;
  propertiesFetchFailed: boolean;
  onChange: (next: NavigationLinksFormValues) => void;
}): JSX.Element {
  const sensors = useSortableSensors();
  const optionsWithFallback = useMemo(() => {
    const optionMap = new Map(propertyOptions.map((option) => [option.id, option]));
    const merged = [...propertyOptions];

    items.forEach((item) => {
      item.children.forEach((child) => {
        const id = child.propertyId.trim();
        if (id.length > 0 && !optionMap.has(id)) {
          const fallback = { id, label: `Unknown property (${id})` };
          optionMap.set(id, fallback);
          merged.push(fallback);
        }
      });
    });

    return merged;
  }, [items, propertyOptions]);

  const handleAdd = () => {
    onChange([...items, createNavigationItem()]);
  };

  const handleRemove = (id: string) => {
    onChange(items.filter((item) => item.id !== id));
  };

  const handleItemUpdate = (
    id: string,
    updater: (item: NavigationItemFormValues) => NavigationItemFormValues,
  ) => {
    onChange(items.map((item) => (item.id === id ? updater(item) : item)));
  };

  const handleItemChange = (id: string, key: NavigationItemFieldKey, value: string | boolean) => {
    handleItemUpdate(id, (item) => ({ ...item, [key]: value }));
  };

  const handleDisplayChange = (id: string, display: NavigationDisplayValue) => {
    const match = NAVIGATION_DISPLAY_OPTIONS.find((option) => option.value === display);
    if (!match) {
      return;
    }

    handleItemUpdate(id, (item) => {
      const nextHref =
        match.action === 'auth'
          ? ''
          : match.action === 'call' && item.href.trim().length > 0 && !item.href.startsWith('tel:')
            ? ''
            : item.href;

      return {
        ...item,
        action: match.action,
        style: match.style,
        href: nextHref,
        external: match.action === 'link' ? item.external : false,
      };
    });
  };

  const handleToggleDropdown = (id: string) => {
    onChange(
      items.map((item) => {
        if (item.id !== id) {
          return item;
        }
        if (item.children.length > 0) {
          return { ...item, children: [] };
        }
        return {
          ...item,
          children: [createNavigationChild()],
          action: 'link',
          style: 'link',
          external: false,
        };
      }),
    );
  };

  const handleChildAdd = (parentId: string) => {
    onChange(
      items.map((item) =>
        item.id === parentId
          ? { ...item, children: [...item.children, createNavigationChild()] }
          : item,
      ),
    );
  };

  const handleChildChange = (
    parentId: string,
    childId: string,
    key: NavigationChildFieldKey,
    value: string | boolean,
  ) => {
    onChange(
      items.map((item) => {
        if (item.id !== parentId) {
          return item;
        }
        return {
          ...item,
          children: item.children.map((child) =>
            child.id === childId ? { ...child, [key]: value } : child,
          ),
        };
      }),
    );
  };

  const handleChildRemove = (parentId: string, childId: string) => {
    onChange(
      items.map((item) =>
        item.id === parentId
          ? { ...item, children: item.children.filter((child) => child.id !== childId) }
          : item,
      ),
    );
  };

  const handleChildReorder = (parentId: string, nextChildren: NavigationChildFormValues[]) => {
    onChange(
      items.map((item) => (item.id === parentId ? { ...item, children: nextChildren } : item)),
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onChange(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <ArrayFieldGroupContainer
      title={title}
      description={description}
      error={error}
      onAdd={handleAdd}
      addLabel="Add menu item"
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-4">
            {items.map((item) => (
              <SortableNavigationItem
                key={item.id}
                item={item}
                propertyOptions={optionsWithFallback}
                propertiesAreLoading={propertiesAreLoading}
                propertiesFetchFailed={propertiesFetchFailed}
                onChange={(key, value) => {
                  handleItemChange(item.id, key, value);
                }}
                onDisplayChange={(nextDisplay) => {
                  handleDisplayChange(item.id, nextDisplay);
                }}
                onRemove={() => {
                  handleRemove(item.id);
                }}
                onToggleDropdown={() => {
                  handleToggleDropdown(item.id);
                }}
                onChildAdd={() => {
                  handleChildAdd(item.id);
                }}
                onChildChange={(childId, key, value) => {
                  handleChildChange(item.id, childId, key, value);
                }}
                onChildRemove={(childId) => {
                  handleChildRemove(item.id, childId);
                }}
                onChildReorder={(nextChildren) => {
                  handleChildReorder(item.id, nextChildren);
                }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </ArrayFieldGroupContainer>
  );
}

function SortableNavigationItem({
  item,
  propertyOptions,
  propertiesAreLoading,
  propertiesFetchFailed,
  onChange,
  onDisplayChange,
  onRemove,
  onToggleDropdown,
  onChildAdd,
  onChildChange,
  onChildRemove,
  onChildReorder,
}: {
  item: NavigationItemFormValues;
  propertyOptions: NavigationPropertyOption[];
  propertiesAreLoading: boolean;
  propertiesFetchFailed: boolean;
  onChange: (key: NavigationItemFieldKey, value: string | boolean) => void;
  onDisplayChange: (display: NavigationDisplayValue) => void;
  onRemove: () => void;
  onToggleDropdown: () => void;
  onChildAdd: () => void;
  onChildChange: (childId: string, key: NavigationChildFieldKey, value: string | boolean) => void;
  onChildRemove: (childId: string) => void;
  onChildReorder: (nextChildren: NavigationChildFormValues[]) => void;
}): JSX.Element {
  const sensors = useSortableSensors();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const hasChildren = item.children.length > 0;
  const action = resolveNavigationAction(item.action);
  const display = resolveNavigationDisplay(item);
  const showLinkInput = !hasChildren && action !== 'auth';

  const handleChildDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = item.children.findIndex((child) => child.id === active.id);
    const newIndex = item.children.findIndex((child) => child.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onChildReorder(arrayMove(item.children, oldIndex, newIndex));
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'rounded-2xl border border-surface-subtle bg-white p-4 shadow-sm',
        isDragging && 'shadow-lg',
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="mt-1 flex h-11 w-11 cursor-grab touch-none items-center justify-center rounded-full border border-surface-subtle bg-white text-text-primary/70 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary active:cursor-grabbing"
            aria-label="Drag to reorder menu item"
            {...attributes}
            {...listeners}
          >
            <DragHandleDots2Icon className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex-1 space-y-3">
            <div
              className={clsx(
                'grid gap-3',
                hasChildren ? 'md:grid-cols-1' : 'md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]',
              )}
            >
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  Label
                </label>
                <input
                  type="text"
                  value={item.label}
                  onChange={(event) => {
                    onChange('label', event.target.value);
                  }}
                  className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                />
              </div>
              {showLinkInput ? (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    Link
                  </label>
                  <input
                    type="text"
                    value={item.href}
                    onChange={(event) => {
                      onChange('href', event.target.value);
                    }}
                    placeholder={action === 'call' ? 'tel:+1-555-555-5555' : '/contact'}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                  {action === 'call' ? (
                    <span className="text-xs text-text-primary/60">
                      Leave blank to use the primary contact phone.
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {!hasChildren && action === 'auth' ? (
              <p className="text-xs text-text-primary/60">Auth buttons open the sign-in dialog.</p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              {!hasChildren ? (
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  Display
                  <select
                    value={display}
                    onChange={(event) => {
                      onDisplayChange(event.target.value as NavigationDisplayValue);
                    }}
                    className="rounded-xl border border-surface-subtle bg-white px-3 py-2 text-sm font-semibold text-text-primary focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                  >
                    {NAVIGATION_DISPLAY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  Dropdown menu
                </span>
              )}
              <label className="inline-flex items-center gap-2 rounded-full border border-surface-subtle bg-surface-subtle/40 px-3 py-2 text-xs font-semibold text-text-primary/70">
                <input
                  type="checkbox"
                  checked={item.enabled}
                  onChange={(event) => {
                    onChange('enabled', event.target.checked);
                  }}
                  className="h-4 w-4"
                />
                Enabled
              </label>
              {!hasChildren && action === 'link' ? (
                <label className="inline-flex items-center gap-2 rounded-full border border-surface-subtle bg-surface-subtle/40 px-3 py-2 text-xs font-semibold text-text-primary/70">
                  <input
                    type="checkbox"
                    checked={item.external}
                    onChange={(event) => {
                      onChange('external', event.target.checked);
                    }}
                    className="h-4 w-4"
                  />
                  External
                </label>
              ) : null}
              <button
                type="button"
                className="rounded-full border border-brand-primary/40 px-3 py-2 text-xs font-semibold text-brand-primary transition hover:bg-brand-primary hover:text-white"
                onClick={onToggleDropdown}
              >
                {hasChildren ? 'Use as link' : 'Add dropdown items'}
              </button>
              <button
                type="button"
                className="rounded-full border border-surface-subtle px-3 py-2 text-xs font-semibold text-text-primary/70 transition hover:bg-surface-subtle"
                onClick={onRemove}
              >
                Remove
              </button>
            </div>
          </div>
        </div>

        {hasChildren ? (
          <div className="grid gap-3 rounded-2xl border border-surface-subtle bg-surface-subtle/40 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                Dropdown items
              </p>
              <button
                type="button"
                className="rounded-full border border-brand-primary px-3 py-2 text-xs font-semibold text-brand-primary transition hover:bg-brand-primary hover:text-white"
                onClick={onChildAdd}
              >
                Add item
              </button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleChildDragEnd}
            >
              <SortableContext
                items={item.children.map((child) => child.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-3">
                  {item.children.map((child) => (
                    <SortableNavigationChildRow
                      key={child.id}
                      child={child}
                      propertyOptions={propertyOptions}
                      propertiesAreLoading={propertiesAreLoading}
                      propertiesFetchFailed={propertiesFetchFailed}
                      onChange={(key, value) => {
                        onChildChange(child.id, key, value);
                      }}
                      onRemove={() => {
                        onChildRemove(child.id);
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SortableNavigationChildRow({
  child,
  propertyOptions,
  propertiesAreLoading,
  propertiesFetchFailed,
  onChange,
  onRemove,
}: {
  child: NavigationChildFormValues;
  propertyOptions: NavigationPropertyOption[];
  propertiesAreLoading: boolean;
  propertiesFetchFailed: boolean;
  onChange: (key: NavigationChildFieldKey, value: string | boolean) => void;
  onRemove: () => void;
}): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: child.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const propertyId = child.propertyId.trim();
  const isPropertyLink = propertyId.length > 0;
  const canSelectProperty = propertyOptions.length > 0;
  const linkType = isPropertyLink ? 'property' : 'link';

  const handleLinkTypeChange = (nextType: string) => {
    if (nextType === 'property') {
      if (!canSelectProperty) {
        return;
      }
      const nextPropertyId = propertyOptions[0]?.id ?? '';
      if (nextPropertyId.length === 0) {
        return;
      }
      onChange('propertyId', nextPropertyId);
      onChange('href', '');
      onChange('external', false);
      return;
    }

    onChange('propertyId', '');
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'rounded-xl border border-surface-subtle bg-white p-3 shadow-sm',
        isDragging && 'shadow-md',
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="mt-1 flex h-10 w-10 cursor-grab touch-none items-center justify-center rounded-full border border-surface-subtle bg-white text-text-primary/70 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary active:cursor-grabbing"
            aria-label="Drag to reorder dropdown item"
            {...attributes}
            {...listeners}
          >
            <DragHandleDots2Icon className="h-4 w-4" aria-hidden="true" />
          </button>
          <div className="flex-1 space-y-3">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                Item type
              </label>
              <select
                value={linkType}
                onChange={(event) => {
                  handleLinkTypeChange(event.target.value);
                }}
                className="rounded-xl border border-surface-subtle bg-white px-3 py-2 text-sm font-semibold text-text-primary focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              >
                <option value="link">Custom link</option>
                <option value="property" disabled={!canSelectProperty}>
                  Property
                </option>
              </select>
              {!canSelectProperty ? (
                <span className="text-xs text-text-primary/60">
                  {propertiesFetchFailed
                    ? 'Unable to load published properties.'
                    : propertiesAreLoading
                      ? 'Loading published properties...'
                      : 'No published properties are available yet.'}
                </span>
              ) : null}
            </div>

            {isPropertyLink ? (
              <div className="grid gap-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    Property
                  </label>
                  <select
                    value={child.propertyId}
                    onChange={(event) => {
                      onChange('propertyId', event.target.value);
                    }}
                    className="rounded-xl border border-surface-subtle bg-white px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  >
                    <option value="" disabled>
                      Select a property
                    </option>
                    {propertyOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    Label override
                  </label>
                  <input
                    type="text"
                    value={child.label}
                    onChange={(event) => {
                      onChange('label', event.target.value);
                    }}
                    placeholder="Leave blank to use the address."
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    Label
                  </label>
                  <input
                    type="text"
                    value={child.label}
                    onChange={(event) => {
                      onChange('label', event.target.value);
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    Link
                  </label>
                  <input
                    type="text"
                    value={child.href}
                    onChange={(event) => {
                      onChange('href', event.target.value);
                    }}
                    className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="inline-flex items-center gap-2 rounded-full border border-surface-subtle bg-surface-subtle/40 px-3 py-2 text-xs font-semibold text-text-primary/70">
            <input
              type="checkbox"
              checked={child.enabled}
              onChange={(event) => {
                onChange('enabled', event.target.checked);
              }}
              className="h-4 w-4"
            />
            Enabled
          </label>
          {!isPropertyLink ? (
            <label className="inline-flex items-center gap-2 rounded-full border border-surface-subtle bg-surface-subtle/40 px-3 py-2 text-xs font-semibold text-text-primary/70">
              <input
                type="checkbox"
                checked={child.external}
                onChange={(event) => {
                  onChange('external', event.target.checked);
                }}
                className="h-4 w-4"
              />
              External
            </label>
          ) : null}
          <button
            type="button"
            className="rounded-full border border-surface-subtle px-3 py-2 text-xs font-semibold text-text-primary/70 transition hover:bg-surface-subtle"
            onClick={onRemove}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

interface FieldGroupBaseProps {
  title: string;
  description: string;
  error?: string;
}

function HoursFieldGroup({
  title,
  description,
  items,
  onChange,
  error,
}: FieldGroupBaseProps & {
  items: ContactHoursFormValues;
  onChange: (next: ContactHoursFormValues) => void;
}): JSX.Element {
  const handleAdd = () => {
    onChange([...items, { label: '', value: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  const handleLabelChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, label: value } : item)));
  };

  const handleValueChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, value } : item)));
  };

  return (
    <ArrayFieldGroupContainer
      title={title}
      description={description}
      error={error}
      onAdd={handleAdd}
    >
      {items.map((item, index) => (
        <ArrayFieldRow
          key={`${title}-${String(index)}`}
          primaryLabel="Label"
          secondaryLabel="Value"
          primaryValue={item.label}
          secondaryValue={item.value}
          onPrimaryChange={(value) => {
            handleLabelChange(index, value);
          }}
          onSecondaryChange={(value) => {
            handleValueChange(index, value);
          }}
          onRemove={() => {
            handleRemove(index);
          }}
        />
      ))}
    </ArrayFieldGroupContainer>
  );
}

function ContactFieldGroup({
  title,
  description,
  items,
  onChange,
  error,
}: FieldGroupBaseProps & {
  items: LeadershipContactsFormValues;
  onChange: (next: LeadershipContactsFormValues) => void;
}): JSX.Element {
  const handleAdd = () => {
    onChange([...items, { name: '', title: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  const handleNameChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, name: value } : item)));
  };

  const handleTitleChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, title: value } : item)));
  };

  return (
    <ArrayFieldGroupContainer
      title={title}
      description={description}
      error={error}
      onAdd={handleAdd}
    >
      {items.map((item, index) => (
        <ArrayFieldRow
          key={`${title}-${String(index)}`}
          primaryLabel="Name"
          secondaryLabel="Title"
          primaryValue={item.name}
          secondaryValue={item.title ?? ''}
          onPrimaryChange={(value) => {
            handleNameChange(index, value);
          }}
          onSecondaryChange={(value) => {
            handleTitleChange(index, value);
          }}
          onRemove={() => {
            handleRemove(index);
          }}
        />
      ))}
    </ArrayFieldGroupContainer>
  );
}

function ArrayFieldGroupContainer({
  title,
  description,
  error,
  onAdd,
  addLabel,
  addDisabled,
  children,
}: FieldGroupBaseProps & {
  onAdd: () => void;
  addLabel?: string;
  addDisabled?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-surface-subtle bg-surface-subtle/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-brand-primary">{title}</p>
          <p className="text-xs text-text-primary/70">{description}</p>
        </div>
        <button
          type="button"
          className="min-h-11 rounded-full border border-brand-primary px-4 py-2 text-xs font-semibold text-brand-primary transition hover:bg-brand-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent disabled:hover:text-brand-primary"
          onClick={onAdd}
          disabled={addDisabled}
        >
          {addLabel ?? 'Add'}
        </button>
      </div>
      <div className="flex flex-col gap-3">{children}</div>
      {typeof error === 'string' && error.length > 0 ? (
        <p className="text-xs font-medium text-red-600">{error}</p>
      ) : null}
    </div>
  );
}

function ArrayFieldRow({
  primaryLabel,
  secondaryLabel,
  primaryValue,
  secondaryValue,
  onPrimaryChange,
  onSecondaryChange,
  onRemove,
}: {
  primaryLabel: string;
  secondaryLabel: string;
  primaryValue: string;
  secondaryValue: string;
  onPrimaryChange: (value: string) => void;
  onSecondaryChange: (value: string) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {primaryLabel}
        </label>
        <input
          type="text"
          value={primaryValue}
          onChange={(event) => {
            onPrimaryChange(event.target.value);
          }}
          className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {secondaryLabel}
        </label>
        <input
          type="text"
          value={secondaryValue}
          onChange={(event) => {
            onSecondaryChange(event.target.value);
          }}
          className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
        />
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="border-surface-strong hover:bg-surface-strong rounded-full border px-3 py-2 text-xs font-semibold text-text-primary/70 transition"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function SingleFieldRow({
  label,
  value,
  onChange,
  onRemove,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {label}
        </label>
        <input
          type="text"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          className="rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm"
        />
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="border-surface-strong hover:bg-surface-strong rounded-full border px-3 py-2 text-xs font-semibold text-text-primary/70 transition"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function StringListFieldGroup({
  title,
  description,
  items,
  onChange,
  error,
  itemLabel = 'Value',
  addLabel,
}: FieldGroupBaseProps & {
  items: string[];
  onChange: (next: string[]) => void;
  itemLabel?: string;
  addLabel?: string;
}): JSX.Element {
  const handleAdd = () => {
    onChange([...items, '']);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  const handleChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? value : item)));
  };

  return (
    <ArrayFieldGroupContainer
      title={title}
      description={description}
      error={error}
      onAdd={handleAdd}
      addLabel={addLabel}
    >
      {items.map((item, index) => (
        <SingleFieldRow
          key={`${title}-${String(index)}`}
          label={itemLabel}
          value={item}
          onChange={(value) => {
            handleChange(index, value);
          }}
          onRemove={() => {
            handleRemove(index);
          }}
        />
      ))}
    </ArrayFieldGroupContainer>
  );
}

function TitleDescriptionFieldGroup({
  title,
  description,
  items,
  onChange,
  error,
  addLabel,
}: FieldGroupBaseProps & {
  items: Array<{ title: string; description: string }>;
  onChange: (next: Array<{ title: string; description: string }>) => void;
  addLabel?: string;
}): JSX.Element {
  const handleAdd = () => {
    onChange([...items, { title: '', description: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  const handleTitleChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, title: value } : item)));
  };

  const handleDescriptionChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, description: value } : item)));
  };

  return (
    <ArrayFieldGroupContainer
      title={title}
      description={description}
      error={error}
      onAdd={handleAdd}
      addLabel={addLabel}
    >
      {items.map((item, index) => (
        <ArrayFieldRow
          key={`${title}-${String(index)}`}
          primaryLabel="Title"
          secondaryLabel="Description"
          primaryValue={item.title}
          secondaryValue={item.description}
          onPrimaryChange={(value) => {
            handleTitleChange(index, value);
          }}
          onSecondaryChange={(value) => {
            handleDescriptionChange(index, value);
          }}
          onRemove={() => {
            handleRemove(index);
          }}
        />
      ))}
    </ArrayFieldGroupContainer>
  );
}

function LabelValueFieldGroup({
  title,
  description,
  items,
  onChange,
  error,
  addLabel,
}: FieldGroupBaseProps & {
  items: Array<{ label: string; value: string }>;
  onChange: (next: Array<{ label: string; value: string }>) => void;
  addLabel?: string;
}): JSX.Element {
  const handleAdd = () => {
    onChange([...items, { label: '', value: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  const handleLabelChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, label: value } : item)));
  };

  const handleValueChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, value } : item)));
  };

  return (
    <ArrayFieldGroupContainer
      title={title}
      description={description}
      error={error}
      onAdd={handleAdd}
      addLabel={addLabel}
    >
      {items.map((item, index) => (
        <ArrayFieldRow
          key={`${title}-${String(index)}`}
          primaryLabel="Label"
          secondaryLabel="Value"
          primaryValue={item.label}
          secondaryValue={item.value}
          onPrimaryChange={(value) => {
            handleLabelChange(index, value);
          }}
          onSecondaryChange={(value) => {
            handleValueChange(index, value);
          }}
          onRemove={() => {
            handleRemove(index);
          }}
        />
      ))}
    </ArrayFieldGroupContainer>
  );
}

function ValueLabelFieldGroup({
  title,
  description,
  items,
  onChange,
  error,
  addLabel,
}: FieldGroupBaseProps & {
  items: Array<{ value: string; label: string }>;
  onChange: (next: Array<{ value: string; label: string }>) => void;
  addLabel?: string;
}): JSX.Element {
  const handleAdd = () => {
    onChange([...items, { value: '', label: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  const handleValueChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, value } : item)));
  };

  const handleLabelChange = (index: number, value: string) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, label: value } : item)));
  };

  return (
    <ArrayFieldGroupContainer
      title={title}
      description={description}
      error={error}
      onAdd={handleAdd}
      addLabel={addLabel}
    >
      {items.map((item, index) => (
        <ArrayFieldRow
          key={`${title}-${String(index)}`}
          primaryLabel="Value"
          secondaryLabel="Label"
          primaryValue={item.value}
          secondaryValue={item.label}
          onPrimaryChange={(value) => {
            handleValueChange(index, value);
          }}
          onSecondaryChange={(value) => {
            handleLabelChange(index, value);
          }}
          onRemove={() => {
            handleRemove(index);
          }}
        />
      ))}
    </ArrayFieldGroupContainer>
  );
}
